import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  RefreshCw, Search, Download, Plus, Settings,
  Check, Star, Mail, Phone,
  Linkedin, Facebook, Instagram, Youtube, Twitter, AtSign
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { Header, DataDrawer } from '@/components';
import { getCredits } from '@/services/credits';
import { loadContactItem, updateContactRecord, loadContactsServerSide } from '@/services/contacts';
import { API_CONFIG } from '@/services/api';
import { researchContact, listInstantlyCampaignOptions, syncExistingContactsToCrm, enrollExistingContactsToInstantly, type InstantlyCampaignOption } from '@/services/research';
import { generatePepJob, getPepJob, getPepLatest, getPepRunStatus } from '@/services/pep';
import { getPepCompanyProfile, getPepProfile, type PepProfile } from '@/services/pepProfile';
import { startCrunchbaseScrape, pollCrunchbaseResults, startLinkedIn360Scrape, pollLinkedIn360Results } from '@/services/scraping';
import { loadHotListDb, removeHotListEntityDb, type HotListEntity } from '@/services/hotLists';
import { getAccountSettings, setGhlSendAsLocation } from '@/services/account';
import { List, type RowComponentProps } from 'react-window';

// API Endpoints
const RESEARCH_API_URL = API_CONFIG.RESEARCH_API;
const LIST_ENTITY_TYPE = 'contact';
const DEFAULT_LIST_STORAGE_KEY = 'profitibull_default_contact_list';
const PEPAI_CAMPAIGN_STORAGE_KEY = 'profitibull_pepai_campaign_id';
const SEND_TO_CRM_STORAGE_KEY = 'profitibull_mydata_contacts_send_to_crm';
const MAX_EXPORT_FIELDS = 25;


// Types
type ContactSocialPlatform = 'linkedin' | 'facebook' | 'instagram' | 'youtube' | 'tiktok' | 'x' | 'threads';

interface Contact {
  searchResultId: string;
  fullName?: string;
  Name?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  Title?: string;
  company?: string;
  Company?: string;
  email?: string;
  Email?: string;
  email1?: string;
  email2?: string;
  email3?: string;
  personalEmail?: string;
  PersonalEmail?: string;
  personalEmail1?: string;
  contactPhone1?: string;
  contactMobilePhone1?: string;
  companyPhone1?: string;
  linkedIn?: string;
  LIProfileUrl?: string;
  CompanyLIProfileUrl?: string;
  researchedAt?: string;
  lists?: string[] | string;
  ContactLocation?: string | Record<string, unknown> | Array<unknown>;
  CompanyLocation?: string | Record<string, unknown> | Array<unknown>;
  Website?: string;
  CompanyDomain?: string;
  formerCompany?: string;
  formerTitle?: string;
  titleStartedAt?: string;
  startedAtCurrentCompany?: string;
  contactSearchId?: number;
  uiMatchId?: string;
  facebook_url?: string;
  instagram_url?: string;
  youtube_url?: string;
  tiktok_url?: string;
  x_url?: string;
  threads_url?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  youtubeUrl?: string;
  tiktokUrl?: string;
  xUrl?: string;
  threadsUrl?: string;
  twitterUrl?: string;
  linkedin_profile_ds?: string[];
  linkedin_posts_ds?: string[];
  linkedin_scraped_at?: string;
  [key: string]: unknown;
}

type PepRunState = {
  run: any | null;
  steps: any[];
};

type PepProgramSummaryEntry = {
  programType: string;
  status: string;
  runId: string;
  channelFamily: string;
  entrySource: string;
  nextDueAt: string;
  startedAt: string;
  lastStep: string;
  lastStepStatus: string;
  branchReason: string;
};

const decodeAmp = (value: string) => value.replace(/&amp;/g, '&');

export function MyListsContacts() {
  const { userEmail, setCredits, isAuthenticated, accessPepaiResearch, addScrapeJob, updateScrapeJob, isAdmin, adminToken } = useAppStore();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [userLists, setUserLists] = useState<string[]>([]);
  const [defaultList, setDefaultList] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [selectionScope, setSelectionScope] = useState<'page' | 'matching'>('page');

  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentListFilter, setCurrentListFilter] = useState('');
  const [researchedDateFrom, setResearchedDateFrom] = useState('');
  const [researchedDateTo, setResearchedDateTo] = useState('');
  const [pepStatusFilter, setPepStatusFilter] = useState('');
  const [hasBusinessEmailFilter, setHasBusinessEmailFilter] = useState(false);
  const [hasPersonalEmailFilter, setHasPersonalEmailFilter] = useState(false);
  const [hasLinkedinFilter, setHasLinkedinFilter] = useState(false);
  const [hasBusinessPhoneFilter, setHasBusinessPhoneFilter] = useState(false);
  const [hasPersonalPhoneFilter, setHasPersonalPhoneFilter] = useState(false);
  const [titleFilterInput, setTitleFilterInput] = useState('');
  const [titleExactMatch, setTitleExactMatch] = useState(false);
  const [locationFilterInput, setLocationFilterInput] = useState('');
  const [locationScopeFilter, setLocationScopeFilter] = useState<'both_or' | 'both_and' | 'contact' | 'company'>('both_or');
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage] = useState(25);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingContactItemId, setLoadingContactItemId] = useState<string | null>(null);
  const [contactsLoadComplete, setContactsLoadComplete] = useState(false);
  const [contactsLoadError, setContactsLoadError] = useState<string | null>(null);
  const [exportProgress, setExportProgress] = useState<{
    active: boolean;
    stage: string;
    processed: number;
    total: number;
  }>({ active: false, stage: '', processed: 0, total: 0 });
  const [showExportFieldsModal, setShowExportFieldsModal] = useState(false);
  const [exportFieldSelection, setExportFieldSelection] = useState<string[]>([]);
  const [exportFieldPickerValue, setExportFieldPickerValue] = useState('');
  const lastContactsCommitCountRef = useRef(0);
  const lastContactsCommitAtRef = useRef(0);
  const contactsRequestSeqRef = useRef(0);

  const [createListModal, setCreateListModal] = useState(false);
  const [manageListsModal, setManageListsModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [addToListModal, setAddToListModal] = useState<{ contact: Contact | null }>({ contact: null });
  const [addToListSelection, setAddToListSelection] = useState('');
  const [rerunLoading, setRerunLoading] = useState<Record<string, boolean>>({});
  const [crmSyncLoading, setCrmSyncLoading] = useState(false);
  const [instantlySyncLoading, setInstantlySyncLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<Record<string, boolean>>({});
  const [socialScrapeLoading, setSocialScrapeLoading] = useState<Record<string, boolean>>({});
  const [socialEdit, setSocialEdit] = useState<{ contact: Contact; platform: ContactSocialPlatform } | null>(null);
  const [socialEditValue, setSocialEditValue] = useState('');
  const [contactSocialDrawer, setContactSocialDrawer] = useState<{ contact: Contact; platform: ContactSocialPlatform } | null>(null);
  const [contactProfileDrawer, setContactProfileDrawer] = useState<{ contact: Contact } | null>(null);
  const [contactModalTab, setContactModalTab] = useState<'ov' | 'intel' | 'sig' | 'act' | 'seq' | 'notes'>('ov');
  const openDrawerContactKeyRef = useRef('');
  const hydratedPepRunKeysRef = useRef<Set<string>>(new Set());
  const [contactModalTheme, setContactModalTheme] = useState<'dark' | 'light'>('dark');
  const [contactSocialReport, setContactSocialReport] = useState<{
    contact: Contact;
    platform: ContactSocialPlatform;
    items: unknown[];
  } | null>(null);
  const [contactSocialReportLoading, setContactSocialReportLoading] = useState<Record<string, boolean>>({});
  const [contactCrunchbaseReport, setContactCrunchbaseReport] = useState<{ companyName: string; data: unknown } | null>(null);
  const [contactCompanyReport, setContactCompanyReport] = useState<{
    companyName: string;
    source: 'linkedin_360' | 'website';
    data: unknown;
    scrapedAt?: string;
  } | null>(null);
  const [pepLoading, setPepLoading] = useState<Record<string, boolean>>({});
  const [pepData, setPepData] = useState<Record<string, any>>({});
  const [pepError, setPepError] = useState<Record<string, string | null>>({});
  const [pepRunData, setPepRunData] = useState<Record<string, PepRunState | null>>({});
  const [bulkPepLoading, setBulkPepLoading] = useState(false);
  const [instantlyCampaigns, setInstantlyCampaigns] = useState<InstantlyCampaignOption[]>([]);
  const [selectedInstantlyCampaignId, setSelectedInstantlyCampaignId] = useState(
    () => window.localStorage.getItem(PEPAI_CAMPAIGN_STORAGE_KEY) || ''
  );
  const [sendToCrm, setSendToCrm] = useState(
    () => window.localStorage.getItem(SEND_TO_CRM_STORAGE_KEY) === 'true'
  );
  const [ghlSendAsLocationId, setGhlSendAsLocationId] = useState('');
  const [ghlTargetOptions, setGhlTargetOptions] = useState<Array<{ locationId: string; label: string }>>([]);
  const [ghlSendAsSaving, setGhlSendAsSaving] = useState(false);
  const [pepProfile, setPepProfile] = useState<PepProfile | null>(null);
  const [pepPolicyExpanded, setPepPolicyExpanded] = useState<Record<string, boolean>>({});
  const [crunchbaseLoading, setCrunchbaseLoading] = useState<Record<string, boolean>>({});
  const [crunchbaseReports, setCrunchbaseReports] = useState<Record<string, unknown>>({});
  const [companyLinkedinLoading, setCompanyLinkedinLoading] = useState<Record<string, boolean>>({});
  const [websiteLoading, setWebsiteLoading] = useState<Record<string, boolean>>({});
  const [companyLinkedinReports, setCompanyLinkedinReports] = useState<Record<string, unknown>>({});
  const [websiteReports, setWebsiteReports] = useState<Record<string, unknown>>({});
  const [hotListItems, setHotListItems] = useState<HotListEntity[]>([]);
  const [contactNotesEditing, setContactNotesEditing] = useState(false);
  const [contactNotesSaving, setContactNotesSaving] = useState(false);
  const [contactNotesError, setContactNotesError] = useState<string | null>(null);
  const [contactNotesDraft, setContactNotesDraft] = useState<{
    businessEmail: string;
    personalEmail: string;
    businessPhone: string;
    mobilePhone: string;
    linkedin: string;
    location: string;
    departmentTags: string;
    seniorityTier: string;
    companySize: string;
    industryCodes: string;
  } | null>(null);

  const [sortKeyContacts, setSortKeyContacts] = useState<'date_desc' | 'date_asc' | 'name_asc' | 'name_desc' | 'company_asc' | 'company_desc' | 'pepai_asc' | 'pepai_desc'>('date_desc');

  const canUsePepaiResearch = userEmail.toLowerCase().endsWith('@profitibull.com') || accessPepaiResearch;
  const canChooseGhlTarget = isAdmin || userEmail.toLowerCase().endsWith('@profitibull.com');
  const targetGhlLocationId = useMemo(() => {
    if (!canChooseGhlTarget) return undefined;
    const value = String(ghlSendAsLocationId || '').trim();
    if (!value) return undefined;
    return ghlTargetOptions.some((option) => option.locationId === value) ? value : undefined;
  }, [canChooseGhlTarget, ghlSendAsLocationId, ghlTargetOptions]);
  const getSelectionId = (contact: Partial<Contact> | null | undefined): string => {
    const candidates = [
      (contact as any)?.searchResultIdRaw,
      contact?.searchResultId,
      (contact as any)?.contactId,
      (contact as any)?.id,
    ];
    for (const value of candidates) {
      const normalized = String(value ?? '').trim();
      if (normalized) return normalized;
    }
    return '';
  };

  const defaultExportFields = useMemo(
    () => [
      'searchResultId',
      'fullName',
      'title',
      'company',
      'Email',
      'PersonalEmail',
      'contactPhone1',
      'contactMobilePhone1',
      'linkedIn',
      'Website',
      '_contact_location',
      '_list_names',
      'researchedAt',
    ],
    []
  );

  const flattenExportPreviewObject = useCallback((obj: Record<string, unknown>, prefix = '', out: Set<string>) => {
    Object.entries(obj || {}).forEach(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        flattenExportPreviewObject(value as Record<string, unknown>, fullKey, out);
      } else {
        out.add(fullKey);
      }
    });
  }, []);

  const availableExportFields = useMemo(() => {
    const keys = new Set<string>(defaultExportFields);
    const previewRows = contacts.slice(0, 200);
    previewRows.forEach((contact) => flattenExportPreviewObject(contact as Record<string, unknown>, '', keys));
    const derived = [
      '_list_names',
      '_contact_location',
      '_facebook',
      '_instagram',
      '_youtube',
      '_tiktok',
      '_x',
      '_threads',
      '_linkedin_profile_dataset',
      '_linkedin_posts_dataset',
    ];
    derived.forEach((field) => keys.add(field));
    return Array.from(keys).sort((a, b) => a.localeCompare(b));
  }, [contacts, defaultExportFields, flattenExportPreviewObject]);

  useEffect(() => {
    if (exportFieldSelection.length > 0) return;
    const seeded = defaultExportFields.filter((field) => availableExportFields.includes(field)).slice(0, MAX_EXPORT_FIELDS);
    setExportFieldSelection(seeded);
  }, [availableExportFields, defaultExportFields, exportFieldSelection.length]);


  const normalizeDomain = (value?: string): string => {
    const raw = (value || '').toString().trim().toLowerCase();
    if (!raw) return '';
    let working = raw.replace(/^mailto:/, '');
    if (working.includes('@')) {
      working = working.split('@').pop() || '';
    }
    working = working.replace(/^https?:\/\//, '').replace(/^www\./, '');
    working = working.split('/')[0] || '';
    working = working.split('#')[0] || '';
    working = working.split('?')[0] || '';
    if (working.includes(':')) {
      const parts = working.split(':');
      if (parts[0]) working = parts[0];
    }
    return working.trim();
  };

  const formatLocation = (value: unknown): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(formatLocation).filter(Boolean).join(' | ');
    if (typeof value === 'object') {
      const v = value as Record<string, unknown>;
      const full = typeof v.fullString === 'string' ? v.fullString : '';
      if (full) return full;
      const city = typeof v.city === 'string' ? v.city : '';
      const state = (typeof v.stateAbbr === 'string' ? v.stateAbbr : '') || (typeof v.state === 'string' ? v.state : '');
      const country = (typeof v.countryAbbr === 'string' ? v.countryAbbr : '') || (typeof v.country === 'string' ? v.country : '');
      const parts = [city, state, country].filter(Boolean);
      if (parts.length) return parts.join(', ');
    }
    return '';
  };

  const companyNameFromDomain = (domain: string): string => {
    if (!domain) return '';
    const parts = domain.split('.').filter(Boolean);
    if (parts.length === 0) return '';
    let idx = Math.max(0, parts.length - 2);
    // Heuristic for two-part TLDs (e.g., co.uk)
    if (parts.length >= 3) {
      const tld = parts[parts.length - 1];
      const sld = parts[parts.length - 2];
      if (tld.length <= 3 && sld.length <= 3) {
        idx = Math.max(0, parts.length - 3);
      }
    }
    const label = parts[idx] || parts[0];
    const words = label.replace(/_/g, '-').split('-').filter(Boolean);
    return words.map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ');
  };

  const getContactName = (c: Contact): string =>
    (c.fullName || c.Name || `${c.firstName || ''} ${c.lastName || ''}`.trim()).trim();

  const normalizeLinkedinProfileUrl = (raw: string): string => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    // Avoid Sales Navigator / Recruiter URLs for profile scraping
    const lower = trimmed.toLowerCase();
    if (lower.includes('linkedin.com/sales/') || lower.includes('linkedin.com/talent/')) return '';
    // Ensure protocol
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    // Normalize to linkedin.com/in/slug when possible
    const match = withProtocol.match(/linkedin\.com\/in\/[^/?#]+/i);
    if (match) return `https://www.${match[0].replace(/^https?:\/\//i, '')}`;
    // Fallback: return cleaned url without query/fragment
    return withProtocol.split('#')[0].split('?')[0];
  };

  const normalizeWebsiteUrl = (raw: string): string => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const getContactSocialUrl = (c: Contact, platform: ContactSocialPlatform): string => {
    switch (platform) {
      case 'linkedin':
        return normalizeLinkedinProfileUrl(c.linkedIn || c.LIProfileUrl || '');
      case 'facebook':
        return c.facebook_url || c.facebookUrl || '';
      case 'instagram':
        return c.instagram_url || c.instagramUrl || '';
      case 'youtube':
        return c.youtube_url || c.youtubeUrl || '';
      case 'tiktok':
        return c.tiktok_url || c.tiktokUrl || '';
      case 'x':
        return c.x_url || c.xUrl || c.twitterUrl || '';
      case 'threads':
        return c.threads_url || c.threadsUrl || '';
      default:
        return '';
    }
  };

  const getContactSocialDatasetIds = (c: Contact, platform: ContactSocialPlatform): { profile?: string; posts?: string } => {
    if (platform === 'linkedin') {
      const profileIds = Array.isArray(c.linkedin_profile_ds) ? c.linkedin_profile_ds : [];
      const postsIds = Array.isArray(c.linkedin_posts_ds) ? c.linkedin_posts_ds : [];
      return {
        profile: profileIds.length ? profileIds[profileIds.length - 1] : undefined,
        posts: postsIds.length ? postsIds[postsIds.length - 1] : undefined,
      };
    }
    return {};
  };

  const pickContactField = (c: Contact, keys: string[]): string => {
    for (const key of keys) {
      const value = (c as any)?.[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  };

  const joinContactFields = (c: Contact, keys: string[]): string => {
    const values = keys
      .map((key) => (c as any)?.[key])
      .filter((v) => typeof v === 'string' && v.trim()) as string[];
    return values.join(', ');
  };

  const getContactCompany = (c: Contact): string => (c.company || c.Company || '').trim();

  const getContactDomain = (c: Contact): string =>
    normalizeDomain(c.CompanyDomain || c.Website || c.email1 || c.email || c.Email || '');

  const getBusinessEmail = (contact: Contact): string =>
    String(pickContactField(contact, ['email', 'Email', 'email1', 'businessEmail1', 'businessEmail']) || '').trim();

  const getPersonalEmail = (contact: Contact): string =>
    String(pickContactField(contact, ['personalEmail1', 'PersonalEmail', 'personal_email', 'personalEmail']) || '').trim();

  const getBusinessPhone = (contact: Contact): string =>
    String(
      pickContactField(contact, ['contactPhone1', 'directDial', 'DirectDial', 'Phone', 'companyPhone1', 'CompanyPhone1']) || ''
    ).trim();

  const getPersonalPhone = (contact: Contact): string =>
    String(
      pickContactField(contact, ['contactMobilePhone1', 'mobile', 'MobilePhone', 'Mobile', 'contactPhone1', 'Phone']) || ''
    ).trim();

  const getContactTitle = (contact: Contact): string =>
    String(pickContactField(contact, ['Title', 'title']) || '').trim();

  const getContactLocationText = (contact: Contact): string =>
    formatLocation(contact.ContactLocation) || '';

  const getCompanyLocationText = (contact: Contact): string =>
    formatLocation(
      contact.CompanyLocation || (contact as any).CompanyHQ || (contact as any).Headquarters || (contact as any).headquarters
    ) || '';

  const buildPepContact = (c: Contact) => {
    const firstName = pickContactField(c, ['firstName', 'FirstName', 'first_name', 'First']);
    const lastName = pickContactField(c, ['lastName', 'LastName', 'last_name', 'Last']);
    const contactName = getContactName(c);
    const companyName = getContactCompany(c);
    const industry = pickContactField(c, ['CompanyIndustry', 'CompanyIndustries', 'Industry', 'industry']);
    const linkedinUrl = getContactSocialUrl(c, 'linkedin') || pickContactField(c, ['LIProfileUrl', 'linkedinUrl', 'linkedin_url']);
    const email = pickContactField(c, ['email', 'Email', 'email1', 'businessEmail1', 'businessEmail']);
    const phone = pickContactField(c, ['contactPhone1', 'contactMobilePhone1', 'Phone']);
    const stageRaw = pickContactField(c, ['stage', 'Stage']);
    const stage = Number(stageRaw) || 1;
    const heat = Number(pickContactField(c, ['perpetual_heat_score', 'PerpetualHeat', 'perpetualHeatScore'])) || 0;
    const potential = Number(pickContactField(c, ['potential_heat_score', 'PotentialHeat', 'potentialHeatScore'])) || 0;
    const velocity =
      (pickContactField(c, ['heat_velocity', 'heatVelocity']) as any) ||
      'FLAT';
    const primaryChannel = linkedinUrl ? 'LINKEDIN' : email ? 'EMAIL' : 'LINKEDIN';
    const emailSends = Number(pickContactField(c, ['email_sends_last_14_days', 'emailSendsLast14Days'])) || 0;
    const tags = Array.isArray((c as any).tags) ? (c as any).tags : [];
    const priorTouches = Array.isArray((c as any).prior_touches) ? (c as any).prior_touches : [];
    return {
      id: String((c as any).contactId || (c as any).id || c.searchResultId || contactName),
      first_name: firstName || contactName.split(' ')[0] || '',
      last_name: lastName || contactName.split(' ').slice(1).join(' ') || '',
      title: pickContactField(c, ['Title', 'title']),
      company_name: companyName,
      industry,
      location: formatLocation(c.ContactLocation || c.CompanyLocation) || '',
      profile_url: linkedinUrl || '',
      email: email || null,
      phone: phone || null,
      stage,
      gate_cleared: true,
      direct_reply_received: Boolean(pickContactField(c, ['direct_reply_received', 'directReplyReceived'])),
      voicemail_permitted: Boolean(pickContactField(c, ['voicemail_permitted', 'voicemailPermitted'])),
      do_not_contact: Boolean(pickContactField(c, ['do_not_contact', 'doNotContact'])),
      perpetual_heat_score: heat,
      potential_heat_score: potential,
      heat_velocity: velocity,
      cold_days: Number(pickContactField(c, ['cold_days', 'coldDays'])) || 0,
      primary_channel: primaryChannel,
      email_sends_last_14_days: emailSends,
      tags,
      prior_touches: priorTouches,
    };
  };

  const buildPepSignals = () => ({
    job_listings: {
      score: 0,
      detected_at: null,
      summary: null,
      roles: [],
      tools_mentioned: [],
      pain_keywords: [],
    },
    growth_announcements: {
      score: 0,
      detected_at: null,
      summary: null,
      announcement_type: null,
      amount: null,
      source: null,
    },
    client_reviews: {
      score: 0,
      detected_at: null,
      summary: null,
      positive_themes: [],
      negative_themes: [],
      source: null,
    },
    social_activity: {
      score: 0,
      detected_at: null,
      summary: null,
      post_topic: null,
      engagement_count: 0,
      platform: null,
      hashtag_listener_catch: false,
      caught_comment: null,
    },
    employee_sentiment: {
      score: 0,
      detected_at: null,
      summary: null,
      pain_themes: [],
      tools_mentioned: [],
      source: null,
    },
  });

  const deepFindArray = (obj: any, keys: string[]): any[] | null => {
    if (!obj) return null;
    if (Array.isArray(obj)) return null;
    for (const key of keys) {
      if (Array.isArray(obj[key])) return obj[key];
    }
    for (const value of Object.values(obj)) {
      if (Array.isArray(value)) {
        if (keys.includes('any')) return value;
      } else if (value && typeof value === 'object') {
        const found = deepFindArray(value, keys);
        if (found) return found;
      }
    }
    return null;
  };

  const deepCollect = (obj: any, predicate: (key: string, value: any) => boolean, out: any[] = []) => {
    if (!obj) return out;
    if (Array.isArray(obj)) {
      obj.forEach((item) => deepCollect(item, predicate, out));
      return out;
    }
    if (typeof obj === 'object') {
      for (const [k, v] of Object.entries(obj)) {
        if (predicate(k, v)) out.push(v);
        if (v && typeof v === 'object') deepCollect(v, predicate, out);
      }
    }
    return out;
  };

  const extractOpenRoles = (data: any): number | null => {
    if (!data) return null;
    const counts = deepCollect(data, (k, v) => typeof v === 'number' && /open_roles|open_positions|openings|job_openings|jobs_count|job_count/i.test(k));
    if (counts.length) return Number(counts[0]);
    const jobsArr = deepFindArray(data, ['jobs', 'job_postings', 'jobPostings', 'open_roles', 'open_positions', 'openJobs', 'jobListings', 'listings']);
    if (jobsArr && Array.isArray(jobsArr)) return jobsArr.length;
    return null;
  };

  const extractTotalFunding = (data: any): string => {
    if (!data) return '';
    const values = deepCollect(
      data,
      (k, v) =>
        (typeof v === 'number' || typeof v === 'string') &&
        /total_funding|funding_total|totalFunding|total_funding_usd|funding_total_usd/i.test(k)
    ).filter(Boolean);
    const raw = values[0];
    if (raw === undefined || raw === null) return '';
    return String(raw);
  };

  const extractIntentSignals = (data: any): string[] => {
    if (!data) return [];
    const list =
      deepFindArray(data, ['bombora_surge_list', 'intent_signals', 'intent_list', 'intent_topics', 'topics']) || [];
    if (!Array.isArray(list)) return [];
    return list
      .map((item: any) => item?.topic_identifier?.value || item?.topic?.value || item?.name || item?.value || item)
      .filter(Boolean);
  };

  const collectRatings = (data: any): number[] => {
    if (!data) return [];
    const ratings = deepCollect(data, (k, v) => typeof v === 'number' && /rating|score|stars/i.test(k)).filter(
      (v) => typeof v === 'number'
    );
    return ratings as number[];
  };

  const computeReviewAverage = (g2Data: any, trustpilotData: any): string => {
    const combined = [...collectRatings(g2Data), ...collectRatings(trustpilotData)];
    if (!combined.length) return '';
    const avg = combined.reduce((a, b) => a + b, 0) / combined.length;
    return avg.toFixed(2);
  };

  const formatSignalValue = (value: unknown): string => {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (value instanceof Date) return value.toISOString();
    if (Array.isArray(value)) return value.map((item) => formatSignalValue(item)).filter(Boolean).join(', ');
    if (typeof value === 'object') {
      const v = value as Record<string, unknown>;
      const preferred =
        v.iso ||
        v.isoDate ||
        v.iso_date ||
        v.date ||
        v.datetime ||
        v.timestamp ||
        v.value ||
        v.text ||
        v.label;
      if (preferred != null && preferred !== value) return formatSignalValue(preferred);
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const formatDateTimeMMDDYYYY = (value: unknown): string => {
    const raw = formatSignalValue(value);
    if (!raw) return '';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = String(date.getFullYear());
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${mm}/${dd}/${yyyy} ${hh}:${min}:${ss}`;
  };

  const getContactLinkedinPosts = (contact: Contact): Array<{ id: string; title: string; time: string; url: string }> => {
    const extractPosts = (payload: any): any[] => {
      if (!payload) return [];
      if (Array.isArray(payload?.posts)) return payload.posts;
      if (Array.isArray(payload)) {
        return payload.flatMap((entry: any) =>
          Array.isArray(entry?.posts) ? entry.posts : entry && typeof entry === 'object' ? [entry] : []
        );
      }
      return [];
    };

    let posts: any[] = [];
    if (contactSocialReport && contactSocialReport.platform === 'linkedin' && contactSocialReport.contact.searchResultId === contact.searchResultId) {
      posts = extractPosts(contactSocialReport.items);
    }
    if (!posts.length) {
      posts = extractPosts((contact as any).linkedin_ds);
    }

    return posts
      .map((post: any, idx: number) => {
        const titleRaw =
          post?.text ||
          post?.title ||
          post?.caption ||
          post?.message ||
          post?.content ||
          '';
        const title = String(titleRaw || '').replace(/\s+/g, ' ').trim();
        const timeRaw = post?.date || post?.postedAt || post?.createdAt || post?.timestamp || post?.published;
        const urlRaw = post?.url || post?.post_url || post?.postUrl || post?.activityUrl || post?.link || '';
        const id = String(post?.id || post?.post_id || post?.urn || idx);
        return {
          id,
          title: title || '(No title)',
          time: formatDateTimeMMDDYYYY(timeRaw),
          url: String(urlRaw || ''),
          _timeOrder: new Date(formatSignalValue(timeRaw)).getTime() || 0,
        };
      })
      .sort((a: any, b: any) => (b._timeOrder || 0) - (a._timeOrder || 0))
      .slice(0, 10)
      .map(({ _timeOrder, ...rest }: any) => rest);
  };

  const getContactLinkedinActivity = (contact: Contact): { count: number; latest?: string } | null => {
    const posts = getContactLinkedinPosts(contact);
    if (!posts.length) return null;
    const latest = posts[0]?.time || undefined;
    return { count: posts.length, latest };
  };

  const INDIRECT_INTENT_MAX_TOTAL = 20;
  const INDIRECT_INTENT_MAX_PER_POST = 5;
  const indirectPromotionKeywords = [
    'event', 'fundraiser', 'fundraising', 'campaign', 'donor', 'donate', 'charity',
    'ticket', 'raffle', 'gala', 'webinar', 'summit', 'workshop', 'program', 'camp',
    'register', 'signup', 'awareness', 'community', 'volunteer', 'outreach', 'application',
    'hiring', 'open position', 'team is growing', 'apply',
  ];
  const indirectCtaKeywords = [
    'give today', 'learn more', 'visit', 'join us', 'register', 'apply', 'support', 'help',
  ];

  const computeIndirectIntentFromPosts = (posts: Array<{ id: string; title: string; time: string; url: string }>) => {
    let runningTotal = 0;
    const postScores = posts.map((post) => {
      const text = `${post.title || ''} ${post.url || ''}`.toLowerCase();
      let points = 0;
      const reasons: string[] = [];

      const promotionHits = indirectPromotionKeywords.filter((k) => text.includes(k));
      if (promotionHits.length) {
        points += 2;
        reasons.push(`promotion signal (${promotionHits[0]})`);
      }
      const ctaHits = indirectCtaKeywords.filter((k) => text.includes(k));
      if (ctaHits.length) {
        points += 1;
        reasons.push(`call-to-action (${ctaHits[0]})`);
      }
      if (/https?:\/\/|lnkd\.in\//i.test(post.title || '') || /https?:\/\//i.test(post.url || '')) {
        points += 1;
        reasons.push('external link');
      }
      if (/(hiring|open position|team is growing|apply)/i.test(text)) {
        points += 1;
        reasons.push('growth/recruiting');
      }

      const perPost = Math.max(0, Math.min(INDIRECT_INTENT_MAX_PER_POST, points));
      const roomLeft = Math.max(0, INDIRECT_INTENT_MAX_TOTAL - runningTotal);
      const applied = Math.min(perPost, roomLeft);
      runningTotal += applied;

      return {
        id: post.id,
        perPost,
        applied,
        reasons,
      };
    });

    return {
      totalPoints: Math.min(INDIRECT_INTENT_MAX_TOTAL, runningTotal),
      postScores,
      capped: true,
    };
  };

  const normalizeFitCheckPost = (raw: any, idx: number, fallbackUrl?: string): any | null => {
    if (!raw || typeof raw !== 'object') return null;
    const urlRaw =
      raw.url ||
      raw.post_url ||
      raw.postUrl ||
      raw.permalink ||
      raw.link ||
      raw.activityUrl ||
      raw.inputUrl ||
      fallbackUrl ||
      '';
    const url = typeof urlRaw === 'string' ? urlRaw.trim() : '';
    const id = raw.id || raw.post_id || raw.postId || raw.urn || `${idx}`;
    const text =
      raw.text ||
      raw.caption ||
      raw.message ||
      raw.content ||
      raw.title ||
      raw.notes ||
      '';
    const hashtags = Array.isArray(raw.hashtags)
      ? raw.hashtags.map((h: any) => String(h).replace(/^#/, '')).filter(Boolean)
      : [];
    const mediaType = raw.mediaType || raw.type || raw.postType || 'post';
    const postedAt = raw.postedAt || raw.timestamp || raw.create_time || raw.date || raw.published || null;
    const commentsCount = Number(raw.commentsCount ?? raw.comment_count ?? raw.commentCount ?? raw.comments_count ?? 0) || 0;
    const likesCount = Number(raw.likesCount ?? raw.like_count ?? raw.reactions_count ?? raw.reactionCount ?? 0) || 0;
    return {
      id: String(id),
      url: url || undefined,
      text: String(text || ''),
      hashtags,
      mediaType: String(mediaType || 'post'),
      postedAt: postedAt ? formatSignalValue(postedAt) : null,
      commentsCount,
      likesCount,
    };
  };

  const buildFitCheckRequest = (contact: Contact): any | null => {
    const sources: Array<{ platform: string; posts: any[] }> = [];
    const platformFallbackUrl: Record<string, string> = {
      linkedin: String(
        pickContactField(contact, ['LinkedInURL', 'LinkedinURL', 'linkedin_url', 'linkedin', 'linkedinProfileUrl']) || ''
      ).trim(),
      instagram: String(pickContactField(contact, ['Instagram', 'instagram']) || '').trim(),
      facebook: String(pickContactField(contact, ['Facebook', 'facebook']) || '').trim(),
      x: String(pickContactField(contact, ['XPage', 'x_page', 'twitter', 'Twitter']) || '').trim(),
    };

    const addSource = (platform: string, payload: any) => {
      if (!payload) return;
      const posts = Array.isArray(payload?.posts)
        ? payload.posts
        : Array.isArray(payload)
          ? payload.flatMap((entry: any) => (Array.isArray(entry?.posts) ? entry.posts : entry ? [entry] : []))
          : [];
      if (posts.length) sources.push({ platform, posts });
    };

    if (contactSocialReport && contactSocialReport.contact.searchResultId === contact.searchResultId) {
      const sourcePayload = Array.isArray(contactSocialReport.items) ? contactSocialReport.items : [];
      addSource(contactSocialReport.platform, sourcePayload);
    }

    const cAny = contact as any;
    addSource('linkedin', cAny.linkedin_ds);
    addSource('instagram', cAny.instagram_ds);
    addSource('facebook', cAny.facebook_ds);
    addSource('x', cAny.x_ds || cAny.twitter_ds);

    const linkedinPostsForFit = getContactLinkedinPosts(contact);
    const chosen = linkedinPostsForFit.length > 0
      ? { platform: 'linkedin', posts: linkedinPostsForFit.map((post) => ({
          id: post.id,
          url: post.url,
          text: post.title,
          postedAt: post.time,
        })) }
      : sources.find((s) => s.posts.length > 0);
    if (!chosen) return null;

    const candidates = chosen.posts
      .map((post: any, idx: number) => normalizeFitCheckPost(post, idx, platformFallbackUrl[chosen.platform]))
      .filter(Boolean)
      .slice(0, 10);

    if (!candidates.length && platformFallbackUrl[chosen.platform]) {
      candidates.push({
        id: `${chosen.platform}-fallback`,
        url: platformFallbackUrl[chosen.platform],
        text: String(buildPepSignalsFromIntel(contact)?.social_activity?.summary || ''),
        hashtags: [],
        mediaType: 'profile',
        postedAt: null,
        commentsCount: 0,
        likesCount: 0,
      });
    }

    if (!candidates.length) return null;

    return {
      desired_action: 'comment',
      platform: chosen.platform,
      min_score: 80,
      candidates,
    };
  };

  const buildPepSignalsFromIntel = (contact: Contact) => {
    const signals = buildPepSignals() as any;
    const companyName = deriveCompanyName(contact);
    const cbRaw = crunchbaseReports[companyName] || (contact as any).crunchbase_ds || null;
    const cbData = Array.isArray(cbRaw) ? (cbRaw[0] || null) : cbRaw;
    const fundingTotal = extractTotalFunding(cbData);
    const intentSignals = extractIntentSignals(cbData);
    const openRoles = extractOpenRoles(cbData);
    if (fundingTotal || intentSignals.length) {
      signals.growth_announcements.summary = [
        fundingTotal ? `Total raised: ${fundingTotal}` : '',
        intentSignals.length ? `Intent: ${intentSignals.join(', ')}` : '',
      ].filter(Boolean).join(' · ');
      signals.growth_announcements.source = 'Crunchbase';
      signals.growth_announcements.score = intentSignals.length ? 60 : 40;
      signals.growth_announcements.detected_at = new Date().toISOString();
    }
    if (openRoles !== null) {
      signals.job_listings.summary = `${openRoles} open roles`;
      signals.job_listings.score = openRoles > 0 ? 55 : 0;
      signals.job_listings.detected_at = new Date().toISOString();
    }
    const liActivity = getContactLinkedinActivity(contact);
    if (liActivity?.count) {
      signals.social_activity.summary = `LinkedIn posts: ${liActivity.count}${liActivity.latest ? ` (latest ${liActivity.latest})` : ''}`;
      signals.social_activity.platform = 'linkedin';
      signals.social_activity.engagement_count = liActivity.count;
      signals.social_activity.score = 45;
    }
    const reviewAverage = computeReviewAverage((contact as any).g2_ds, (contact as any).trustpilot_ds);
    if (reviewAverage) {
      signals.client_reviews.summary = `Average rating: ${reviewAverage} / 5`;
      signals.client_reviews.source = 'reviews';
      signals.client_reviews.score = 40;
    }
    return signals;
  };

  const computeFitcheckScores = (
    contact: Contact,
    options: {
      pepProfile?: PepProfile | null;
      linkedinPosts: Array<{ id: string; title: string; time: string; url: string }>;
      pepFitCheck?: any;
    }
  ) => {
    const { pepProfile, linkedinPosts, pepFitCheck } = options;
    const icp = String(pepProfile?.icpPersona || '').toLowerCase();
    const industry = String(
      pickContactField(contact, ['CompanyIndustries', 'CompanyIndustry', 'Industry', 'industry']) || ''
    ).toLowerCase();
    const department = String(pickContactField(contact, ['department', 'Department', 'dept', 'Dept']) || '').toLowerCase();
    const companyWebsite = String(
      pickContactField(contact, ['CompanyWebsite', 'Website', 'companyWebsite', 'companyDomain', 'CompanyDomain']) || ''
    ).trim();
    const companyLinkedin = String(
      pickContactField(contact, ['CompanyLinkedInURL', 'companyLinkedinUrl', 'company_linkedin_url']) || ''
    ).trim();
    const contactLinkedin = normalizeLinkedinProfileUrl(
      String(pickContactField(contact, ['linkedIn', 'LIProfileUrl', 'linkedinUrl', 'linkedin_url']) || '')
    );
    const businessEmail = String(
      pickContactField(contact, ['email', 'Email', 'email1', 'businessEmail1', 'businessEmail']) || ''
    ).trim();
    const businessPhone = String(
      pickContactField(contact, ['contactPhone1', 'directDial', 'DirectDial', 'Phone', 'companyPhone1', 'CompanyPhone1']) || ''
    ).trim();
    const mobilePhone = String(
      pickContactField(contact, ['contactMobilePhone1', 'mobile', 'MobilePhone', 'Mobile']) || ''
    ).trim();

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(businessEmail);
    const hasPhone = businessPhone.replace(/\D/g, '').length >= 10;
    const hasMobile = mobilePhone.replace(/\D/g, '').length >= 10;
    const hasContactLi = /^https?:\/\/(www\.)?linkedin\.com\//i.test(contactLinkedin);
    const hasCompanyLi = /^https?:\/\/(www\.)?linkedin\.com\//i.test(companyLinkedin);
    const hasWebsite = /^https?:\/\/|^[a-z0-9.-]+\.[a-z]{2,}$/i.test(companyWebsite);

    const personalSocialCount = ['facebook', 'instagram', 'youtube', 'tiktok', 'x', 'threads']
      .map((platform) => String(getContactSocialUrl(contact, platform as any) || '').trim())
      .filter(Boolean).length;
    const companySocialCount = [
      pickContactField(contact, ['CompanyFacebook', 'companyFacebook']),
      pickContactField(contact, ['CompanyInstagram', 'companyInstagram']),
      pickContactField(contact, ['CompanyYoutube', 'companyYoutube']),
      pickContactField(contact, ['CompanyX', 'companyX', 'companyTwitter']),
      pickContactField(contact, ['CompanyLinkedInURL', 'companyLinkedinUrl']),
    ].map((v) => String(v || '').trim()).filter(Boolean).length;

    const coreConnectSignals = [isValidEmail, hasPhone, hasMobile, hasContactLi, hasCompanyLi, hasWebsite];
    const coreConnectScore = (coreConnectSignals.filter(Boolean).length / coreConnectSignals.length) * 100;
    const socialBonus = Math.min(10, personalSocialCount * 1.2 + companySocialCount * 1.2);
    let connectability = Math.min(100, coreConnectScore + socialBonus);
    const coreCount = coreConnectSignals.filter(Boolean).length;
    if (coreCount <= 1) connectability = Math.max(0, connectability - 35);
    else if (coreCount <= 2) connectability = Math.max(0, connectability - 20);

    const icpTokens = icp.split(/[^a-z0-9]+/).filter((v) => v.length >= 4);
    const industryMatches = icpTokens.some((token) => industry.includes(token));
    const departmentMatches = icpTokens.some((token) => department.includes(token));
    const hasPostIntent = linkedinPosts.length > 0;
    let useCasePotential =
      (industryMatches ? 40 : (industry ? 8 : 0)) +
      (departmentMatches ? 30 : (department ? 6 : 0)) +
      (hasPostIntent ? 30 : 0);
    if (!industry && !department && !hasPostIntent) useCasePotential = Math.max(0, useCasePotential - 40);
    useCasePotential = Math.min(100, useCasePotential);

    const contextFields = [
      Boolean(pickContactField(contact, ['firstName', 'FirstName', 'first_name', 'First'])),
      Boolean(pickContactField(contact, ['lastName', 'LastName', 'last_name', 'Last'])),
      Boolean(pickContactField(contact, ['Title', 'title'])),
      Boolean(department),
      Boolean(industry),
      Boolean(companyWebsite),
      Boolean(contactLinkedin),
      isValidEmail,
      hasPhone,
      hasMobile,
      linkedinPosts.length > 0,
      Boolean((contact as any).crunchbase_ds),
    ];
    const depthRatio = contextFields.filter(Boolean).length / contextFields.length;
    let contextDataDepth = depthRatio * 100;
    if (depthRatio < 0.25) contextDataDepth = Math.max(0, contextDataDepth - 35);
    else if (depthRatio < 0.5) contextDataDepth = Math.max(0, contextDataDepth - 20);

    const contactScore =
      (connectability * 0.4) +
      (useCasePotential * 0.3) +
      (contextDataDepth * 0.3);

    const employeeCount = String(pickContactField(contact, ['CompanyStaffCount', 'CompanyStaffCountRange', 'employeeCount']) || '');
    const revenueRange = String(
      pickContactField(contact, ['CompanyRevenue', 'RevenueRange', 'revenueRange', 'CompanyRevenueRange', 'CompanyAnnualRevenue']) || ''
    );
    const hasSizeRevenue = Boolean(employeeCount || revenueRange);
    const sizeRevenueFit = hasSizeRevenue ? 75 : 0;

    const signals = buildPepSignalsFromIntel(contact);
    const buyingSignalsRaw = [
      Number(signals.growth_announcements?.score || 0),
      Number(signals.job_listings?.score || 0),
      Number(signals.client_reviews?.score || 0),
      Number(signals.social_activity?.score || 0),
    ];
    const buyingSignals = Math.min(100, (buyingSignalsRaw.reduce((a, b) => a + b, 0) / buyingSignalsRaw.length) * 1.4);
    const engagementFootprint = Math.min(
      100,
      (linkedinPosts.length * 6) +
      (personalSocialCount * 5) +
      (companySocialCount * 5)
    );
    let icpIndustryScore = industryMatches ? 100 : (industry ? 20 : 0);
    if (!industry) icpIndustryScore = Math.max(0, icpIndustryScore - 20);

    let companyScore =
      (icpIndustryScore * 0.25) +
      (sizeRevenueFit * 0.15) +
      (buyingSignals * 0.30) +
      (engagementFootprint * 0.30);
    if (!companyWebsite && !industry) companyScore = Math.max(0, companyScore - 25);

    const combinedScore = (companyScore * 0.6) + (contactScore * 0.4);

    const band = (score: number) => {
      if (score >= 90) return 'Ideal';
      if (score >= 70) return 'Strong';
      if (score >= 50) return 'Medium';
      if (score >= 25) return 'Weak';
      return 'Unsure';
    };

    const fitCheckCandidates = Array.isArray(pepFitCheck?.candidate_scores) ? pepFitCheck.candidate_scores : [];

    return {
      contactScore: Math.round(contactScore),
      companyScore: Math.round(companyScore),
      combinedScore: Math.round(combinedScore),
      contactBand: band(contactScore),
      companyBand: band(companyScore),
      combinedBand: band(combinedScore),
      contactBreakdown: {
        connectability: Math.round(connectability),
        use_case_potential: Math.round(useCasePotential),
        context_data_depth: Math.round(contextDataDepth),
      },
      companyBreakdown: {
        icp_industry_match: Math.round(icpIndustryScore),
        size_revenue_fit: Math.round(sizeRevenueFit),
        buying_signals: Math.round(buyingSignals),
        engagement_footprint: Math.round(engagementFootprint),
      },
      fitCheckCandidates,
    };
  };

  const getPepLeadId = (contact: Contact) =>
    String((contact as any).contactId || (contact as any).id || contact.searchResultId || getContactName(contact));

  const getPersistedPepRunId = (contact: Contact) =>
    String((contact as any).pep_sequence_run_id || '').trim();

  const getPepStorageKey = (leadId: string) => `pep:last:${userEmail || 'anon'}:${leadId}`;

  const compactPepCache = (value: any) => {
    if (!value || typeof value !== 'object') return value;
    const fitCheck = value.fit_check && typeof value.fit_check === 'object'
      ? {
          approved: value.fit_check.approved ?? false,
          fit_score: value.fit_check.fit_score ?? 0,
          reason: value.fit_check.reason ?? '',
          recommended_action: value.fit_check.recommended_action ?? '',
          target_url: value.fit_check.target_url ?? '',
          themes: Array.isArray(value.fit_check.themes) ? value.fit_check.themes.slice(0, 5) : [],
          recurring_topics: Array.isArray(value.fit_check.recurring_topics) ? value.fit_check.recurring_topics.slice(0, 5) : [],
          candidate_scores: Array.isArray(value.fit_check.candidate_scores) ? value.fit_check.candidate_scores.slice(0, 5) : [],
        }
      : undefined;
    const touchpointPath = Array.isArray(value.touchpoint_path)
      ? value.touchpoint_path.slice(0, 10)
      : undefined;
    const decisionObject = value.decision_object && typeof value.decision_object === 'object'
      ? {
          selected_signal: value.decision_object.selected_signal ?? '',
          signal_score: value.decision_object.signal_score ?? 0,
          signal_summary: value.decision_object.signal_summary ?? '',
          channel: value.decision_object.channel ?? '',
          campaign_mode: value.decision_object.campaign_mode ?? '',
          touch_number: value.decision_object.touch_number ?? 0,
        }
      : undefined;
    const message = value.message && typeof value.message === 'object'
      ? {
          subject: value.message.subject ?? null,
          body: typeof value.message.body === 'string' ? value.message.body.slice(0, 4000) : '',
          cta_type: value.message.cta_type ?? '',
        }
      : undefined;
    return {
      decision: value.decision ?? '',
      hold_reason: value.hold_reason ?? null,
      contact_id: value.contact_id ?? '',
      recommended_next_action: value.recommended_next_action ?? '',
      decision_object: decisionObject,
      message,
      fit_check: fitCheck,
      touchpoint_path: touchpointPath,
      playbook: value.playbook ?? undefined,
      cachedAt: new Date().toISOString(),
    };
  };

  const isLegacyPepPayload = (value: any) => {
    const steps = Array.isArray(value?.touchpoint_path)
      ? value.touchpoint_path
      : Array.isArray(value?.decision?.touchpoint_path)
        ? value.decision.touchpoint_path
        : Array.isArray(value?.record?.decision?.touchpoint_path)
          ? value.record.decision.touchpoint_path
          : Array.isArray(value?.result?.decision?.touchpoint_path)
            ? value.result.decision.touchpoint_path
            : [];
    const actions = steps.map((step: any) => String(step?.action || '').trim().toUpperCase()).filter(Boolean);
    if (!actions.length) return false;
    const legacyActions = new Set([
      'SOCIAL_RESEARCH',
      'COMPANY_RESEARCH',
      'RESEARCH_PODCAST',
      'RESEARCH_SUBSTACK',
      'INTENT_CHECK',
      'PAUSE_LOW_ENGAGEMENT',
    ]);
    return actions.length > 15 || actions.some((action: string) => legacyActions.has(action));
  };

  const buildCurrentPepTouchpointPath = () => ([
    { day: 1, action: 'EMAIL_SEND', channel: 'EMAIL', message_required: true, angle: 'Primary Signal Lead', signal_hook_source: 'primary signal', wait_days: 0 },
    { day: 2, action: 'WEBSITE_SCRAPE', channel: null, message_required: false, angle: 'Research Day', signal_hook_source: 'website', wait_days: 1 },
    { day: 2, action: 'LINKEDIN_PROFILE_SCRAPE', channel: 'LINKEDIN', message_required: false, angle: 'Research Day', signal_hook_source: 'linkedin_profile', wait_days: 1 },
    { day: 2, action: 'LINKEDIN_360_SCRAPE', channel: 'LINKEDIN', message_required: false, angle: 'Research Day', signal_hook_source: 'linkedin_360', wait_days: 1 },
    { day: 2, action: 'INSTANTLY_CHECK', channel: 'EMAIL', message_required: false, angle: 'Research Day', signal_hook_source: 'instantly_feedback', wait_days: 1 },
    { day: 2, action: 'SOCIAL_PROFILE_SCRAPE', channel: null, message_required: false, angle: 'Research Day', signal_hook_source: 'social_discovery', wait_days: 1 },
    { day: 3, action: 'LINKEDIN_VIEW_PROFILE', channel: 'LINKEDIN', message_required: false, angle: 'Decision Day', signal_hook_source: 'linkedin_presence', wait_days: 1 },
    { day: 3, action: 'DECIDE_NEXT_ACTION', channel: null, message_required: false, angle: 'Decision Day', signal_hook_source: 'multi_source_assessment', wait_days: 1 },
    { day: 3, action: 'PROSPECT_ASSIGNMENT_UPDATE', channel: null, message_required: false, angle: 'Decision Day', signal_hook_source: 'assignment_reassessment', wait_days: 1 },
    { day: 4, action: 'INTERACTION_CHECK', channel: null, message_required: false, angle: 'Review Day', signal_hook_source: 'engagement_review', wait_days: 1 },
    { day: 4, action: 'EMAIL_2_DECISION', channel: 'EMAIL', message_required: false, angle: 'Review Day', signal_hook_source: 'email_timing', wait_days: 1 },
    { day: 5, action: 'SOCIAL_PROFILE_VIEW_PROXY', channel: null, message_required: false, angle: 'Reassessment Day', signal_hook_source: 'social_proxy_view', wait_days: 1 },
    { day: 5, action: 'SOCIAL_PROFILE_VIEW_PERSONAL', channel: null, message_required: false, angle: 'Reassessment Day', signal_hook_source: 'social_personal_view', wait_days: 1 },
    { day: 5, action: 'FOLLOW_COMPANY_PAGE', channel: null, message_required: false, angle: 'Reassessment Day', signal_hook_source: 'company_follow', wait_days: 1 },
    { day: 5, action: 'REASSESS_PROGRAM', channel: null, message_required: false, angle: 'Reassessment Day', signal_hook_source: 'program_adjustment', wait_days: 1 },
  ]);

  const CURRENT_PEP_SEQUENCE_VERSION = 'PEPAI-D1-D5-v1';

  const sanitizePepPayload = (value: any) => {
    if (!value || typeof value !== 'object') return value;
    if (!isLegacyPepPayload(value)) return value;
    const freshPath = buildCurrentPepTouchpointPath();
    return {
      ...value,
      touchpoint_path: freshPath,
      decision: value.decision && typeof value.decision === 'object'
        ? { ...value.decision, touchpoint_path: freshPath }
        : value.decision,
      record: value.record && typeof value.record === 'object'
        ? {
            ...value.record,
            touchpoint_path: freshPath,
            decision: value.record.decision && typeof value.record.decision === 'object'
              ? { ...value.record.decision, touchpoint_path: freshPath }
              : value.record.decision,
          }
        : value.record,
      result: value.result && typeof value.result === 'object'
        ? {
            ...value.result,
            touchpoint_path: freshPath,
            decision: value.result.decision && typeof value.result.decision === 'object'
              ? { ...value.result.decision, touchpoint_path: freshPath }
              : value.result.decision,
            record: value.result.record && typeof value.result.record === 'object'
              ? {
                  ...value.result.record,
                  touchpoint_path: freshPath,
                  decision: value.result.record.decision && typeof value.result.record.decision === 'object'
                    ? { ...value.result.record.decision, touchpoint_path: freshPath }
                    : value.result.record.decision,
                }
              : value.result.record,
          }
        : value.result,
    };
  };

  const writePepCache = (storageKey: string, value: any) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(compactPepCache(sanitizePepPayload(value))));
    } catch {
      // ignore storage quota and browser storage failures
    }
  };

  const loadPepLatest = async (contact: Contact) => {
    if (!userEmail) return;
    const key = getPepLeadId(contact);
    const leadId = getPepLeadId(contact);
    const storageKey = getPepStorageKey(leadId);
    try {
      const cached = window.localStorage.getItem(storageKey);
      if (cached) {
        const parsed = sanitizePepPayload(JSON.parse(cached));
        setPepData((prev) => ({ ...prev, [key]: parsed }));
      }
    } catch {
      // ignore local cache parse errors
    }
    try {
      const res = await getPepLatest(userEmail, leadId);
      if (res?.record) {
        const payload = sanitizePepPayload(res.record.decision ? res.record : res.record.result || res.record);
        setPepData((prev) => ({ ...prev, [key]: payload }));
        writePepCache(storageKey, payload);
      }
    } catch (err) {
      // ignore
    }
  };

  const loadPepRunLatest = async (contact: Contact) => {
    if (!userEmail) return;
    const key = getPepLeadId(contact);
    const leadId = getPepLeadId(contact);
    const runId = getPersistedPepRunId(contact);
    try {
      const res = await getPepRunStatus({
        tenantId: userEmail,
        ...(runId ? { runId } : { leadId }),
      });
      setPepRunData((prev) => ({
        ...prev,
        [key]: {
          run: res?.run || null,
          steps: Array.isArray(res?.steps) ? res.steps : [],
        },
      }));
    } catch {
      // ignore run status load errors
    }
  };

  useEffect(() => {
    if (!canUsePepaiResearch) {
      setInstantlyCampaigns([]);
      setSelectedInstantlyCampaignId('');
      try {
        window.localStorage.removeItem(PEPAI_CAMPAIGN_STORAGE_KEY);
      } catch {
        // ignore
      }
      return;
    }
    listInstantlyCampaignOptions({ userEmail })
      .then((data) => {
        if (!data?.success) return;
        const campaigns = Array.isArray(data.campaigns) ? data.campaigns : [];
        setInstantlyCampaigns(campaigns);
        setSelectedInstantlyCampaignId((current) => {
          const existing = current || window.localStorage.getItem(PEPAI_CAMPAIGN_STORAGE_KEY) || '';
          if (existing && campaigns.some((campaign) => campaign.id === existing)) return existing;
          const fallback =
            (data.defaultCampaign?.id && campaigns.some((campaign) => campaign.id === data.defaultCampaign?.id)
              ? data.defaultCampaign.id
              : campaigns[0]?.id) || '';
          if (fallback) {
            try {
              window.localStorage.setItem(PEPAI_CAMPAIGN_STORAGE_KEY, fallback);
            } catch {
              // ignore
            }
          }
          return fallback;
        });
      })
      .catch(() => undefined);
  }, [canUsePepaiResearch, userEmail]);

  useEffect(() => {
    if (!selectedInstantlyCampaignId) return;
    try {
      window.localStorage.setItem(PEPAI_CAMPAIGN_STORAGE_KEY, selectedInstantlyCampaignId);
    } catch {
      // ignore
    }
  }, [selectedInstantlyCampaignId]);

  useEffect(() => {
    window.localStorage.setItem(SEND_TO_CRM_STORAGE_KEY, sendToCrm ? 'true' : 'false');
  }, [sendToCrm]);

  const queuePepForContact = async (contact: Contact) => {
    if (!userEmail) throw new Error('Missing user email');
    const leadId = getPepLeadId(contact);
    const companyName = deriveCompanyName(contact);
    let pepCompanyProfile: PepProfile | null = null;
    if (companyName) {
      try {
        const companyProfileRes = await getPepCompanyProfile(userEmail, companyName);
        if (companyProfileRes?.success && companyProfileRes.profile) {
          pepCompanyProfile = companyProfileRes.profile;
        }
      } catch {
        // continue without company override
      }
    }
    const payload = {
      fitCheck: buildFitCheckRequest(contact),
    };
    const selectedCampaign =
      selectedInstantlyCampaignId
        ? instantlyCampaigns.find((campaign) => campaign.id === selectedInstantlyCampaignId) ?? null
        : null;
    const requestPayload = {
      action: 'START_SEQUENCE',
      channel: payload.fitCheck ? 'COMMENT' : 'EMAIL',
      campaign_mode: 'BRAND',
      sender_name: 'Simply Scrapable',
      sender_company: 'Simply Scrapable',
      landing_page_url: null,
      industry_report_name: null,
      touch_number: 1,
      instantlyCampaignId: selectedCampaign?.id,
      instantlyCampaignName: selectedCampaign?.name,
      pep_profile: pepProfile || undefined,
      pep_profile_company: pepCompanyProfile || undefined,
      fit_check: payload.fitCheck || undefined,
    };
    const pepRequest = {
      tenantId: userEmail,
      leadId,
      contact: buildPepContact(contact),
      signals: buildPepSignalsFromIntel(contact),
      pepProfile: pepProfile || undefined,
      request: requestPayload,
    };
    const queued = await generatePepJob(pepRequest, {
      forceRegenerate: true,
      startSequence: true,
    });
    const jobId = queued?.jobId;
    if (!jobId) {
      throw new Error('PEP job not created');
    }
    return { jobId, leadId };
  };

  const persistPepStatusToContact = async (
    contact: Contact,
    status: string,
    runId?: string | null
  ) => {
    if (!userEmail) return;
    const explicitContactId = String((contact as any).contactId || (contact as any).id || '').trim();
    const searchResultId = String(contact.searchResultId || '').trim();
    if (!explicitContactId && !searchResultId) return;
    try {
      await updateContactRecord({
        userEmail,
        ...(explicitContactId ? { contactId: explicitContactId } : {}),
        ...(searchResultId ? { searchResultId } : {}),
        updates: {
          pep_sequence_status: status,
          pep_sequence_run_id: runId || '',
          pep_sequence_updated_at: new Date().toISOString(),
        },
      });
      upsertContactInCollections({
        ...contact,
        pep_sequence_status: status,
        pep_sequence_run_id: runId || '',
        pep_sequence_updated_at: new Date().toISOString(),
      });
    } catch {
      // ignore contact status persistence failures
    }
  };

  const handleGeneratePep = async (contact: Contact) => {
    if (!userEmail) return;
    const key = getPepLeadId(contact);
    const leadId = getPepLeadId(contact);
    const storageKey = getPepStorageKey(leadId);
    setPepLoading((prev) => ({ ...prev, [key]: true }));
    setPepError((prev) => ({ ...prev, [key]: null }));
    try {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        // ignore storage cleanup failures
      }
      setPepData((prev) => ({ ...prev, [key]: null }));
      const queued = await queuePepForContact(contact);
      const jobId = queued.jobId;
      setPepData((prev) => ({ ...prev, [key]: { status: 'PENDING', jobId } }));
      await persistPepStatusToContact(contact, 'QUEUED');

      let attempt = 0;
      let done = false;
      while (!done && attempt < 12) {
        await new Promise((r) => setTimeout(r, 3000));
        const job = await getPepJob(jobId);
        const status = job?.job?.status;
        if (status === 'COMPLETE') {
          const resolved = sanitizePepPayload(job?.job?.result || job?.result || null);
          if (resolved) {
            setPepData((prev) => ({ ...prev, [key]: resolved }));
            writePepCache(storageKey, resolved);
          }
          const sequenceRunId = job?.job?.sequence?.runId;
          if (sequenceRunId) {
            await persistPepStatusToContact(contact, 'RUNNING', String(sequenceRunId));
          }
          try {
            const runStatus = await getPepRunStatus({
              tenantId: userEmail,
              ...(sequenceRunId ? { runId: String(sequenceRunId) } : { leadId }),
            });
            setPepRunData((prev) => ({
              ...prev,
              [key]: {
                run: runStatus?.run || null,
                steps: Array.isArray(runStatus?.steps) ? runStatus.steps : [],
              },
            }));
            await persistPepStatusToContact(
              contact,
              String(runStatus?.run?.status || 'PENDING').toUpperCase(),
              runStatus?.run?.runId ? String(runStatus.run.runId) : null
            );
          } catch {
            // ignore run status load errors
          }
          done = true;
          break;
        }
        if (status === 'FAILED') {
          throw new Error('PEP job failed');
        }
        attempt += 1;
      }
      if (!done) {
        throw new Error('PEP job timed out');
      }
    } catch (e: any) {
      setPepError((prev) => ({ ...prev, [key]: e?.message || 'Failed to generate P.E.P.ai' }));
    } finally {
      setPepLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleStartPepaiBulk = async () => {
    if (!userEmail || selectedContacts.size === 0 || bulkPepLoading) return;
    setBulkPepLoading(true);
    const selected = contacts.filter((contact) => selectedContacts.has(getSelectionId(contact)));
    let queuedCount = 0;
    let skippedActiveCount = 0;
    let failedCount = 0;

    for (const contact of selected) {
      const key = getPepLeadId(contact);
      const leadId = getPepLeadId(contact);
      try {
        const existingRun = pepRunData[key] || null;
        let runStatus = String(existingRun?.run?.status || '').toUpperCase();
        if (!runStatus) {
          try {
            const latest = await getPepRunStatus({ tenantId: userEmail, leadId });
            if (latest?.run || Array.isArray(latest?.steps)) {
              setPepRunData((prev) => ({
                ...prev,
                [key]: {
                  run: latest?.run || null,
                  steps: Array.isArray(latest?.steps) ? latest.steps : [],
                },
              }));
            }
            runStatus = String(latest?.run?.status || '').toUpperCase();
          } catch {
            // ignore read failures and try to queue
          }
        }

        if (runStatus === 'RUNNING') {
          await persistPepStatusToContact(contact, 'RUNNING', existingRun?.run?.runId ? String(existingRun.run.runId) : null);
          skippedActiveCount += 1;
          continue;
        }

        setPepError((prev) => ({ ...prev, [key]: null }));
        setPepLoading((prev) => ({ ...prev, [key]: true }));
        const storageKey = getPepStorageKey(leadId);
        try {
          window.localStorage.removeItem(storageKey);
        } catch {
          // ignore
        }
        setPepData((prev) => ({ ...prev, [key]: null }));
        const queued = await queuePepForContact(contact);
        setPepData((prev) => ({ ...prev, [key]: { status: 'PENDING', jobId: queued.jobId } }));
        await persistPepStatusToContact(contact, 'QUEUED');
        let attempt = 0;
        while (attempt < 12) {
          await new Promise((r) => setTimeout(r, 3000));
          const job = await getPepJob(queued.jobId);
          const jobStatus = String(job?.job?.status || '').toUpperCase();
          if (jobStatus === 'COMPLETE') {
            const sequenceRunId = job?.job?.sequence?.runId;
            if (sequenceRunId) {
              await persistPepStatusToContact(contact, 'RUNNING', String(sequenceRunId));
              try {
                const runStatus = await getPepRunStatus({
                  tenantId: userEmail,
                  runId: String(sequenceRunId),
                });
                setPepRunData((prev) => ({
                  ...prev,
                  [key]: {
                    run: runStatus?.run || null,
                    steps: Array.isArray(runStatus?.steps) ? runStatus.steps : [],
                  },
                }));
              } catch {
                // ignore run refresh failures
              }
            }
            break;
          }
          if (jobStatus === 'FAILED') {
            throw new Error('PEP job failed');
          }
          attempt += 1;
        }
        queuedCount += 1;
      } catch (error: any) {
        failedCount += 1;
        setPepError((prev) => ({ ...prev, [key]: error?.message || 'Failed to queue P.E.P.ai' }));
      } finally {
        setPepLoading((prev) => ({ ...prev, [key]: false }));
      }
    }

    setBulkPepLoading(false);
    window.alert(`PEPai queued: ${queuedCount}\nSkipped active: ${skippedActiveCount}\nFailed: ${failedCount}`);
  };

  const extractEmails = (value?: string): string[] => {
    if (!value) return [];
    return value
      .split(/[,;\s|]+/)
      .map((email) => email.trim())
      .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(email));
  };

  const pickPrimaryEmail = (value?: string, preferredDomain?: string): string => {
    const emails = extractEmails(value);
    if (!emails.length) return '';
    if (preferredDomain) {
      const matched = emails.find((email) => email.toLowerCase().endsWith(`@${preferredDomain.toLowerCase()}`));
      if (matched) return matched;
    }
    return emails[0];
  };

  const buildContactNotesDraft = (c: Contact) => {
    const companyWebsite = pickContactField(c, ['CompanyWebsite', 'Website', 'companyWebsite', 'companyDomain', 'CompanyDomain']);
    const companyDomain = normalizeDomain(
      companyWebsite ||
      (c as any).CompanyDomain ||
      (c as any).companyDomain ||
      ''
    );
    const businessEmailRaw = joinContactFields(c, ['email1', 'email2', 'email3', 'email', 'Email']);
    const personalEmailRaw = pickContactField(c, ['personalEmail1', 'PersonalEmail', 'personal_email', 'personalEmail']);
    const businessEmail = pickPrimaryEmail(businessEmailRaw, companyDomain);
    const personalEmail = (() => {
      const personal = extractEmails(personalEmailRaw);
      if (!personal.length) return '';
      const deduped = personal.filter((email) => email.toLowerCase() !== businessEmail.toLowerCase());
      return deduped[0] || personal[0];
    })();

    return {
      businessEmail,
      personalEmail,
      businessPhone: pickContactField(c, ['contactPhone1', 'directDial', 'DirectDial', 'Phone', 'companyPhone1', 'CompanyPhone1']),
      mobilePhone: pickContactField(c, ['contactMobilePhone1', 'mobile', 'MobilePhone', 'Mobile']),
      linkedin: normalizeLinkedinProfileUrl(pickContactField(c, ['linkedIn', 'LIProfileUrl', 'linkedinUrl', 'linkedin_url'])),
      location: formatLocation(c.ContactLocation || c.CompanyLocation) || '',
      departmentTags: pickContactField(c, ['DepartmentTags', 'departmentTags', 'Department']),
      seniorityTier: pickContactField(c, ['SeniorityTier', 'seniorityTier', 'Seniority']),
      companySize: pickContactField(c, ['CompanySizeBrackets', 'companySizeBrackets', 'CompanyStaffCountRange']),
      industryCodes: pickContactField(c, ['IndustryCodes', 'industryCodes', 'CompanyNaicsCode']),
    };
  };

  const sameContact = (a: Contact, b: Contact) => {
    const aKeys = [a.searchResultId, (a as any).contactId, (a as any).id].filter(Boolean).map(String);
    const bKeys = [b.searchResultId, (b as any).contactId, (b as any).id].filter(Boolean).map(String);
    return aKeys.some((k) => bKeys.includes(k));
  };

  const upsertContactInCollections = (updated: Contact) => {
    setContacts((prev) => prev.map((item) => (sameContact(item, updated) ? updated : item)));
    setContactProfileDrawer((prev) => (prev && sameContact(prev.contact, updated) ? { contact: updated } : prev));
  };

  const startContactNotesEdit = (contact: Contact) => {
    setContactNotesError(null);
    setContactNotesDraft(buildContactNotesDraft(contact));
    setContactNotesEditing(true);
  };

  const cancelContactNotesEdit = () => {
    setContactNotesEditing(false);
    setContactNotesError(null);
    if (contactProfileDrawer?.contact) {
      setContactNotesDraft(buildContactNotesDraft(contactProfileDrawer.contact));
    } else {
      setContactNotesDraft(null);
    }
  };

  const setContactNotesDraftField = (key: string, value: string) => {
    setContactNotesDraft((prev) => ({ ...(prev || ({} as any)), [key]: value }));
  };

  const saveContactNotes = async (contact: Contact) => {
    if (!userEmail || !contactNotesDraft) return;
    const contactId = String((contact as any).contactId || (contact as any).id || '');
    const searchResultId = String(contact.searchResultId || '');
    if (!contactId && !searchResultId) {
      setContactNotesError('Missing contact identifier');
      return;
    }

    const trimmed = (v: string) => v.trim();
    const withProtocol = (url: string) => {
      const raw = trimmed(url);
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw)) return raw;
      return `https://${raw}`;
    };
    const updates: Record<string, unknown> = {};
    const unset: string[] = [];
    const mapField = (value: string, setKeys: string[], unsetKeys: string[]) => {
      const raw = trimmed(value);
      if (raw) {
        for (const key of setKeys) updates[key] = raw;
      } else {
        unset.push(...unsetKeys);
      }
    };

    mapField(contactNotesDraft.businessEmail, ['email1', 'email', 'Email'], ['email1', 'email', 'Email', 'email2', 'email3']);
    mapField(contactNotesDraft.personalEmail, ['personalEmail1', 'PersonalEmail', 'personalEmail'], ['personalEmail1', 'PersonalEmail', 'personalEmail', 'personal_email']);
    mapField(contactNotesDraft.businessPhone, ['contactPhone1'], ['contactPhone1', 'directDial', 'DirectDial', 'Phone']);
    mapField(contactNotesDraft.mobilePhone, ['contactMobilePhone1'], ['contactMobilePhone1', 'mobile', 'MobilePhone', 'Mobile']);
    mapField(withProtocol(contactNotesDraft.linkedin), ['LIProfileUrl', 'linkedIn'], ['LIProfileUrl', 'linkedIn', 'linkedinUrl', 'linkedin_url']);
    mapField(contactNotesDraft.location, ['ContactLocation'], ['ContactLocation']);
    mapField(contactNotesDraft.departmentTags, ['DepartmentTags'], ['DepartmentTags', 'departmentTags', 'Department']);
    mapField(contactNotesDraft.seniorityTier, ['SeniorityTier'], ['SeniorityTier', 'seniorityTier', 'Seniority']);
    mapField(contactNotesDraft.companySize, ['CompanySizeBrackets'], ['CompanySizeBrackets', 'companySizeBrackets']);
    mapField(contactNotesDraft.industryCodes, ['IndustryCodes'], ['IndustryCodes', 'industryCodes', 'CompanyNaicsCode']);

    try {
      setContactNotesSaving(true);
      setContactNotesError(null);
      const res = await updateContactRecord({
        userEmail,
        contactId: contactId || undefined,
        searchResultId: searchResultId || undefined,
        updates,
        unset: Array.from(new Set(unset)),
      });
      if (!res?.success || !res?.contact) {
        setContactNotesError(res?.error || 'Failed to save contact');
        return;
      }
      const nextContact = res.contact as Contact;
      upsertContactInCollections(nextContact);
      setContactNotesDraft(buildContactNotesDraft(nextContact));
      setContactNotesEditing(false);
    } catch (error: any) {
      setContactNotesError(error?.message || 'Failed to save contact');
    } finally {
      setContactNotesSaving(false);
    }
  };

  const deriveCompanyName = (c: Contact): string => {
    const company = getContactCompany(c);
    const contactName = getContactName(c).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const companyNorm = company.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    if (company && companyNorm && companyNorm !== contactName) return company;
    const domain = getContactDomain(c);
    const derived = companyNameFromDomain(domain);
    return derived || company || 'Unknown';
  };

  const hasDatasetField = (contact: Contact, keys: string[]) =>
    keys.some((key) => {
      const value = (contact as any)?.[key];
      return (Array.isArray(value) && value.length > 0) || (typeof value === 'string' && value.trim().length > 0);
    });

  const hasCompanyCrunchbaseData = (contact: Contact, companyName: string) =>
    Boolean(crunchbaseReports[companyName]) ||
    hasDatasetField(contact, ['crunchbase_ds', 'crunchbase_scraped_at']);

  const hasCompanyWebsiteData = (contact: Contact, companyName: string) =>
    Boolean(websiteReports[companyName]) ||
    hasDatasetField(contact, ['website_ds', 'website_links_ds', 'website_scraped_at']);

  const hasCompanyLinkedinData = (contact: Contact, companyName: string) =>
    Boolean(companyLinkedinReports[companyName]) ||
    hasDatasetField(contact, [
      'company_linkedin_profile_ds',
      'company_linkedin_posts_ds',
      'company_linkedin_jobs_ds',
      'linkedin_company_ds',
      'company_linkedin_scraped_at',
    ]);

  const contactsForUi = contacts;

  // Load data
  const loadContacts = useCallback(async () => {
    if (!userEmail) return;
    const requestSeq = ++contactsRequestSeqRef.current;
    setLoading(true);
    setLoadingMore(true);
    setContactsLoadComplete(false);
    setContactsLoadError(null);
    lastContactsCommitCountRef.current = 0;
    lastContactsCommitAtRef.current = 0;
    try {
      const { contacts: page, totalCount } = await loadContactsServerSide(
        userEmail,
        currentPage,
        perPage,
        sortKeyContacts.startsWith('name') ? 'full_name' : sortKeyContacts.startsWith('company') ? 'company' : sortKeyContacts.startsWith('title') ? 'title' : 'enriched_at',
        sortKeyContacts.endsWith('_asc') ? 'asc' : 'desc',
        searchTerm,
        researchedDateFrom,
        researchedDateTo,
        {
          filters: {
            hasBusinessEmail: hasBusinessEmailFilter,
            hasPersonalEmail: hasPersonalEmailFilter,
            hasLinkedinUrl: hasLinkedinFilter,
            hasBusinessPhone: hasBusinessPhoneFilter,
            hasPersonalPhone: hasPersonalPhoneFilter,
            titleTerms: titleFilterInput
              .split(',')
              .map((part) => part.trim().toLowerCase())
              .filter(Boolean),
            titleExactMatch,
            location: locationFilterInput.trim(),
            locationScope: locationScopeFilter,
          },
        },
      );
      if (requestSeq !== contactsRequestSeqRef.current) return;
      setContacts(page as unknown as Contact[]);
      setTotalContacts(totalCount);
      setContactsLoadComplete(true);
    } catch (e: any) {
      if (requestSeq !== contactsRequestSeqRef.current) return;
      console.error('Failed to load contacts:', e);
      setContactsLoadError(e?.message || 'Search failed. Please try again.');
      setContacts([]);
      setTotalContacts(0);
      setContactsLoadComplete(true);
    } finally {
      if (requestSeq === contactsRequestSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [
    userEmail,
    currentPage,
    perPage,
    sortKeyContacts,
    searchTerm,
    researchedDateFrom,
    researchedDateTo,
    hasBusinessEmailFilter,
    hasPersonalEmailFilter,
    hasLinkedinFilter,
    hasBusinessPhoneFilter,
    hasPersonalPhoneFilter,
    titleFilterInput,
    titleExactMatch,
    locationFilterInput,
    locationScopeFilter,
  ]);

  const commitSearch = useCallback(() => {
    const nextSearchTerm = searchInput.trim();
    setContactsLoadError(null);
    setSelectedContacts(new Set());
    setSelectionScope('page');
    if (currentPage !== 0) {
      setCurrentPage(0);
    }
    if (nextSearchTerm === searchTerm && currentPage === 0) {
      void loadContacts();
      return;
    }
    setSearchTerm(nextSearchTerm);
  }, [searchInput, searchTerm, currentPage, loadContacts]);

  useEffect(() => {
    const nextSearchTerm = searchInput.trim();
    if (nextSearchTerm === searchTerm) return;
    const timer = window.setTimeout(() => {
      setContactsLoadError(null);
      setSelectedContacts(new Set());
      setSelectionScope('page');
      if (currentPage !== 0) setCurrentPage(0);
      setSearchTerm(nextSearchTerm);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput, searchTerm, currentPage]);

  const openContactProfile = async (contact: Contact) => {
    if (!userEmail) {
      setContactProfileDrawer({ contact });
      return;
    }
    const key = String(contact.searchResultId || (contact as any).contactId || '').trim();
    if (key) setLoadingContactItemId(key);
    try {
      const full = await loadContactItem({
        userEmail,
        contactId: String((contact as any).contactId || '').trim() || undefined,
        searchResultId: String(contact.searchResultId || '').trim() || undefined,
      });
      setContactProfileDrawer({ contact: full as Contact });
    } catch (error) {
      console.warn('Failed to load full contact; opening with list payload', error);
      setContactProfileDrawer({ contact });
    } finally {
      if (key) setLoadingContactItemId((current) => (current === key ? null : current));
    }
  };

  const loadUserLists = useCallback(async () => {
    if (!userEmail) return;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/get-lists?userEmail=${encodeURIComponent(userEmail)}&entityType=${LIST_ENTITY_TYPE}`);
      const data = await res.json();
      if (data.success) {
        setUserLists(data.lists || []);
      }
    } catch (e) {
      console.error('Failed to load lists:', e);
    }
  }, [userEmail]);

  useEffect(() => {
    if (isAuthenticated && userEmail) {
      loadContacts();
      loadUserLists();
      getCredits(userEmail).then(setCredits);
      loadHotListDb(userEmail).then(setHotListItems).catch(() => {});
      
      const savedDefault = localStorage.getItem(DEFAULT_LIST_STORAGE_KEY) || localStorage.getItem('profitibull_default_list');
      if (savedDefault) setDefaultList(savedDefault);
      
    }
  }, [isAuthenticated, userEmail, loadUserLists, setCredits, loadContacts]);



  const handleRemoveHotListItem = async (id: string) => {
    if (!userEmail) return;
    const next = await removeHotListEntityDb(userEmail, id);
    setHotListItems(next);
  };

  useEffect(() => {
    let active = true;
    if (!userEmail) return;
    getPepProfile(userEmail)
      .then((res) => {
        if (!active) return;
        if (res?.success && res.profile) {
          setPepProfile(res.profile);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [userEmail]);

  useEffect(() => {
    let active = true;
    if (!userEmail || !canChooseGhlTarget) {
      setGhlTargetOptions([]);
      setGhlSendAsLocationId('');
      return () => {
        active = false;
      };
    }
    if (!adminToken) {
      setGhlTargetOptions([]);
      return () => {
        active = false;
      };
    }
    (async () => {
      try {
        const settings = await getAccountSettings({
          token: adminToken,
          email: userEmail,
        });
        if (!active) return;
        const options = (settings.ghlSendAsOptions ?? [])
          .filter((opt) => opt.active !== false)
          .map((opt) => {
            const locationId = String(opt.locationId || '').trim();
            const name = String(opt.name || '').trim();
            return {
              locationId,
              label: `${name || locationId} (${locationId})`,
            };
          })
          .filter((opt) => Boolean(opt.locationId));
        setGhlTargetOptions(options);
        const savedLocationId = String(settings.account?.ghlSendAsLocationId || '').trim();
        setGhlSendAsLocationId(
          savedLocationId && options.some((option) => option.locationId === savedLocationId)
            ? savedLocationId
            : ''
        );
      } catch {
        if (!active) return;
        setGhlTargetOptions([]);
        setGhlSendAsLocationId('');
      }
    })();
    return () => {
      active = false;
    };
  }, [adminToken, canChooseGhlTarget, userEmail]);

  async function autoLoadContactLinkedinReport(contact: Contact) {
    if (!userEmail) return;
    const ids = getContactSocialDatasetIds(contact, 'linkedin');
    if (!ids.profile && !ids.posts) return;
    const key = `${contact.searchResultId}-linkedin`;
    if (contactSocialReportLoading[key]) return;
    if (
      contactSocialReport &&
      contactSocialReport.platform === 'linkedin' &&
      contactSocialReport.contact.searchResultId === contact.searchResultId
    ) {
      return;
    }
    setContactSocialReportLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const result: Record<string, unknown> = {};
      if (ids.profile) {
        const res = await fetch(`${RESEARCH_API_URL}/contact-socials/dataset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataset_id: ids.profile }),
        });
        const data = await res.json();
        if (data.success) result.profile = data.items || [];
      }
      if (ids.posts) {
        const res = await fetch(`${RESEARCH_API_URL}/contact-socials/dataset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataset_id: ids.posts }),
        });
        const data = await res.json();
        if (data.success) result.posts = data.items || [];
      }
      if (Object.keys(result).length) {
        setContactSocialReport({ contact, platform: 'linkedin', items: [result] });
      }
    } catch (e) {
      console.error('Auto-load LinkedIn report failed:', e);
    } finally {
      setContactSocialReportLoading((prev) => ({ ...prev, [key]: false }));
    }
  }

  useEffect(() => {
    if (!contactProfileDrawer) return;
    const drawerContactKey = getPepLeadId(contactProfileDrawer.contact);
    if (openDrawerContactKeyRef.current !== drawerContactKey) {
      openDrawerContactKeyRef.current = drawerContactKey;
      setContactModalTab('ov');
    }
    setContactNotesEditing(false);
    setContactNotesError(null);
    setContactNotesDraft(buildContactNotesDraft(contactProfileDrawer.contact));
    const stored = window.localStorage.getItem('solo-theme');
    setContactModalTheme(stored === 'light' ? 'light' : 'dark');
    autoLoadContactLinkedinReport(contactProfileDrawer.contact);
    loadPepLatest(contactProfileDrawer.contact);
    loadPepRunLatest(contactProfileDrawer.contact);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setContactProfileDrawer(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [contactProfileDrawer]);

  useEffect(() => {
    if (!contactProfileDrawer) {
      openDrawerContactKeyRef.current = '';
    }
  }, [contactProfileDrawer]);

  // Get parsed lists for a contact
  const getContactLists = (contact: Contact): string[] => {
    const parseStringArray = (value: unknown): string[] => {
      if (Array.isArray(value)) return value.map((item) => String(item ?? '').trim()).filter(Boolean);
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return [];
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.map((item) => String(item ?? '').trim()).filter(Boolean);
          }
        } catch {
          // keep single-string fallback
        }
        return [trimmed];
      }
      return [];
    };

    const configuredLists = new Set((userLists || []).map((name) => String(name || '').trim()).filter(Boolean));
    const explicitLists = parseStringArray(contact.lists);
    const rawTags = parseStringArray((contact as any).tags);
    const combined = Array.from(new Set([...explicitLists, ...rawTags]));
    const normalized = new Set<string>();

    for (const entryRaw of combined) {
      const entry = String(entryRaw || '').trim();
      if (!entry) continue;

      if (configuredLists.has(entry)) {
        normalized.add(entry);
        continue;
      }

      if (entry.startsWith('Contact_')) {
        normalized.add(entry);
        continue;
      }

      const typedCandidate = `Contact_${entry}`;
      if (configuredLists.has(typedCandidate)) {
        normalized.add(typedCandidate);
      }
    }

    return Array.from(normalized);
  };

  const normalizePepStatus = (contact: Contact): string => {
    const entries = getPepProgramEntries(contact);
    if (!entries.length) return 'IDLE';
    return [...entries]
      .sort((a, b) => pepStatusOrder(a.status) - pepStatusOrder(b.status))[0]
      ?.status || 'IDLE';
  };

  const readStringAttr = (value: any): string => {
    if (typeof value === 'string') return value;
    if (!value || typeof value !== 'object') return '';
    if (typeof value.S === 'string') return value.S;
    return '';
  };

  const readMapAttr = (value: any): Record<string, any> => {
    if (!value || typeof value !== 'object') return {};
    if (value.M && typeof value.M === 'object') return value.M;
    return value;
  };

  const readProgramSummaryEntries = (contact: Contact): PepProgramSummaryEntry[] => {
    const rawSummary = (contact as any).pep_program_summary;
    const summaryMap = readMapAttr(rawSummary);
    const entries = Object.entries(summaryMap).map(([programType, rawEntry]) => {
      const entry = readMapAttr(rawEntry);
      return {
        programType: String(programType || '').trim().toUpperCase(),
        status: readStringAttr(entry.status).trim().toUpperCase(),
        runId: readStringAttr(entry.runId).trim(),
        channelFamily: readStringAttr(entry.channelFamily).trim().toUpperCase(),
        entrySource: readStringAttr(entry.entrySource).trim().toUpperCase(),
        nextDueAt: readStringAttr(entry.nextDueAt).trim(),
        startedAt: readStringAttr(entry.startedAt).trim(),
        lastStep: readStringAttr(entry.lastStep).trim(),
        lastStepStatus: readStringAttr(entry.lastStepStatus).trim().toUpperCase(),
        branchReason: readStringAttr(entry.branchReason).trim(),
      };
    });
    return entries.filter((entry) => entry.programType && (entry.runId || entry.status));
  };

  const pepStatusOrder = (value: string) => {
    switch (String(value || '').trim().toUpperCase()) {
      case 'RUNNING': return 0;
      case 'QUEUED': return 1;
      case 'PAUSED': return 2;
      case 'COMPLETE': return 3;
      case 'FAILED': return 4;
      case 'REMOVED_REPLY': return 5;
      case 'IDLE': return 6;
      default: return 7;
    }
  };

  const getPepProgramEntries = (contact: Contact): PepProgramSummaryEntry[] => {
    const persistedEntries = readProgramSummaryEntries(contact);
    const entriesByProgram = new Map<string, PepProgramSummaryEntry>(
      persistedEntries.map((entry) => [entry.programType, entry]),
    );
    const pepKey = getPepLeadId(contact);
    const liveRun = pepRunData[pepKey]?.run || null;
    if (liveRun) {
      const liveProgramType = String(
        liveRun.programType ||
        (contact as any).pep_sequence_program ||
        'LINKEDIN_FULL',
      ).trim().toUpperCase();
      const liveStatus = String(liveRun.status || '').trim().toUpperCase();
      const existing = entriesByProgram.get(liveProgramType);
      entriesByProgram.set(liveProgramType, {
        programType: liveProgramType,
        status: liveStatus || existing?.status || 'QUEUED',
        runId: String(liveRun.runId || existing?.runId || '').trim(),
        channelFamily: String(
          liveRun.channelFamily ||
          existing?.channelFamily ||
          (contact as any).pep_sequence_channel_family ||
          '',
        ).trim().toUpperCase(),
        entrySource: String(
          liveRun.entrySource ||
          existing?.entrySource ||
          (contact as any).pep_sequence_entry_source ||
          '',
        ).trim().toUpperCase(),
        nextDueAt: String(existing?.nextDueAt || (contact as any).pep_sequence_next_due_at || '').trim(),
        startedAt: String(existing?.startedAt || (contact as any).pep_sequence_started_at || '').trim(),
        lastStep: String(existing?.lastStep || (contact as any).pep_sequence_last_step || '').trim(),
        lastStepStatus: String(existing?.lastStepStatus || (contact as any).pep_sequence_last_step_status || '').trim().toUpperCase(),
        branchReason: String(existing?.branchReason || '').trim(),
      });
    }
    if (entriesByProgram.size === 0) {
      const legacyStatus = String((contact as any).pep_sequence_status || '').trim().toUpperCase();
      const legacyRunId = String((contact as any).pep_sequence_run_id || '').trim();
      const legacyProgram = String((contact as any).pep_sequence_program || '').trim().toUpperCase();
      if (legacyStatus || legacyRunId || legacyProgram) {
        entriesByProgram.set(legacyProgram || 'LINKEDIN_FULL', {
          programType: legacyProgram || 'LINKEDIN_FULL',
          status: legacyRunId && (!legacyStatus || legacyStatus === 'IDLE') ? 'QUEUED' : (legacyStatus || 'QUEUED'),
          runId: legacyRunId,
          channelFamily: String((contact as any).pep_sequence_channel_family || '').trim().toUpperCase(),
          entrySource: String((contact as any).pep_sequence_entry_source || '').trim().toUpperCase(),
          nextDueAt: String((contact as any).pep_sequence_next_due_at || '').trim(),
          startedAt: String((contact as any).pep_sequence_started_at || '').trim(),
          lastStep: String((contact as any).pep_sequence_last_step || '').trim(),
          lastStepStatus: String((contact as any).pep_sequence_last_step_status || '').trim().toUpperCase(),
          branchReason: '',
        });
      }
    }
    return [...entriesByProgram.values()].sort((a, b) => {
      const statusCompare = pepStatusOrder(a.status) - pepStatusOrder(b.status);
      if (statusCompare !== 0) return statusCompare;
      return a.programType.localeCompare(b.programType);
    });
  };

  const formatPepProgramLabel = (programType: string): string => {
    switch (programType) {
      case 'LINKEDIN_FULL': return 'LI';
      case 'FACEBOOK_COMMENTER': return 'FB';
      case 'INSTAGRAM_SOCIAL': return 'IG';
      default: return programType.replace(/_/g, ' ') || 'PEP';
    }
  };

  const pepBadgeMeta = (status: string) => {
    switch (String(status || '').trim().toUpperCase()) {
      case 'RUNNING':
        return { status: 'Active', className: 'bg-emerald-900/70 text-emerald-300 border-emerald-700' };
      case 'QUEUED':
        return { status: 'Queued', className: 'bg-amber-900/70 text-amber-300 border-amber-700' };
      case 'COMPLETE':
        return { status: 'Complete', className: 'bg-sky-900/70 text-sky-300 border-sky-700' };
      case 'FAILED':
        return { status: 'Failed', className: 'bg-red-900/70 text-red-300 border-red-700' };
      case 'PAUSED':
        return { status: 'Paused', className: 'bg-violet-900/70 text-violet-300 border-violet-700' };
      case 'REMOVED_REPLY':
        return { status: 'Replied', className: 'bg-teal-900/70 text-teal-300 border-teal-700' };
      default:
        return { status: String(status || 'Idle').replace(/_/g, ' '), className: 'bg-gray-800 text-gray-200 border-gray-700' };
    }
  };

  const titleTerms = useMemo(
    () =>
      titleFilterInput
        .split(',')
        .map((part) => part.trim().toLowerCase())
        .filter(Boolean),
    [titleFilterInput]
  );

  const hasAdvancedContactsFilters = useMemo(
    () =>
      hasBusinessEmailFilter ||
      hasPersonalEmailFilter ||
      hasLinkedinFilter ||
      hasBusinessPhoneFilter ||
      hasPersonalPhoneFilter ||
      titleTerms.length > 0 ||
      locationFilterInput.trim().length > 0,
    [
      hasBusinessEmailFilter,
      hasPersonalEmailFilter,
      hasLinkedinFilter,
      hasBusinessPhoneFilter,
      hasPersonalPhoneFilter,
      titleTerms,
      locationFilterInput,
    ]
  );

  const serverSideFilterPayload = useMemo(
    () => ({
      hasBusinessEmail: hasBusinessEmailFilter,
      hasPersonalEmail: hasPersonalEmailFilter,
      hasLinkedinUrl: hasLinkedinFilter,
      hasBusinessPhone: hasBusinessPhoneFilter,
      hasPersonalPhone: hasPersonalPhoneFilter,
      titleTerms,
      titleExactMatch,
      location: locationFilterInput.trim(),
      locationScope: locationScopeFilter,
    }),
    [
      hasBusinessEmailFilter,
      hasPersonalEmailFilter,
      hasLinkedinFilter,
      hasBusinessPhoneFilter,
      hasPersonalPhoneFilter,
      titleTerms,
      titleExactMatch,
      locationFilterInput,
      locationScopeFilter,
    ]
  );

  const applyContactsClientFilters = useCallback(
    (items: Contact[]): Contact[] => {
      const locationNeedle = locationFilterInput.trim().toLowerCase();
      return items.filter((contact) => {
        if (currentListFilter && !getContactLists(contact).includes(currentListFilter)) return false;
        if (pepStatusFilter && normalizePepStatus(contact) !== pepStatusFilter) return false;

        if (hasBusinessEmailFilter && !getBusinessEmail(contact)) return false;
        if (hasPersonalEmailFilter && !getPersonalEmail(contact)) return false;
        if (hasLinkedinFilter && !normalizeLinkedinProfileUrl(getContactSocialUrl(contact, 'linkedin'))) return false;
        if (hasBusinessPhoneFilter && !getBusinessPhone(contact)) return false;
        if (hasPersonalPhoneFilter && !getPersonalPhone(contact)) return false;

        if (titleTerms.length > 0) {
          const title = getContactTitle(contact).toLowerCase();
          if (!title) return false;
          const matched = titleExactMatch
            ? titleTerms.some((term) => title === term)
            : titleTerms.some((term) => title.includes(term));
          if (!matched) return false;
        }

        if (locationNeedle) {
          const contactLocation = getContactLocationText(contact).toLowerCase();
          const companyLocation = getCompanyLocationText(contact).toLowerCase();
          const contactMatch = contactLocation.includes(locationNeedle);
          const companyMatch = companyLocation.includes(locationNeedle);

          if (locationScopeFilter === 'contact' && !contactMatch) return false;
          if (locationScopeFilter === 'company' && !companyMatch) return false;
          if (locationScopeFilter === 'both_and' && !(contactMatch && companyMatch)) return false;
          if (locationScopeFilter === 'both_or' && !(contactMatch || companyMatch)) return false;
        }

        return true;
      });
    },
    [
      currentListFilter,
      pepStatusFilter,
      hasBusinessEmailFilter,
      hasPersonalEmailFilter,
      hasLinkedinFilter,
      hasBusinessPhoneFilter,
      hasPersonalPhoneFilter,
      titleTerms,
      titleExactMatch,
      locationFilterInput,
      locationScopeFilter,
    ]
  );

  // Filter contacts
  const filteredContacts = useMemo(() => applyContactsClientFilters(contacts as Contact[]), [contacts, applyContactsClientFilters]);

  const clearAllVisibleFilters = useCallback(() => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentListFilter('');
    setResearchedDateFrom('');
    setResearchedDateTo('');
    setPepStatusFilter('');
    setHasBusinessEmailFilter(false);
    setHasPersonalEmailFilter(false);
    setHasLinkedinFilter(false);
    setHasBusinessPhoneFilter(false);
    setHasPersonalPhoneFilter(false);
    setTitleFilterInput('');
    setTitleExactMatch(false);
    setLocationFilterInput('');
    setLocationScopeFilter('both_or');
    setSelectedContacts(new Set());
    setSelectionScope('page');
    setCurrentPage(0);
  }, []);

  const activeFilterPills = useMemo(() => {
    const pills: Array<{ key: string; label: string; onRemove: () => void }> = [];
    const resetSelection = () => {
      setSelectedContacts(new Set());
      setSelectionScope('page');
      setCurrentPage(0);
    };
    if (searchTerm) {
      pills.push({
        key: 'search',
        label: `Search: ${searchTerm}`,
        onRemove: () => {
          setSearchInput('');
          setSearchTerm('');
          resetSelection();
        },
      });
    }
    if (currentListFilter) pills.push({ key: 'list', label: `List: ${currentListFilter}`, onRemove: () => { setCurrentListFilter(''); resetSelection(); } });
    if (researchedDateFrom) pills.push({ key: 'date_from', label: `Date from: ${researchedDateFrom}`, onRemove: () => { setResearchedDateFrom(''); resetSelection(); } });
    if (researchedDateTo) pills.push({ key: 'date_to', label: `Date to: ${researchedDateTo}`, onRemove: () => { setResearchedDateTo(''); resetSelection(); } });
    if (pepStatusFilter) pills.push({ key: 'pep', label: `PEPai: ${pepStatusFilter}`, onRemove: () => { setPepStatusFilter(''); resetSelection(); } });
    if (hasBusinessEmailFilter) pills.push({ key: 'has_business_email', label: 'Has business email', onRemove: () => { setHasBusinessEmailFilter(false); resetSelection(); } });
    if (hasPersonalEmailFilter) pills.push({ key: 'has_personal_email', label: 'Has personal email', onRemove: () => { setHasPersonalEmailFilter(false); resetSelection(); } });
    if (hasLinkedinFilter) pills.push({ key: 'has_linkedin', label: 'Has LinkedIn', onRemove: () => { setHasLinkedinFilter(false); resetSelection(); } });
    if (hasBusinessPhoneFilter) pills.push({ key: 'has_business_phone', label: 'Has business phone', onRemove: () => { setHasBusinessPhoneFilter(false); resetSelection(); } });
    if (hasPersonalPhoneFilter) pills.push({ key: 'has_personal_phone', label: 'Has personal phone', onRemove: () => { setHasPersonalPhoneFilter(false); resetSelection(); } });
    if (titleFilterInput.trim()) {
      pills.push({
        key: 'title',
        label: `Title${titleExactMatch ? ' (exact)' : ''}: ${titleFilterInput.trim()}`,
        onRemove: () => {
          setTitleFilterInput('');
          setTitleExactMatch(false);
          resetSelection();
        },
      });
    }
    if (locationFilterInput.trim()) {
      const scopeLabel =
        locationScopeFilter === 'both_and'
          ? 'Contact+Company'
          : locationScopeFilter === 'contact'
            ? 'Contact'
            : locationScopeFilter === 'company'
              ? 'Company'
              : 'Contact/Company';
      pills.push({
        key: 'location',
        label: `Location (${scopeLabel}): ${locationFilterInput.trim()}`,
        onRemove: () => {
          setLocationFilterInput('');
          setLocationScopeFilter('both_or');
          resetSelection();
        },
      });
    }
    return pills;
  }, [
    searchTerm,
    currentListFilter,
    researchedDateFrom,
    researchedDateTo,
    pepStatusFilter,
    hasBusinessEmailFilter,
    hasPersonalEmailFilter,
    hasLinkedinFilter,
    hasBusinessPhoneFilter,
    hasPersonalPhoneFilter,
    titleFilterInput,
    titleExactMatch,
    locationFilterInput,
    locationScopeFilter,
  ]);



  const handleRerunSeamless = async (contact: Contact) => {
    if (!userEmail) return;
    const searchResultId = contact.searchResultId;
    if (!searchResultId) return;
    if (!confirm(`Re-run research for ${contact.fullName || contact.Name || 'this contact'}? This will use credits.`)) {
      return;
    }
    setRerunLoading((prev) => ({ ...prev, [searchResultId]: true }));
    try {
      const lead = {
        searchResultId,
        contactSearchId: contact.contactSearchId ?? 0,
        uiMatchId: contact.uiMatchId,
        name: contact.fullName || contact.Name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
        title: contact.title || contact.Title || '',
        companyName: contact.company || contact.Company || '',
        domain: contact.CompanyDomain || contact.Website || '',
        liUrl: contact.linkedIn || contact.LIProfileUrl || '',
        email: contact.email1 || contact.email || '',
        personalEmail1: contact.personalEmail1 || contact.PersonalEmail || '',
      };
      const selectedCampaign =
        selectedInstantlyCampaignId
          ? instantlyCampaigns.find((campaign) => campaign.id === selectedInstantlyCampaignId) ?? null
          : null;
      const pepaiEnabledForRerun = canUsePepaiResearch && Boolean(selectedCampaign);
      await researchContact(lead as any, userEmail, {
        preserveSearchResultId: true,
        pepaiResearch: pepaiEnabledForRerun,
        extraTags: pepaiEnabledForRerun ? ['PEPAI-research'] : [],
        instantlyCampaignId: selectedCampaign?.id,
        instantlyCampaignName: selectedCampaign?.name,
        targetGhlLocationId,
        skipCrmSync: !sendToCrm,
      });
      await loadContacts();
      getCredits(userEmail).then(setCredits);
    } catch (e) {
      console.error('Rerun research failed:', e);
      alert('Rerun research failed');
    } finally {
      setRerunLoading((prev) => ({ ...prev, [searchResultId]: false }));
    }
  };

  const handleRerunSelected = async () => {
    if (!userEmail) return;
    if (selectedContacts.size === 0) return;
    if (!confirm(`Re-run research for ${selectedContacts.size} contacts? This will use credits.`)) {
      return;
    }
    const ids = Array.from(selectedContacts);
    for (const searchResultId of ids) {
      const contact = contacts.find((c) => getSelectionId(c) === searchResultId);
      if (!contact) continue;
      setRerunLoading((prev) => ({ ...prev, [searchResultId]: true }));
      try {
        const lead = {
          searchResultId,
          contactSearchId: contact.contactSearchId ?? 0,
          uiMatchId: contact.uiMatchId,
          name: contact.fullName || contact.Name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
          title: contact.title || contact.Title || '',
          companyName: contact.company || contact.Company || '',
          domain: contact.CompanyDomain || contact.Website || '',
          liUrl: contact.linkedIn || contact.LIProfileUrl || '',
          email: contact.email1 || contact.email || '',
          personalEmail1: contact.personalEmail1 || contact.PersonalEmail || '',
        };
        const selectedCampaign =
          selectedInstantlyCampaignId
            ? instantlyCampaigns.find((campaign) => campaign.id === selectedInstantlyCampaignId) ?? null
            : null;
        const pepaiEnabledForRerun = canUsePepaiResearch && Boolean(selectedCampaign);
        await researchContact(lead as any, userEmail, {
          preserveSearchResultId: true,
          pepaiResearch: pepaiEnabledForRerun,
          extraTags: pepaiEnabledForRerun ? ['PEPAI-research'] : [],
          instantlyCampaignId: selectedCampaign?.id,
          instantlyCampaignName: selectedCampaign?.name,
          targetGhlLocationId,
          skipCrmSync: !sendToCrm,
        });
      } catch (e) {
        console.error('Rerun research failed:', e);
      } finally {
        setRerunLoading((prev) => ({ ...prev, [searchResultId]: false }));
      }
    }
    await loadContacts();
    getCredits(userEmail).then(setCredits);
  };

  const resolveSelectedRowsForBulk = async (): Promise<Contact[]> => {
    const selectedIds = new Set(
      Array.from(selectedContacts)
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    );
    if (selectedIds.size === 0) return [];

    const seen = new Set<string>();
    const rows: Contact[] = [];
    const appendMatches = (items: Contact[]) => {
      items.forEach((contact) => {
        const id = getSelectionId(contact);
        if (!id || !selectedIds.has(id) || seen.has(id)) return;
        seen.add(id);
        rows.push(contact);
      });
    };

    appendMatches(contacts);
    if (rows.length >= selectedIds.size || !userEmail) return rows;

    const sortCol =
      sortKeyContacts.startsWith('name')
        ? 'full_name'
        : sortKeyContacts.startsWith('company')
          ? 'company'
          : sortKeyContacts.startsWith('title')
            ? 'title'
            : 'enriched_at';
    const sortDir = sortKeyContacts.endsWith('_asc') ? 'asc' : 'desc';
    const pageSize = 500;
    let page = 0;

    while (true) {
      const { contacts: pageContacts } = await loadContactsServerSide(
        userEmail,
        page,
        pageSize,
        sortCol,
        sortDir,
        searchTerm,
        researchedDateFrom,
        researchedDateTo,
        { includeCount: false, filters: serverSideFilterPayload },
      );
      if (!pageContacts.length) break;

      const scoped = applyContactsClientFilters(pageContacts as Contact[]);
      appendMatches(scoped);
      if (rows.length >= selectedIds.size) break;
      if (pageContacts.length < pageSize) break;
      page += 1;
    }

    return rows;
  };

  const getListIdentifiersForContact = (contact: Partial<Contact>): { searchResultId: string; contactId: string } => {
    const rawSearchResultId = String((contact as any).searchResultIdRaw || '').trim();
    const fallbackSearchResultId = String(contact.searchResultId || '').trim();
    const contactId = String((contact as any).contactId || (contact as any).id || '').trim();
    const searchResultId =
      rawSearchResultId || (fallbackSearchResultId && fallbackSearchResultId !== contactId ? fallbackSearchResultId : '');
    return { searchResultId, contactId };
  };

  const resolveSelectedListIdentifiers = async (): Promise<{ searchResultIds: string[]; contactIds: string[] }> => {
    const selectedRows = await resolveSelectedRowsForBulk();
    const searchResultIds = Array.from(
      new Set(
        selectedRows
          .map((contact) => getListIdentifiersForContact(contact).searchResultId)
          .filter(Boolean)
      )
    );
    const contactIds = Array.from(
      new Set(
        selectedRows
          .map((contact) => getListIdentifiersForContact(contact).contactId)
          .filter(Boolean)
      )
    );
    return { searchResultIds, contactIds };
  };

  const handleSendSelectedToCrm = async () => {
    if (!userEmail || selectedContacts.size === 0) return;
    setCrmSyncLoading(true);
    try {
      const selectedRows = await resolveSelectedRowsForBulk();
      const searchResultIds = Array.from(
        new Set(
          selectedRows
            .map((contact) => String((contact as any).searchResultIdRaw || contact.searchResultId || '').trim())
            .filter(Boolean)
        )
      );
      const contactIds = Array.from(
        new Set(
          selectedRows
            .map((contact) => String(contact.contactId || contact.id || '').trim())
            .filter(Boolean)
        )
      );

      if (searchResultIds.length === 0 && contactIds.length === 0) {
        alert('Selected contacts are missing IDs for CRM sync.');
        return;
      }

      const result = await syncExistingContactsToCrm({
        userEmail,
        searchResultIds,
        contactIds,
        contacts: selectedRows.map((contact) => {
          const normalizedSearchResultId = String((contact as any).searchResultIdRaw || contact.searchResultId || '').trim();
          const normalizedContactId = String(contact.contactId || contact.id || '').trim();
          return {
            ...contact,
            ...(normalizedSearchResultId ? { searchResultId: normalizedSearchResultId } : {}),
            ...(normalizedContactId ? { contactId: normalizedContactId } : {}),
          };
        }),
        targetGhlLocationId,
      });
      if (!result.success) throw new Error(result.error || 'CRM sync failed');
      alert(
        `CRM Sync Complete: ${result.sent ?? 0} sent` +
          `${result.failed ? `, ${result.failed} failed` : ''}` +
          `${result.skipped ? `, ${result.skipped} skipped` : ''}.`
      );
    } catch (error) {
      console.error('Send selected to CRM failed', error);
      alert('Failed to send selected contacts to CRM.');
    } finally {
      setCrmSyncLoading(false);
    }
  };

  const handleSendSelectedToInstantly = async () => {
    if (!userEmail || selectedContacts.size === 0) return;
    if (!selectedInstantlyCampaignId) {
      alert('Select an Instantly campaign first.');
      return;
    }
    setInstantlySyncLoading(true);
    try {
      const selectedRows = await resolveSelectedRowsForBulk();
      const searchResultIds = Array.from(
        new Set(
          selectedRows
            .map((contact) => String((contact as any).searchResultIdRaw || contact.searchResultId || '').trim())
            .filter(Boolean)
        )
      );
      const contactIds = Array.from(
        new Set(
          selectedRows
            .map((contact) => String(contact.contactId || contact.id || '').trim())
            .filter(Boolean)
        )
      );

      if (searchResultIds.length === 0 && contactIds.length === 0) {
        alert('Selected contacts are missing IDs for Instantly sync.');
        return;
      }

      const selectedCampaign =
        instantlyCampaigns.find((campaign) => campaign.id === selectedInstantlyCampaignId) ?? null;

      const result = await enrollExistingContactsToInstantly({
        userEmail,
        instantlyCampaignId: selectedInstantlyCampaignId,
        instantlyCampaignName: selectedCampaign?.name,
        searchResultIds,
        contactIds,
        contacts: selectedRows.map((contact) => {
          const normalizedSearchResultId = String((contact as any).searchResultIdRaw || contact.searchResultId || '').trim();
          const normalizedContactId = String(contact.contactId || contact.id || '').trim();
          return {
            ...contact,
            ...(normalizedSearchResultId ? { searchResultId: normalizedSearchResultId } : {}),
            ...(normalizedContactId ? { contactId: normalizedContactId } : {}),
          };
        }),
      });
      if (!result.success) throw new Error(result.error || 'Instantly sync failed');
      alert(
        `Instantly Sync Complete: ${result.sent ?? 0} sent` +
          `${result.failed ? `, ${result.failed} failed` : ''}` +
          `${result.skipped ? `, ${result.skipped} skipped` : ''}.`
      );
    } catch (error) {
      console.error('Send selected to Instantly failed', error);
      alert('Failed to send selected contacts to Instantly.');
    } finally {
      setInstantlySyncLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalContacts / perPage));

  const paginatedContacts = useMemo(
    () => filteredContacts,
    [filteredContacts]
  );
  const visiblePageIds = useMemo(
    () => paginatedContacts.map((contact) => getSelectionId(contact)).filter(Boolean),
    [paginatedContacts]
  );
  const allVisibleSelected = visiblePageIds.length > 0 && visiblePageIds.every((id) => selectedContacts.has(id));
  const selectedCount = selectedContacts.size;
  const allMatchingSelected = selectionScope === 'matching' && selectedCount > 0;
  const hasClientSideOnlyFilters = Boolean(currentListFilter || pepStatusFilter);
  const canOfferSelectAllMatching =
    allVisibleSelected &&
    selectionScope !== 'matching';

  useEffect(() => {
    if (!contactsLoadComplete || !userEmail || paginatedContacts.length === 0) return;
    let cancelled = false;
    const hydrateVisiblePepRuns = async () => {
      await Promise.all(
        paginatedContacts.map(async (contact) => {
          const key = getPepLeadId(contact);
          const runId = getPersistedPepRunId(contact);
          const hydrateKey = `${key}::${runId || 'lead'}`;
          if (hydratedPepRunKeysRef.current.has(hydrateKey)) return;
          hydratedPepRunKeysRef.current.add(hydrateKey);
          try {
            const res = await getPepRunStatus({
              tenantId: userEmail,
              ...(runId ? { runId } : { leadId: key }),
            });
            if (cancelled) return;
            setPepRunData((prev) => ({
              ...prev,
              [key]: {
                run: res?.run || null,
                steps: Array.isArray(res?.steps) ? res.steps : [],
              },
            }));
          } catch {
            hydratedPepRunKeysRef.current.delete(hydrateKey);
            // ignore per-contact run status failures
          }
        })
      );
    };
    hydrateVisiblePepRuns().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [contactsLoadComplete, userEmail, paginatedContacts]);

  // Selection handlers
  const toggleSelectAll = () => {
    const pageIds = visiblePageIds.filter(Boolean);
    if (pageIds.length === 0) return;
    setSelectedContacts((prev) => {
      if (selectionScope === 'matching') {
        // In "all matching" mode, header checkbox should clear full selection.
        return new Set<string>();
      }
      const next = new Set(prev);
      const shouldDeselect = pageIds.every((id) => next.has(id));
      if (shouldDeselect) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
    setSelectionScope('page');
  };

  const handleSelectAllFilteredContacts = () => {
    if (!userEmail) return;
    if (!confirm('Select all matching contacts across all pages? This may take some time.')) return;
    const sortCol =
      sortKeyContacts.startsWith('name')
        ? 'full_name'
        : sortKeyContacts.startsWith('company')
          ? 'company'
          : sortKeyContacts.startsWith('title')
            ? 'title'
            : 'enriched_at';
    const sortDir = sortKeyContacts.endsWith('_asc') ? 'asc' : 'desc';
    const pageSize = 500;

    setLoadingMore(true);
    void (async () => {
      try {
        const nextSelected = new Set<string>();
        let page = 0;

        while (true) {
          const { contacts: pageContacts } = await loadContactsServerSide(
            userEmail,
            page,
            pageSize,
            sortCol,
            sortDir,
            searchTerm,
            researchedDateFrom,
            researchedDateTo,
            { includeCount: false, filters: serverSideFilterPayload },
          );
          if (!pageContacts.length) break;

          const filtered = applyContactsClientFilters(pageContacts as Contact[]);
          filtered.forEach((contact) => {
            const id = getSelectionId(contact);
            if (id) nextSelected.add(id);
          });

          if (pageContacts.length < pageSize) break;
          page += 1;
        }

        setSelectedContacts(nextSelected);
        setSelectionScope('matching');
      } catch (error) {
        console.error('Select all filtered failed', error);
        alert('Failed to select all filtered contacts. Please try again.');
      } finally {
        setLoadingMore(false);
      }
    })();
  };

  const toggleContact = (id: string) => {
    if (!id) return;
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setSelectionScope('page');
  };

  // List management
  const handleCreateList = async () => {
    if (!newListName.trim()) { alert('Please enter a list name'); return; }
    if (userLists.includes(newListName)) { alert('List already exists'); return; }

    try {
      const res = await fetch(`${RESEARCH_API_URL}/upsert-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          entityType: LIST_ENTITY_TYPE,
          listName: newListName,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to create list');
      const canonicalName = String(data.listName || newListName);
      setUserLists((prev) => [...prev, canonicalName].sort());

      if (setAsDefault) {
        setDefaultList(canonicalName);
        localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, canonicalName);
      }

      setCreateListModal(false);
      setNewListName('');
      setSetAsDefault(false);
      await loadUserLists();
      alert(`List "${canonicalName}" created!`);
    } catch (e) {
      console.error('Failed to create list:', e);
      alert('Failed to create list');
    }
  };

  const handleSetDefaultList = (listName: string) => {
    setDefaultList(listName);
    localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, listName);
  };

  const handleDeleteList = async (listName: string) => {
    if (!confirm(`Delete list "${listName}"? Contacts will not be deleted, just removed from this list.`)) return;

    const contactsInList = contacts.filter(c => getContactLists(c).includes(listName));
    
    if (contactsInList.length > 0) {
      try {
        const searchResultIds = Array.from(
          new Set(
            contactsInList
              .map((contact) => getListIdentifiersForContact(contact).searchResultId)
              .filter(Boolean)
          )
        );
        const contactIds = Array.from(
          new Set(
            contactsInList
              .map((contact) => getListIdentifiersForContact(contact).contactId)
              .filter(Boolean)
          )
        );
        await fetch(`${RESEARCH_API_URL}/update-lists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail,
            entityType: LIST_ENTITY_TYPE,
            searchResultIds,
            contactIds,
            action: 'remove',
            listName
          })
        });
      } catch (e) {
        console.error('Failed to remove contacts from list:', e);
      }
    }

    try {
      await fetch(`${RESEARCH_API_URL}/delete-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          entityType: LIST_ENTITY_TYPE,
          listName,
        }),
      });
    } catch (e) {
      console.error('Failed to delete list metadata:', e);
    }

    setUserLists(userLists.filter(l => l !== listName));
    if (defaultList === listName) {
      setDefaultList('');
      localStorage.removeItem(DEFAULT_LIST_STORAGE_KEY);
    }
    loadContacts();
  };

  const handleAddToList = async (listName: string) => {
    if (!listName || selectedContacts.size === 0) return;

    try {
      const { searchResultIds, contactIds } = await resolveSelectedListIdentifiers();
      if (searchResultIds.length === 0 && contactIds.length === 0) {
        alert('Selected contacts are missing IDs for list updates.');
        return;
      }
      const res = await fetch(`${RESEARCH_API_URL}/update-lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          entityType: LIST_ENTITY_TYPE,
          searchResultIds,
          contactIds,
          action: 'add',
          listName,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to add to list');
      const updated = Number(data?.updated ?? 0);
      if (updated === 0) {
        alert(`No new contacts were added to "${listName}".`);
        return;
      }
      alert(`Added ${updated} contacts to "${listName}"`);
      await loadUserLists();
      await loadContacts();
      setSelectedContacts(new Set());
      setSelectionScope('page');
    } catch (e) {
      alert('Failed to add to list');
    }
  };

  const handleAddSingleToList = async (contact: Contact, listName: string) => {
    const { searchResultId, contactId } = getListIdentifiersForContact(contact);
    if (!listName || (!searchResultId && !contactId)) return;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/update-lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          entityType: LIST_ENTITY_TYPE,
          searchResultIds: searchResultId ? [searchResultId] : [],
          contactIds: contactId ? [contactId] : [],
          action: 'add',
          listName,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to add to list');
      const updated = Number(data?.updated ?? 0);
      if (updated === 0) {
        alert(`No new contacts were added to "${listName}".`);
        return;
      }
      await loadUserLists();
      await loadContacts();
      setAddToListModal({ contact: null });
      setAddToListSelection('');
    } catch (e) {
      alert('Failed to add to list');
    }
  };

  const handleRemoveFromList = async () => {
    if (!currentListFilter || selectedContacts.size === 0) return;
    if (!confirm(`Remove ${selectedContacts.size} contacts from "${currentListFilter}"?`)) return;

    try {
      const { searchResultIds, contactIds } = await resolveSelectedListIdentifiers();
      if (searchResultIds.length === 0 && contactIds.length === 0) {
        alert('Selected contacts are missing IDs for list updates.');
        return;
      }
      const res = await fetch(`${RESEARCH_API_URL}/update-lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          entityType: LIST_ENTITY_TYPE,
          searchResultIds,
          contactIds,
          listName: currentListFilter,
          action: 'remove',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to remove from list');
      const updated = Number(data?.updated ?? 0);
      if (updated === 0) {
        alert(`No contacts were removed from "${currentListFilter}".`);
        return;
      }
      alert(`Removed ${updated} contacts from "${currentListFilter}"`);
      await loadContacts();
      setSelectedContacts(new Set());
      setSelectionScope('page');
    } catch (e) {
      alert('Failed to remove from list');
    }
  };

  const handleFindContactSocial = async (contact: Contact, platform: ContactSocialPlatform) => {
    if (!userEmail) return;
    const searchResultId = contact.searchResultId;
    if (!searchResultId) return;
    if (!confirm('Run web search for this social profile? Costs 1 credit and has a low hit rate.')) return;
    const key = `${searchResultId}-${platform}`;
    setSocialLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${RESEARCH_API_URL}/contact-socials/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          searchResultId,
          platform,
          name: getContactName(contact),
          company_name: contact.company || contact.Company || '',
          location: formatLocation(contact.ContactLocation || contact.CompanyLocation),
          personal_email: contact.personalEmail1 || contact.PersonalEmail || '',
          business_email: contact.email1 || contact.email || '',
          ...(platform === 'linkedin' ? { postedLimit: '6months' } : {}),
        }),
      });
      const data = await res.json();
      if (!data.success || !data.url) {
        alert(data.error || 'No social profile found');
      } else {
        await loadContacts();
      }
      getCredits(userEmail).then(setCredits);
    } catch (e) {
      console.error('Contact social search failed:', e);
      alert('Contact social search failed');
    } finally {
      setSocialLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleScrapeContactSocial = async (contact: Contact, platform: ContactSocialPlatform) => {
    if (!userEmail) return;
    const searchResultId = contact.searchResultId;
    if (!searchResultId) return;
    const url = getContactSocialUrl(contact, platform);
    if (!url) {
      alert('Add the URL first, then you can scrape.');
      return;
    }
    if (!confirm('Run a platform scrape for this profile? Costs 1 credit and has a low hit rate.')) return;
    const key = `${searchResultId}-${platform}`;
    setSocialScrapeLoading((prev) => ({ ...prev, [key]: true }));
    const jobId = `${platform}-${searchResultId}-${Date.now()}`;
    addScrapeJob({
      id: jobId,
      label: `${platform.toUpperCase()} scrape · ${getContactName(contact)}`,
      source: platform,
      companyName: contact.Company || contact.company || '',
      queuedAt: new Date().toISOString(),
      status: 'queued',
      progress: 5,
      message: 'Starting...',
    });
    try {
      const res = await fetch(`${RESEARCH_API_URL}/contact-socials/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          searchResultId,
          platform,
          url,
          ...(platform === 'linkedin' ? { postedLimit: '6months' } : {}),
        }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.error || 'Social scrape failed');
        updateScrapeJob(jobId, { status: 'failed', progress: 100, message: 'Failed to start' });
      } else {
        updateScrapeJob(jobId, { status: 'running', progress: 15, message: 'Running...' });
        const poll = async () => {
          const statusRes = await fetch(`${RESEARCH_API_URL}/contact-socials/scrape/status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userEmail,
              searchResultId,
              platform,
              run_id: data.runs?.profile?.run_id || data.run_id || '',
              kind: platform === 'linkedin' ? 'profile' : undefined,
            }),
          });
          const statusData = await statusRes.json();
          if (!statusData.success) {
            updateScrapeJob(jobId, { status: 'failed', progress: 100, message: 'Failed' });
            return;
          }
          if (statusData.status === 'RUNNING') {
            updateScrapeJob(jobId, { status: 'running', progress: 40, message: 'Running...' });
            setTimeout(poll, 3000);
            return;
          }
          if (platform === 'linkedin') {
            const postsRes = await fetch(`${RESEARCH_API_URL}/contact-socials/scrape/status`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userEmail,
                searchResultId,
                platform,
                run_id: data.runs?.posts?.run_id || '',
                kind: 'posts',
              }),
            });
            const postsData = await postsRes.json();
            if (postsData.status === 'RUNNING') {
              updateScrapeJob(jobId, { status: 'running', progress: 70, message: 'Running posts...' });
              setTimeout(poll, 3000);
              return;
            }
          }
          updateScrapeJob(jobId, { status: 'succeeded', progress: 100, message: 'Complete' });
          await loadContacts();
        };
        poll();
      }
      getCredits(userEmail).then(setCredits);
    } catch (e) {
      console.error('Contact social scrape failed:', e);
      alert('Contact social scrape failed');
      updateScrapeJob(jobId, { status: 'failed', progress: 100, message: 'Failed' });
    } finally {
      setSocialScrapeLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveContactSocial = async () => {
    if (!userEmail || !socialEdit) return;
    const url = socialEditValue.trim();
    const searchResultId = socialEdit.contact.searchResultId;
    if (!searchResultId) return;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/contact-socials/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          searchResultId,
          platform: socialEdit.platform,
          url,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.error || 'Failed to save URL');
      } else {
        await loadContacts();
        setSocialEdit(null);
        setSocialEditValue('');
      }
    } catch (e) {
      console.error('Save social URL failed:', e);
      alert('Save failed');
    }
  };

  const handleViewContactSocialReport = async (contact: Contact, platform: ContactSocialPlatform) => {
    if (!userEmail) return;
    const ids = getContactSocialDatasetIds(contact, platform);
    if (!ids.profile && !ids.posts) return;
    const key = `${contact.searchResultId}-${platform}`;
    setContactSocialReportLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const result: Record<string, unknown> = {};
      if (ids.profile) {
        const res = await fetch(`${RESEARCH_API_URL}/contact-socials/dataset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataset_id: ids.profile }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Failed to load profile');
        result.profile = data.items || [];
      }
      if (ids.posts) {
        const res = await fetch(`${RESEARCH_API_URL}/contact-socials/dataset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataset_id: ids.posts }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Failed to load posts');
        result.posts = data.items || [];
      }
      setContactSocialReport({ contact, platform, items: [result] });
    } catch (e) {
      console.error('Load contact social report failed:', e);
      alert('Failed to load report');
    } finally {
      setContactSocialReportLoading((prev) => ({ ...prev, [key]: false }));
    }
  };


  const handleScrapeContactCrunchbase = async (companyName: string) => {
    if (!userEmail || !companyName) return;
    if (crunchbaseLoading[companyName]) return;
    const jobId = `crunchbase-${companyName}-${Date.now()}`;
    addScrapeJob({
      id: jobId,
      label: `Crunchbase · ${companyName}`,
      source: 'crunchbase',
      companyName,
      queuedAt: new Date().toISOString(),
      status: 'queued',
      progress: 5,
      message: 'Starting...',
    });
    setCrunchbaseLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const start = await startCrunchbaseScrape(companyName, userEmail, true);
      if (!start.success) throw new Error(start.error || 'Crunchbase scrape failed');
      updateScrapeJob(jobId, { status: 'running', progress: 30, message: 'Scraping...' });
      const data = await pollCrunchbaseResults(start, (progress, message) => {
        updateScrapeJob(jobId, { status: 'running', progress, message });
      });
      setCrunchbaseReports((prev) => ({ ...prev, [companyName]: data }));
      setContactCrunchbaseReport({ companyName, data });
      updateScrapeJob(jobId, { status: 'succeeded', progress: 100, message: 'Complete' });
    } catch (e) {
      console.error('Crunchbase scrape failed:', e);
      alert('Crunchbase scrape failed');
      updateScrapeJob(jobId, { status: 'failed', progress: 100, message: 'Failed' });
    } finally {
      setCrunchbaseLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const loadCompanyLinkedinReport = async (companyName: string) => {
    const res = await fetch(`${RESEARCH_API_URL}/company/linkedin/full`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyName }),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to load LinkedIn report');
    return data.data;
  };

  const loadCompanyWebsiteReport = async (companyName: string) => {
    const res = await fetch(`${RESEARCH_API_URL}/company/website/full`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyName }),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to load website report');
    return data.data;
  };

  const pollWebsiteResults = async (
    contentRunId: string,
    contentDatasetId?: string,
    linksRunId?: string,
    linksDatasetId?: string,
    onProgress?: (progress: number, message: string) => void
  ) => {
    let contentReady = false;
    let linksReady = !linksRunId;
    let resolvedContentDs = contentDatasetId;
    let resolvedLinksDs = linksDatasetId;

    for (let i = 0; i < 90; i += 1) {
      await sleep(3000);
      onProgress?.(30 + Math.floor((i / 90) * 50), `Checking status (${i + 1}/90)...`);

      if (!contentReady) {
        const statusRes = await fetch(`${RESEARCH_API_URL}/company/website/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ run_id: contentRunId, dataset_id: resolvedContentDs }),
        });
        const statusData = await statusRes.json();
        if (statusData.status === 'SUCCEEDED') {
          contentReady = true;
          resolvedContentDs = statusData.dataset_id || resolvedContentDs;
        } else if (statusData.status === 'FAILED' || statusData.status === 'ABORTED') {
          throw new Error('Website content scrape failed');
        }
      }

      if (linksRunId && !linksReady) {
        const statusRes = await fetch(`${RESEARCH_API_URL}/company/website/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ run_id: linksRunId, dataset_id: resolvedLinksDs }),
        });
        const statusData = await statusRes.json();
        if (statusData.status === 'SUCCEEDED') {
          linksReady = true;
          resolvedLinksDs = statusData.dataset_id || resolvedLinksDs;
        } else if (statusData.status === 'FAILED' || statusData.status === 'ABORTED') {
          throw new Error('Website links scrape failed');
        }
      }

      if (contentReady && linksReady) break;
    }

    if (!contentReady) throw new Error('Website content scrape timeout');

    const contentRes = await fetch(`${RESEARCH_API_URL}/company/website/results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dataset_id: resolvedContentDs }),
    });
    const contentData = await contentRes.json();
    const contentItems = Array.isArray(contentData?.results) ? contentData.results : [];

    let linkItems: unknown[] = [];
    if (linksRunId) {
      const linksRes = await fetch(`${RESEARCH_API_URL}/company/website/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataset_id: resolvedLinksDs }),
      });
      const linksData = await linksRes.json();
      linkItems = Array.isArray(linksData?.results) ? linksData.results : [];
    }

    return { contentItems, linkItems };
  };

  const handleScrapeCompanyLinkedin = async (contact: Contact) => {
    if (!userEmail) return;
    const companyName = deriveCompanyName(contact);
    const linkedinUrl = normalizeWebsiteUrl(String(pickContactField(contact, ['CompanyLIProfileUrl', 'companyLinkedinUrl', 'linkedin_company_url'])));
    if (!companyName || companyName === 'Unknown') return;
    if (!linkedinUrl) {
      alert('Missing company LinkedIn URL.');
      return;
    }
    if (companyLinkedinLoading[companyName]) return;
    const jobId = `company-linkedin-${companyName}-${Date.now()}`;
    addScrapeJob({
      id: jobId,
      label: `Company LinkedIn · ${companyName}`,
      source: 'linkedin',
      companyName,
      queuedAt: new Date().toISOString(),
      status: 'queued',
      progress: 5,
      message: 'Starting...',
    });
    setCompanyLinkedinLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const start = await startLinkedIn360Scrape(companyName, linkedinUrl, userEmail, true);
      if (!start?.success) throw new Error(start?.error || 'LinkedIn scrape failed');
      updateScrapeJob(jobId, { status: 'running', progress: 25, message: 'Scraping LinkedIn...' });
      const data = await pollLinkedIn360Results(start, (progress, message) => {
        updateScrapeJob(jobId, { status: 'running', progress, message });
      }, { requireCompanyProfile: false });
      const saveRes = await fetch(`${RESEARCH_API_URL}/company/linkedin/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName, data, linkedinUrl }),
      });
      const saved = await saveRes.json();
      if (!saveRes.ok || !saved?.success) throw new Error(saved?.error || 'Failed to save LinkedIn data');
      setCompanyLinkedinReports((prev) => ({ ...prev, [companyName]: data }));
      setContactCompanyReport({ companyName, source: 'linkedin_360', data, scrapedAt: new Date().toISOString() });
      updateScrapeJob(jobId, { status: 'succeeded', progress: 100, message: 'Complete' });
      await loadContacts();
      void getCredits(userEmail).then(setCredits);
    } catch (error) {
      console.error('Company LinkedIn scrape failed:', error);
      alert('Company LinkedIn scrape failed');
      updateScrapeJob(jobId, { status: 'failed', progress: 100, message: 'Failed' });
    } finally {
      setCompanyLinkedinLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleScrapeCompanyWebsite = async (contact: Contact) => {
    if (!userEmail) return;
    const companyName = deriveCompanyName(contact);
    const website = normalizeWebsiteUrl(String(pickContactField(contact, ['CompanyWebsite', 'Website', 'companyWebsite', 'companyDomain', 'CompanyDomain'])));
    if (!companyName || companyName === 'Unknown' || !website) {
      alert('Missing company website.');
      return;
    }
    if (websiteLoading[companyName]) return;
    const jobId = `company-website-${companyName}-${Date.now()}`;
    addScrapeJob({
      id: jobId,
      label: `Website · ${companyName}`,
      source: 'website',
      companyName,
      queuedAt: new Date().toISOString(),
      status: 'queued',
      progress: 5,
      message: 'Starting...',
    });
    setWebsiteLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      updateScrapeJob(jobId, { status: 'running', progress: 20, message: 'Crawling website...' });
      const res = await fetch(`${RESEARCH_API_URL}/company/website-scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName, website }),
      });
      const start = await res.json();
      if (!res.ok || !start?.success) throw new Error(start?.error || 'Website scrape failed');
      const { contentItems, linkItems } = await pollWebsiteResults(
        start.content?.run_id,
        start.content?.dataset_id,
        start.links?.run_id,
        start.links?.dataset_id,
        (progress, message) => updateScrapeJob(jobId, { status: 'running', progress, message })
      );
      const saveRes = await fetch(`${RESEARCH_API_URL}/company/website/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName, data: contentItems, linksData: linkItems }),
      });
      const saved = await saveRes.json();
      if (!saveRes.ok || !saved?.success) throw new Error(saved?.error || 'Failed to save website data');
      const data = [...contentItems, ...linkItems];
      setWebsiteReports((prev) => ({ ...prev, [companyName]: data }));
      setContactCompanyReport({ companyName, source: 'website', data, scrapedAt: new Date().toISOString() });
      updateScrapeJob(jobId, { status: 'succeeded', progress: 100, message: 'Complete' });
      await loadContacts();
      void getCredits(userEmail).then(setCredits);
    } catch (error) {
      console.error('Company website scrape failed:', error);
      alert('Company website scrape failed');
      updateScrapeJob(jobId, { status: 'failed', progress: 100, message: 'Failed' });
    } finally {
      setWebsiteLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleViewCompanyLinkedinReport = async (companyName: string) => {
    try {
      const cached = companyLinkedinReports[companyName];
      const data = cached ?? await loadCompanyLinkedinReport(companyName);
      if (!cached) setCompanyLinkedinReports((prev) => ({ ...prev, [companyName]: data }));
      setContactCompanyReport({ companyName, source: 'linkedin_360', data });
    } catch (error) {
      console.error('Load company LinkedIn report failed:', error);
      alert('Failed to load company LinkedIn report');
    }
  };

  const handleViewCompanyWebsiteReport = async (companyName: string) => {
    try {
      const cached = websiteReports[companyName];
      const data = cached ?? await loadCompanyWebsiteReport(companyName);
      if (!cached) setWebsiteReports((prev) => ({ ...prev, [companyName]: data }));
      setContactCompanyReport({ companyName, source: 'website', data });
    } catch (error) {
      console.error('Load website report failed:', error);
      alert('Failed to load website report');
    }
  };

  const contactsGridTemplate =
    '40px 200px 180px 180px 160px 200px 200px 140px 140px 90px 90px 90px 90px 90px 90px 90px 120px 220px 100px';
  const contactsGridWidth = 2510;
  const contactRowHeight = 96;
  const contactsListHeight = Math.min(800, Math.max(contactRowHeight * 10, paginatedContacts.length * contactRowHeight));
  type ContactsRowProps = { items: Contact[] };

  const ContactRow = ({ index, style, items }: RowComponentProps<ContactsRowProps>) => {
    const contact = items[index];
    if (!contact) return null;
    const selectionId = getSelectionId(contact);
    const pepBadges = getPepProgramEntries(contact)
      .filter((entry) => entry.status && entry.status !== 'IDLE')
      .map((entry) => {
        const meta = pepBadgeMeta(entry.status);
        return {
          key: `${entry.programType}-${entry.runId || entry.status}`,
          label: `${formatPepProgramLabel(entry.programType)} ${meta.status}`,
          className: meta.className,
        };
      });
    const name = contact.fullName || contact.Name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown';
    const title = decodeAmp(contact.title || contact.Title || '');
    const company = decodeAmp(contact.company || contact.Company || '');
    const location = formatLocation(contact.ContactLocation || contact.CompanyLocation);
    const email = contact.email1 || contact.email || '';
    const personalEmail = contact.personalEmail1 || contact.PersonalEmail || '';
    const businessPhone = contact.companyPhone1 || contact.contactPhone1 || '';
    const mobilePhone = contact.contactMobilePhone1 || '';
    const researchedAt = contact.researchedAt ? new Date(contact.researchedAt).toLocaleDateString() : '';
    const lists = getContactLists(contact);
    const hasBusinessEmail = Boolean(email);
    const hasPersonalEmail = Boolean(personalEmail);
    const hasBusinessPhone = Boolean(businessPhone);
    const hasMobilePhone = Boolean(mobilePhone);

    return (
      <div
        className="contacts-grid-row border-b border-gray-800 hover:bg-gray-900/50 text-sm"
        style={{
          ...style,
          display: 'grid',
          gridTemplateColumns: contactsGridTemplate,
          alignItems: 'start',
        }}
      >
        <div className="px-2 py-2 text-center">
          <input
            type="checkbox"
            checked={selectedContacts.has(selectionId)}
            onChange={() => toggleContact(selectionId)}
            className="w-4 h-4 accent-red-600"
          />
        </div>
        <div className="px-3 py-2">
          <button
            onClick={() => openContactProfile(contact)}
            className="font-semibold text-white hover:text-red-200 text-left"
            disabled={loadingContactItemId === contact.searchResultId}
          >
            {loadingContactItemId === contact.searchResultId ? 'Loading…' : name}
          </button>
          {pepBadges.length ? (
            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {pepBadges.map((badge) => (
                <span key={badge.key} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badge.className}`}>
                  {badge.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div
          className="px-3 py-2 text-gray-300 leading-snug whitespace-normal"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {title}
        </div>
        <div
          className="px-3 py-2 text-gray-300 leading-snug whitespace-normal"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {company || '-'}
        </div>
        <div className="px-3 py-2 text-gray-300">{location || '-'}</div>
        <div className="px-3 py-2">
          <button
            onClick={() => email && copyToClipboard(email, `email-${contact.searchResultId}`)}
            className={`inline-flex items-center gap-1 ${hasBusinessEmail ? 'text-blue-400 hover:text-blue-300' : 'text-gray-600'}`}
            title={email || 'No business email'}
            disabled={!hasBusinessEmail}
          >
            <Mail className={`w-4 h-4 ${hasBusinessEmail ? '' : 'opacity-40'}`} />
            {copiedField === `email-${contact.searchResultId}` ? <Check className="w-3 h-3" /> : null}
          </button>
        </div>
        <div className="px-3 py-2">
          <button
            onClick={() => personalEmail && copyToClipboard(personalEmail, `pemail-${contact.searchResultId}`)}
            className={`inline-flex items-center gap-1 ${hasPersonalEmail ? 'text-blue-400 hover:text-blue-300' : 'text-gray-600'}`}
            title={personalEmail || 'No personal email'}
            disabled={!hasPersonalEmail}
          >
            <Mail className={`w-4 h-4 ${hasPersonalEmail ? '' : 'opacity-40'}`} />
            {copiedField === `pemail-${contact.searchResultId}` ? <Check className="w-3 h-3" /> : null}
          </button>
        </div>
        <div className="px-3 py-2">
          <button
            onClick={() => businessPhone && copyToClipboard(businessPhone, `bphone-${contact.searchResultId}`)}
            className={`inline-flex items-center gap-1 ${hasBusinessPhone ? 'text-blue-400 hover:text-blue-300' : 'text-gray-600'}`}
            title={businessPhone || 'No business phone'}
            disabled={!hasBusinessPhone}
          >
            <Phone className={`w-4 h-4 ${hasBusinessPhone ? '' : 'opacity-40'}`} />
            {copiedField === `bphone-${contact.searchResultId}` ? <Check className="w-3 h-3" /> : null}
          </button>
        </div>
        <div className="px-3 py-2">
          <button
            onClick={() => mobilePhone && copyToClipboard(mobilePhone, `mphone-${contact.searchResultId}`)}
            className={`inline-flex items-center gap-1 ${hasMobilePhone ? 'text-blue-400 hover:text-blue-300' : 'text-gray-600'}`}
            title={mobilePhone || 'No mobile phone'}
            disabled={!hasMobilePhone}
          >
            <Phone className={`w-4 h-4 ${hasMobilePhone ? '' : 'opacity-40'}`} />
            {copiedField === `mphone-${contact.searchResultId}` ? <Check className="w-3 h-3" /> : null}
          </button>
        </div>
        <div className="px-3 py-2 text-center">{renderLinkedInCell(contact)}</div>
        <div className="px-3 py-2 text-center">{renderSocialCell(contact, 'facebook')}</div>
        <div className="px-3 py-2 text-center">{renderSocialCell(contact, 'instagram')}</div>
        <div className="px-3 py-2 text-center">{renderSocialCell(contact, 'youtube')}</div>
        <div className="px-3 py-2 text-center">{renderSocialCell(contact, 'tiktok')}</div>
        <div className="px-3 py-2 text-center">{renderSocialCell(contact, 'x')}</div>
        <div className="px-3 py-2 text-center">{renderSocialCell(contact, 'threads')}</div>
        <div className="px-3 py-2 text-xs text-gray-400">{researchedAt}</div>
        <div className="px-3 py-2">
          <div className="flex flex-wrap gap-1">
            {lists.length > 0 ? lists.map(l => (
              <span key={l} className="px-2 py-0.5 bg-gray-700 text-gray-300 rounded-full text-xs">{l}</span>
            )) : <span className="text-gray-600 text-xs">-</span>}
            <button
              onClick={() => {
                setAddToListSelection(defaultList || '');
                setAddToListModal({ contact });
              }}
              className="px-2 py-0.5 rounded text-white text-[11px] bg-gray-700 hover:bg-gray-600"
            >
              + Add
            </button>
          </div>
        </div>
        <div className="px-3 py-2 text-center border-l border-gray-700">
          <button
            onClick={() => handleRerunSeamless(contact)}
            disabled={rerunLoading[contact.searchResultId]}
            className="px-2 py-1 rounded text-white text-xs bg-red-700 hover:bg-red-600 disabled:opacity-50"
          >
            {rerunLoading[contact.searchResultId] ? '...' : 'Rerun'}
          </button>
        </div>
      </div>
    );
  };

  // My Lists should not trigger LinkedIn enrichment here.

  // Export CSV
  const handleExportCSV = async (selectedFields: string[]) => {
    const sortCol =
      sortKeyContacts.startsWith('name')
        ? 'full_name'
        : sortKeyContacts.startsWith('company')
          ? 'company'
          : sortKeyContacts.startsWith('title')
            ? 'title'
            : 'enriched_at';
    const sortDir = sortKeyContacts.endsWith('_asc') ? 'asc' : 'desc';

    const resolveExportContacts = async (): Promise<Contact[]> => {
      if (selectedContacts.size === 0) return filteredContacts;

      const selectedIds = new Set(
        Array.from(selectedContacts)
          .map((id) => String(id || '').trim())
          .filter(Boolean)
      );
      if (selectedIds.size === 0) return filteredContacts;

      const seen = new Set<string>();
      const rows: Contact[] = [];
      const appendMatches = (items: Contact[]) => {
        items.forEach((contact) => {
          const id = getSelectionId(contact);
          if (!id || !selectedIds.has(id) || seen.has(id)) return;
          seen.add(id);
          rows.push(contact);
        });
      };

      appendMatches(filteredContacts);
      if (rows.length >= selectedIds.size || !userEmail) return rows;

      const pageSize = 500;
      let page = 0;

      while (true) {
        setExportProgress({
          active: true,
          stage: 'Loading selected contacts',
          processed: rows.length,
          total: Math.max(selectedIds.size, rows.length || 1),
        });
        const { contacts: pageContacts } = await loadContactsServerSide(
          userEmail,
          page,
          pageSize,
          sortCol,
          sortDir,
          searchTerm,
          researchedDateFrom,
          researchedDateTo,
          { includeCount: false, filters: serverSideFilterPayload },
        );
        if (!pageContacts.length) break;

        const scoped = applyContactsClientFilters(pageContacts as Contact[]);
        appendMatches(scoped);
        if (rows.length >= selectedIds.size) break;
        if (pageContacts.length < pageSize) break;
        page += 1;
      }

      if (rows.length === 0) {
        const visibleMatches = paginatedContacts.filter((contact) => selectedIds.has(getSelectionId(contact)));
        if (visibleMatches.length > 0) return visibleMatches;
      }

      return rows;
    };

    let toExport: Contact[] = [];
    try {
      setExportProgress({
        active: true,
        stage: 'Preparing export',
        processed: 0,
        total: Math.max(selectedContacts.size || filteredContacts.length, 1),
      });
      toExport = await resolveExportContacts();
    } catch (error) {
      console.error('Export CSV failed while loading selected contacts:', error);
      alert('Failed to load all selected contacts for export.');
      setExportProgress({ active: false, stage: '', processed: 0, total: 0 });
      return;
    }
    if (toExport.length === 0) {
      alert('No contacts to export');
      setExportProgress({ active: false, stage: '', processed: 0, total: 0 });
      return;
    }

    const flattenValue = (value: any): string => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
      return JSON.stringify(value);
    };
    const flattenObject = (obj: Record<string, any>, prefix = '', out: Record<string, string> = {}) => {
      Object.entries(obj || {}).forEach(([key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          flattenObject(value, fullKey, out);
        } else {
          out[fullKey] = flattenValue(value);
        }
      });
      return out;
    };

    const datasetCache = new Map<string, any[]>();
    const fetchDataset = async (datasetId?: string): Promise<any[]> => {
      if (!datasetId) return [];
      if (datasetCache.has(datasetId)) return datasetCache.get(datasetId) || [];
      try {
        const res = await fetch(`${RESEARCH_API_URL}/contact-socials/dataset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataset_id: datasetId }),
        });
        const data = await res.json();
        const items = data?.success ? (data.items || []) : [];
        datasetCache.set(datasetId, items);
        return items;
      } catch {
        datasetCache.set(datasetId, []);
        return [];
      }
    };

    const derivedRow = async (c: Contact) => {
      const ids = getContactSocialDatasetIds(c, 'linkedin');
      const linkedinProfileData = await fetchDataset(ids.profile);
      const linkedinPostsData = await fetchDataset(ids.posts);
      return {
        _list_names: getContactLists(c).join('; '),
        _contact_location: formatLocation(c.ContactLocation || c.CompanyLocation) || '',
        _facebook: getContactSocialUrl(c, 'facebook'),
        _instagram: getContactSocialUrl(c, 'instagram'),
        _youtube: getContactSocialUrl(c, 'youtube'),
        _tiktok: getContactSocialUrl(c, 'tiktok'),
        _x: getContactSocialUrl(c, 'x'),
        _threads: getContactSocialUrl(c, 'threads'),
        _linkedin_profile_dataset: linkedinProfileData,
        _linkedin_posts_dataset: linkedinPostsData,
      };
    };
    let flattened: Record<string, any>[] = [];
    try {
      flattened = [];
      for (let i = 0; i < toExport.length; i += 1) {
        const c = toExport[i];
        const row = {
          ...(await derivedRow(c)),
          ...flattenObject(c as Record<string, any>),
        };
        flattened.push(row);
        setExportProgress({
          active: true,
          stage: 'Building CSV',
          processed: i + 1,
          total: toExport.length,
        });
      }
    } catch (error) {
      console.error('Export CSV failed during row build:', error);
      alert('Failed to build CSV export.');
      setExportProgress({ active: false, stage: '', processed: 0, total: 0 });
      return;
    }

    const headerSet = new Set<string>();
    flattened.forEach((row) => Object.keys(row).forEach((k) => headerSet.add(k)));
    const headers = selectedFields
      .map((field) => String(field || '').trim())
      .filter((field, index, arr) => field && arr.indexOf(field) === index && headerSet.has(field))
      .slice(0, MAX_EXPORT_FIELDS);
    if (headers.length === 0) {
      alert('None of the selected fields are available for export in these contacts.');
      setExportProgress({ active: false, stage: '', processed: 0, total: 0 });
      return;
    }

    const rows = flattened.map((row) => headers.map((h) => String((row as Record<string, unknown>)[h] ?? '')));
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    try {
      const blob = new Blob([csv], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `contacts_${currentListFilter || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (error) {
      console.error('Export CSV failed during download:', error);
      alert('Failed to start CSV download.');
      setExportProgress({ active: false, stage: '', processed: 0, total: 0 });
      return;
    }
    setExportProgress({
      active: true,
      stage: 'Download ready',
      processed: toExport.length,
      total: toExport.length,
    });
    window.setTimeout(() => {
      setExportProgress({ active: false, stage: '', processed: 0, total: 0 });
    }, 2000);
  };

  // Copy to clipboard
  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Please Log In</h1>
          <a href="/login" className="text-red-500 hover:text-red-400">Go to Login</a>
        </div>
      </div>
    );
  }

  const renderSocialCell = (contact: Contact, platform: ContactSocialPlatform) => {
    const url = getContactSocialUrl(contact, platform);
    const key = `${contact.searchResultId}-${platform}`;
    const isFinding = socialLoading[key];
    const isScraping = socialScrapeLoading[key];
    const iconComponentByPlatform: Partial<Record<ContactSocialPlatform, React.ComponentType<{ className?: string }>>> = {
      facebook: Facebook,
      instagram: Instagram,
      youtube: Youtube,
      x: Twitter,
    };
    const imageByPlatform: Partial<Record<ContactSocialPlatform, { src: string; alt: string }>> = {
      tiktok: {
        src: 'https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/698a09847305aaafa7f897bc.png',
        alt: 'TikTok',
      },
      threads: {
        src: 'https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/698a0ab57305aab63df8de8e.png',
        alt: 'Threads',
      },
    };
    const Icon = iconComponentByPlatform[platform];
    const iconClass = `w-5 h-5 min-w-5 min-h-5 ${url ? 'text-blue-400' : 'text-gray-600'}`;
    const imgClass = `w-6 h-6 min-w-6 min-h-6 object-contain block ${url ? '' : 'grayscale opacity-40'}`;
    const image = imageByPlatform[platform];
    return (
      <div className="inline-flex items-center gap-2">
        {Icon ? (
          <button
            className="text-blue-400 p-0 leading-none"
            onClick={() => setContactSocialDrawer({ contact, platform })}
            disabled={isFinding || isScraping}
            title={url ? 'View options' : 'Find (1cr)'}
          >
            <Icon className={iconClass} />
          </button>
        ) : image ? (
          <button
            className="text-blue-400 p-0 leading-none"
            onClick={() => setContactSocialDrawer({ contact, platform })}
            disabled={isFinding || isScraping}
            title={url ? 'View options' : 'Find (1cr)'}
          >
            <img src={image.src} className={imgClass} alt={image.alt} />
          </button>
        ) : (
          <button
            className="text-blue-400 hover:text-blue-300 text-xs"
            onClick={() => setContactSocialDrawer({ contact, platform })}
            disabled={isFinding || isScraping}
          >
            {url ? 'View' : 'Find (1cr)'}
          </button>
        )}
      </div>
    );
  };

  const renderLinkedInCell = (contact: Contact) => {
    const url = normalizeLinkedinProfileUrl(contact.linkedIn || contact.LIProfileUrl || '');
    const iconClass = `w-5 h-5 min-w-5 min-h-5 ${url ? 'text-blue-400' : 'text-gray-600'}`;
    return (
      <div className="inline-flex items-center gap-2">
        <button
          className="text-blue-400 p-0 leading-none"
          onClick={() => setContactSocialDrawer({ contact, platform: 'linkedin' })}
          title={url ? 'View options' : 'Find (1cr)'}
        >
          <Linkedin className={iconClass} />
        </button>
      </div>
    );
  };

  return (
    <div className="contacts-page min-h-screen bg-dark-bg">
      <Header />

      {(loading || loadingMore) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-card-dark border border-gray-700 rounded-xl px-6 py-5 text-center min-w-[280px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto mb-4" />
            <p className="text-white text-lg font-semibold">
              {loading ? 'Loading contacts…' : 'Loading more contacts…'}
            </p>
            <p className="text-xs text-gray-400 mt-2">Please wait</p>
          </div>
        </div>
      )}

      {/* Create List Modal */}
      {createListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setCreateListModal(false)} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-md w-full mx-4 p-6 relative z-10">
            <h3 className="text-xl font-bold text-white mb-4">Create New List</h3>
            <input
              type="text"
              value={newListName}
              onChange={e => setNewListName(e.target.value)}
              placeholder="Enter list name..."
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white mb-4 focus:outline-none focus:border-red-500"
            />
            <label className="flex items-center mb-4 text-gray-300 text-sm">
              <input
                type="checkbox"
                checked={setAsDefault}
                onChange={e => setSetAsDefault(e.target.checked)}
                className="mr-2 accent-red-500"
              />
              Set as default list for new contacts
            </label>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setCreateListModal(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">Cancel</button>
              <button onClick={handleCreateList} className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold">Create List</button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Lists Modal */}
      {manageListsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setManageListsModal(false)} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-lg w-full mx-4 p-6 relative z-10 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Manage Lists</h3>
            {userLists.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No lists created yet.</p>
            ) : (
              <div className="space-y-2">
                {userLists.map(list => (
                  <div key={list} className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-white">{list}</span>
                      {list === defaultList && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
                    </div>
                    <div className="flex items-center space-x-2">
                      {list !== defaultList && (
                        <button onClick={() => handleSetDefaultList(list)} className="text-xs text-gray-400 hover:text-white">Set Default</button>
                      )}
                      <button onClick={() => handleDeleteList(list)} className="text-xs text-red-400 hover:text-red-300">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <button onClick={() => { setManageListsModal(false); setCreateListModal(true); }} className="w-full px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white text-sm">+ Create New List</button>
            </div>
          </div>
        </div>
      )}

      {/* Add to List Modal */}
      {addToListModal.contact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setAddToListModal({ contact: null })} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-md w-full mx-4 p-6 relative z-10">
            <h3 className="text-xl font-bold text-white mb-2">Add to List</h3>
            <p className="text-sm text-gray-400 mb-4">
              {(addToListModal.contact.fullName || addToListModal.contact.Name || 'Contact')}
            </p>
            <select
              value={addToListSelection}
              onChange={e => setAddToListSelection(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm mb-4"
            >
              <option value="">Select a list</option>
              {defaultList && <option value={defaultList}>{defaultList} (Default)</option>}
              {userLists.filter(l => l !== defaultList).map(list => (
                <option key={list} value={list}>{list}</option>
              ))}
              <option value="__new__">+ Create New List</option>
            </select>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setAddToListModal({ contact: null })}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (addToListSelection === '__new__') {
                    setAddToListModal({ contact: null });
                    setCreateListModal(true);
                    return;
                  }
                  if (!addToListSelection) return;
                  handleAddSingleToList(addToListModal.contact as Contact, addToListSelection);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Profile Modal */}
      {contactProfileDrawer && (
        <div
          className="ss-company-modal ss-contact-modal overlay open"
          data-theme={contactModalTheme}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setContactProfileDrawer(null);
            }
          }}
        >
          {(() => {
            const c = contactProfileDrawer.contact;
            const name = getContactName(c);
            const title = c.title || c.Title || 'Contact';
            const company = c.company || c.Company || deriveCompanyName(c) || '-';
            const liUrl = normalizeLinkedinProfileUrl(c.linkedIn || c.LIProfileUrl || '');
            const liIds = getContactSocialDatasetIds(c, 'linkedin');
            const liKey = `${c.searchResultId}-linkedin`;
            const firstName = pickContactField(c, ['firstName', 'FirstName', 'first_name', 'First']);
            const lastName = pickContactField(c, ['lastName', 'LastName', 'last_name', 'Last']);
            const department = pickContactField(c, ['department', 'Department', 'dept', 'Dept']);
            const seniority = pickContactField(c, ['seniority', 'Seniority', 'seniorityLevel', 'SeniorityLevel']);
            const workEmails = joinContactFields(c, ['email1', 'email2', 'email3', 'email', 'Email']);
            const directDial = pickContactField(c, ['contactPhone1', 'directDial', 'DirectDial', 'Phone']);
            const mobilePhone = pickContactField(c, ['contactMobilePhone1', 'mobile', 'MobilePhone']);
            const industry = pickContactField(c, ['CompanyIndustries', 'CompanyIndustry', 'Industry', 'industry']);
            const employeeCount = pickContactField(c, ['CompanyStaffCount', 'CompanyStaffCountRange', 'employeeCount']);
            const revenueRange = pickContactField(c, ['CompanyRevenue', 'RevenueRange', 'revenueRange', 'CompanyRevenueRange', 'CompanyAnnualRevenue']);
            const hqLocation =
              formatLocation(c.CompanyLocation || (c as any).CompanyHQ || (c as any).Headquarters || (c as any).headquarters) ||
              pickContactField(c, ['CompanyLocation', 'CompanyHQ', 'Headquarters', 'headquarters']);
            const foundedYear = pickContactField(c, ['FoundedYear', 'CompanyFoundedYear', 'companyFoundedYear', 'CompanyFounded']);
            const companyWebsite = pickContactField(c, ['CompanyWebsite', 'Website', 'companyWebsite', 'companyDomain', 'CompanyDomain']);
            const technologyCountRaw = pickContactField(c, ['CompanyTechnologiesCount', 'technologyCount', 'TechnologyUsageCount']);
            const technologyCount = technologyCountRaw ? `${technologyCountRaw} systems` : '';
            const companyKey = company && company !== '-' ? company : deriveCompanyName(c);
            const businessEmail = workEmails;
            const personalEmail = pickContactField(c, ['personalEmail1', 'PersonalEmail', 'personal_email']);
            const companyDomain = normalizeDomain(
              companyWebsite ||
              (c as any).CompanyDomain ||
              (c as any).companyDomain ||
              ''
            );
            const primaryBusinessEmail = pickPrimaryEmail(businessEmail, companyDomain);
            const primaryPersonalEmail = (() => {
              const personalEmails = extractEmails(personalEmail);
              if (!personalEmails.length) return '';
              const filtered = personalEmails.filter((email) => email.toLowerCase() !== primaryBusinessEmail.toLowerCase());
              return filtered[0] || personalEmails[0];
            })();
            const primaryBusinessPhone = directDial || pickContactField(c, ['contactPhone1', 'Phone', 'CompanyPhone', 'companyPhone1', 'CompanyPhone1']) || '';
            const primaryMobilePhone = mobilePhone || pickContactField(c, ['contactMobilePhone1', 'mobile', 'Mobile', 'MobilePhone']) || '';
            const formatPhoneDisplay = (value?: string): string => {
              const raw = (value || '').trim();
              if (!raw) return '';
              const digits = raw.replace(/\D/g, '');
              // Normalize US numbers that include leading country code "1".
              const normalized = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
              if (normalized.length === 10) {
                return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
              }
              return raw;
            };
            const mobilePhoneDisplay = formatPhoneDisplay(primaryMobilePhone);
            const liAvatarUrl =
              (c as any)?.linkedin_profile_ds?.[0]?.avatar?.url ||
              (c as any)?.linkedin_profile?.avatar?.url ||
              (c as any)?.linkedin?.avatar?.url ||
              (c as any)?.avatar?.url ||
              (c as any)?.image ||
              '';
            const initials = name
              .split(' ')
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0]?.toUpperCase())
              .join('');
            const hasLinkedIn = Boolean(liUrl);
            const hasFacebook = Boolean(getContactSocialUrl(c, 'facebook'));
            const hasInstagram = Boolean(getContactSocialUrl(c, 'instagram'));
            const hasYoutube = Boolean(getContactSocialUrl(c, 'youtube'));
            const hasTiktok = Boolean(getContactSocialUrl(c, 'tiktok'));
            const hasX = Boolean(getContactSocialUrl(c, 'x'));
            const hasThreads = Boolean(getContactSocialUrl(c, 'threads'));
            const formatFieldValue = (value?: unknown): string => {
              if (Array.isArray(value)) return value.filter(Boolean).join(', ');
              if (value && typeof value === 'object') return JSON.stringify(value);
              return value ? String(value) : '';
            };
            const field = (label: string, value?: unknown) => (
              <div className="mcard-row">
                <div className="mcard-key">{label}</div>
                <div className="mcard-val">{formatFieldValue(value) || '—'}</div>
              </div>
            );
            const notesDraft = contactNotesDraft || buildContactNotesDraft(c);
            const notesField = (label: string, key: keyof typeof notesDraft) => (
              <div className="mcard-row">
                <div className="mcard-key">{label}</div>
                <div className="mcard-val">
                  {contactNotesEditing ? (
                    <input
                      type="text"
                      className="m-input"
                      value={notesDraft[key] || ''}
                      onChange={(event) => setContactNotesDraftField(String(key), event.target.value)}
                    />
                  ) : (
                    formatFieldValue(notesDraft[key]) || '—'
                  )}
                </div>
              </div>
            );
            const parsePercent = (value?: unknown): number | null => {
              if (value === null || value === undefined) return null;
              if (typeof value === 'number' && !Number.isNaN(value)) {
                return value > 1 ? value / 100 : value;
              }
              const raw = String(value).trim();
              if (!raw) return null;
              const numeric = parseFloat(raw.replace('%', ''));
              if (Number.isNaN(numeric)) return null;
              return numeric > 1 ? numeric / 100 : numeric;
            };
            const emailValidationStatus = pickContactField(c, [
              'EmailValidation',
              'EmailValidationStatus',
              'emailValidation',
              'email_validation_status',
              'email_status',
              'emailStatus',
              'email1EmailAI',
            ]);
            const emailValidationScore = (() => {
              const status = (emailValidationStatus || '').toString().toLowerCase();
              if (!status) return null;
              if (status.includes('valid')) return 1;
              if (status.includes('accept all') || status.includes('accept_all')) return 0;
              return -1;
            })();
            const phoneConfidence = parsePercent(pickContactField(c, ['contactPhone1TotalAI', 'contactMobilePhone1TotalAI']));
            const emailConfidence = parsePercent(pickContactField(c, ['email1TotalAI']));
            const companyPhoneConfidence = parsePercent(pickContactField(c, ['companyPhone1TotalAI']));
            const contactMatchScore = (() => {
              const parts: number[] = [];
              if (emailValidationScore !== null) parts.push(emailValidationScore as number);
              if (phoneConfidence !== null) parts.push((phoneConfidence as number) * 1.5);
              if (emailConfidence !== null) parts.push(emailConfidence as number);
              if (companyPhoneConfidence !== null) parts.push(companyPhoneConfidence as number);
              if (parts.length === 0) return '';
              const avg = parts.reduce((a, b) => a + b, 0) / parts.length;
              return `${Math.round(avg * 100)}%`;
            })();
            const pepKey = getPepLeadId(c);
            const pepPayloadRaw = pepData[pepKey];
            const pepPayload = isLegacyPepPayload(pepPayloadRaw) ? null : pepPayloadRaw;
            const pepRunState = pepRunData[pepKey] || null;
            const pepProgramEntries = getPepProgramEntries(c);
            const primaryPepProgram = pepProgramEntries[0] || null;
            const pepRun = pepRunState?.run || (primaryPepProgram
              ? {
                  status: primaryPepProgram.status,
                  runId: primaryPepProgram.runId || null,
                  programType: primaryPepProgram.programType || null,
                  channelFamily: primaryPepProgram.channelFamily || null,
                  entrySource: primaryPepProgram.entrySource || null,
                }
              : null);
            const pepRunSteps = Array.isArray(pepRunState?.steps) ? pepRunState!.steps : [];
            const pepDecision = pepPayload?.decision ?? pepPayload?.record?.decision ?? pepPayload?.decision_object ?? pepPayload?.decisionObject;
            const pepMessageRaw = pepPayload?.decision?.message ?? pepPayload?.message ?? pepPayload?.record?.decision?.message;
            const pepMessage =
              typeof pepMessageRaw === 'string'
                ? pepMessageRaw
                : (pepMessageRaw?.body as string | undefined) ||
                  (pepMessageRaw?.subject as string | undefined) ||
                  null;
            const pepContact = buildPepContact(c);
            const pepLoadingNow = Boolean(pepLoading[pepKey]);
            const pepErrorNow =
              pepError[pepKey] ||
              (pepPayloadRaw && isLegacyPepPayload(pepPayloadRaw) ? 'Legacy touchpoint path ignored. Regenerate to build the new P.E.P.ai sequence.' : null);
            const pepFitCheck =
              pepPayload?.result?.decision?.fit_check ||
              pepPayload?.decision?.fit_check ||
              pepPayload?.record?.decision?.fit_check ||
              pepPayload?.fit_check ||
              null;
            const linkedinPosts = getContactLinkedinPosts(c);
            const liActivity = getContactLinkedinActivity(c);
            const companySignals = buildPepSignalsFromIntel(c);
            const indirectIntent = computeIndirectIntentFromPosts(linkedinPosts);
            const pepFitAction = String(pepFitCheck?.recommended_action || '').toLowerCase();
            const pepFitPassive = ['view_profile', 'like'].includes(pepFitAction);
            const rawSelectedSignal = String(pepDecision?.decision_object?.selected_signal ?? '').trim();
            const hasConcreteSelectedSignal =
              Boolean(rawSelectedSignal) && rawSelectedSignal !== '—' && rawSelectedSignal !== '-';
            const pepSelectedSignal =
              hasConcreteSelectedSignal
                ? rawSelectedSignal
                : (indirectIntent.totalPoints > 0
                  ? 'INDIRECT_INTENT'
                  : (pepFitCheck ? (pepFitPassive ? 'FIT_CHECK_PASSIVE' : 'FIT_CHECK') : '—'));
            const pepSignalScore = (() => {
              const scoreRaw = pepDecision?.decision_object?.signal_score;
              if (typeof scoreRaw === 'number' && Number.isFinite(scoreRaw) && scoreRaw > 0) return scoreRaw;
              const parsedScore = Number(scoreRaw);
              if (Number.isFinite(parsedScore) && parsedScore > 0) return parsedScore;
              if (indirectIntent.totalPoints > 0) return indirectIntent.totalPoints;
              const fitScore = Number(pepFitCheck?.fit_score ?? 0);
              return Number.isFinite(fitScore) ? fitScore : 0;
            })();
            const pepFollowupHint = (() => {
              if (!pepFitCheck || !pepFitPassive) return null;
              const topics = Array.isArray((pepFitCheck as any)?.recurring_topics)
                ? (pepFitCheck as any).recurring_topics.filter((t: any) => Boolean(String(t || '').trim()))
                : [];
              const topTopic = topics.length > 0 ? String(topics[0]) : 'their recent initiative';
              return `Pocket follow-up: ask how "${topTopic}" performed and which channels drove the most engagement.`;
            })();
            const pepPlaybook = pepDecision?.playbook ?? pepPayload?.playbook ?? null;
            const pepTouchPath =
              pepPayload?.result?.decision?.touchpoint_path ||
              pepPayload?.decision?.touchpoint_path ||
              pepPayload?.record?.decision?.touchpoint_path ||
              pepPayload?.touchpoint_path ||
              null;
            const pepStepsByIndex = new Map(
              pepRunSteps.map((step: any) => [Number(step?.stepIndex ?? 0), step])
            );
            const pepCompletedCount = pepRunSteps.filter((step: any) => {
              const status = String(step?.status || '').toUpperCase();
              return status === 'SENT' || status === 'SKIPPED';
            }).length;
            const pepPendingCount = pepRunSteps.filter((step: any) => {
              const status = String(step?.status || '').toUpperCase();
              return status === 'PENDING' || status === 'RUNNING';
            }).length;
            const pepTotalCount = pepRunSteps.length || (pepTouchPath || []).length;
            const pepDecisionActions = new Set([
              'DECIDE_NEXT_ACTION',
              'PROSPECT_ASSIGNMENT_UPDATE',
              'EMAIL_2_DECISION',
              'REASSESS_PROGRAM',
            ]);
            const pepDecisionSteps = pepRunSteps.filter((step: any) =>
              pepDecisionActions.has(String(step?.action || '').toUpperCase())
            );
            const pepLatestDecisionStep =
              [...pepDecisionSteps]
                .reverse()
                .find((step: any) => ['SENT', 'SKIPPED', 'FAILED'].includes(String(step?.status || '').toUpperCase())) ||
              pepDecisionSteps[0] ||
              null;
            const pepDecisionLabel = (() => {
              if (!pepLatestDecisionStep) return 'Awaiting first decision checkpoint';
              const action = String(pepLatestDecisionStep?.action || '').toUpperCase();
              const status = String(pepLatestDecisionStep?.status || '').toUpperCase();
              const label = action.replace(/_/g, ' ').trim();
              return status ? `${label} · ${status}` : label;
            })();
            const pepDecisionReason = (() => {
              if (!pepLatestDecisionStep) return 'Decision checkpoint has not executed yet.';
              const info = pepLatestDecisionStep?.providerResponses?.info;
              const directReason = String(
                info?.decisionReason ||
                info?.reason ||
                pepLatestDecisionStep?.providerResponses?.error ||
                pepLatestDecisionStep?.error ||
                ''
              ).trim();
              if (directReason) return directReason;
              const message = String(info?.message || '').trim();
              if (message) return message;
              const status = String(pepLatestDecisionStep?.status || '').toUpperCase();
              if (status === 'PENDING') return 'Queued for the scheduled decision checkpoint.';
              if (status === 'FAILED') return 'Decision checkpoint failed before returning a reason.';
              if (status === 'SKIPPED') return 'Decision checkpoint was skipped by execution rules.';
              if (status === 'SENT') return 'Decision checkpoint completed.';
              return 'Decision checkpoint status is not yet available.';
            })();
            const pepPolicyVersion = isLegacyPepPayload(pepPayloadRaw)
              ? CURRENT_PEP_SEQUENCE_VERSION
              : (
                pepPayload?.pepProfileVersion ||
                pepPayload?.record?.pepProfileVersion ||
                pepPayload?.result?.record?.pepProfileVersion ||
                CURRENT_PEP_SEQUENCE_VERSION
              );
            const pepMergedProfile =
              pepPayload?.pepProfileMerged ||
              pepPayload?.record?.pepProfileMerged ||
              pepPayload?.result?.record?.pepProfileMerged ||
              null;
            const pepProfileSources =
              pepPayload?.pepProfileSources ||
              pepPayload?.record?.pepProfileSources ||
              pepPayload?.result?.record?.pepProfileSources ||
              null;
            const pepApprovalMode =
              pepMergedProfile?.approvalMode ||
              pepProfile?.approvalMode ||
              'manual';
            const pepSourceSummary = (() => {
              if (!pepProfileSources || typeof pepProfileSources !== 'object') return '';
              const keys = ['tenant', 'user', 'company', 'runtime'];
              const active = keys.filter((key) => {
                const layer = (pepProfileSources as any)[key];
                return layer && typeof layer === 'object' && Object.values(layer).some((value) => Boolean(value));
              });
              return active.length ? active.join(' > ') : 'system only';
            })();
            const pepPolicyOpen = Boolean(pepPolicyExpanded[pepKey]);
            const fitScores = computeFitcheckScores(c, {
              pepProfile: pepProfile || null,
              linkedinPosts,
              pepFitCheck,
            });
            const contactSignalsRows = [
              `Indirect intent points: ${indirectIntent.totalPoints}/20`,
              `LinkedIn posts scanned: ${linkedinPosts.length}`,
              `Primary channel: ${pepContact.primary_channel}`,
              `Connectability: ${fitScores.contactBreakdown.connectability}/100`,
              `Use-case potential: ${fitScores.contactBreakdown.use_case_potential}/100`,
              `Context depth: ${fitScores.contactBreakdown.context_data_depth}/100`,
            ];
            const companySignalsRows = [
              `Growth announcements: ${Number(companySignals.growth_announcements?.score || 0)}`,
              `Job listings: ${Number(companySignals.job_listings?.score || 0)}`,
              `Client reviews: ${Number(companySignals.client_reviews?.score || 0)}`,
              `Social activity: ${Number(companySignals.social_activity?.score || 0)}`,
              `Buying signals: ${fitScores.companyBreakdown.buying_signals}/100`,
              `ICP/industry match: ${fitScores.companyBreakdown.icp_industry_match}/100`,
            ];
            const channelActivity = [
              { key: 'linkedin', label: 'LinkedIn', active: hasLinkedIn, count: liActivity?.count ?? 0, icon: <Linkedin className="w-4 h-4" /> },
              { key: 'x', label: 'X', active: hasX, count: 0, icon: <Twitter className="w-4 h-4" /> },
              { key: 'youtube', label: 'YouTube', active: hasYoutube, count: 0, icon: <Youtube className="w-4 h-4" /> },
              { key: 'instagram', label: 'Instagram', active: hasInstagram, count: 0, icon: <Instagram className="w-4 h-4" /> },
              { key: 'threads', label: 'Threads', active: hasThreads, count: 0, icon: <AtSign className="w-4 h-4" /> },
            ];
            const playbookBlocks = [
              { key: 'opened_no_click', label: 'Opened, No Click' },
              { key: 'opened_clicked', label: 'Opened & Clicked' },
              { key: 'ignored', label: 'Ignored' },
            ];
            const toSteps = (raw: unknown): string[] => {
              if (Array.isArray(raw)) return raw.map((step) => String(step));
              if (!raw) return [];
              return String(raw).split('\n').filter(Boolean);
            };
            const enrichedAt = pickContactField(c, ['researchedAt', 'updatedAt', 'UpdatedAt', 'lastUpdated']) || '';
            return (
              <div className="modal" onClick={(event) => event.stopPropagation()}>
                <div className="mhead">
                  <div className="contact-avatar">
                    {liAvatarUrl ? (
                      <img src={liAvatarUrl} alt={name} className="contact-avatar-img" />
                    ) : (
                      initials || 'SS'
                    )}
                  </div>
                  <div>
                    <div className="mhead-name">{name}</div>
                    <div className="mhead-sub">
                      {title}
                      <span>·</span>
                      <span className="mhead-company">{company}</span>
                      <span>·</span>
                      <span className="b bpu">{seniority || 'C-Suite'}</span>
                      <span className="b bg">Enriched</span>
                    </div>
                  </div>
                  <div className="mhead-actions">
                    <button className="btn" onClick={() => liUrl && window.open(liUrl, '_blank')}>
                      <Linkedin className="w-4 h-4" />
                      LinkedIn
                    </button>
                    <button className="btn" onClick={() => primaryBusinessEmail && copyToClipboard(primaryBusinessEmail, 'contact_email')}>
                      <Mail className="w-4 h-4" />
                      Email
                    </button>
                    <button className="btn btn-p" onClick={() => setContactProfileDrawer(null)}>
                      Add to Sequence
                    </button>
                    <button
                      className="btn"
                      onClick={() => {
                        const next = contactModalTheme === 'dark' ? 'light' : 'dark';
                        setContactModalTheme(next);
                        window.localStorage.setItem('solo-theme', next);
                      }}
                    >
                      {contactModalTheme === 'dark' ? 'Light' : 'Dark'}
                    </button>
                    <button className="close-btn" onClick={() => setContactProfileDrawer(null)}>
                      ×
                    </button>
                  </div>
                </div>

                <div className="stat-strip">
                  {primaryBusinessEmail ? <div className="stat-chip"><strong>{primaryBusinessEmail}</strong></div> : null}
                  {primaryBusinessPhone ? <div className="stat-chip">{primaryBusinessPhone}</div> : null}
                  {mobilePhoneDisplay ? <div className="stat-chip">{mobilePhoneDisplay}</div> : null}
                  {primaryPersonalEmail ? <div className="stat-chip">{primaryPersonalEmail}</div> : null}
                </div>

                <div className="mtabs">
                  <div className={`mtab ${contactModalTab === 'ov' ? 'on' : ''}`} onClick={() => setContactModalTab('ov')}>Overview</div>
                  <div className={`mtab ${contactModalTab === 'intel' ? 'on' : ''}`} onClick={() => setContactModalTab('intel')}>Intelligence</div>
                  <div className={`mtab ${contactModalTab === 'sig' ? 'on' : ''}`} onClick={() => setContactModalTab('sig')}>Signals</div>
                  <div className={`mtab ${contactModalTab === 'act' ? 'on' : ''}`} onClick={() => setContactModalTab('act')}>Activity</div>
                  <div className={`mtab ${contactModalTab === 'seq' ? 'on' : ''}`} onClick={() => setContactModalTab('seq')}>Sequence</div>
                  <div className={`mtab ${contactModalTab === 'notes' ? 'on' : ''}`} onClick={() => setContactModalTab('notes')}>Notes</div>
                </div>

                <div className="mbody">
                  {contactModalTab === 'ov' && (
                    <div className="mpad">
                      <div className="kv-row kv-row-4">
                        <div className="kv-mini">
                          <div className="kv-mini-lbl">Perpetual Heat</div>
                          <div className="kv-mini-val">{pepContact.perpetual_heat_score}</div>
                          <div className="kv-mini-sub">/100</div>
                        </div>
                        <div className="kv-mini">
                          <div className="kv-mini-lbl">Potential Heat</div>
                          <div className="kv-mini-val">{pepContact.potential_heat_score}</div>
                          <div className="kv-mini-sub">/100</div>
                        </div>
                        <div className="kv-mini">
                          <div className="kv-mini-lbl">Heat Velocity</div>
                          <div className="kv-mini-val">{pepContact.heat_velocity}</div>
                          <div className="kv-mini-sub">30-day momentum</div>
                        </div>
                        <div className="kv-mini">
                          <div className="kv-mini-lbl">Contact Match</div>
                          <div className="kv-mini-val">{contactMatchScore || '—'}</div>
                          <div className="kv-mini-sub">confidence</div>
                        </div>
                      </div>
                      <div className="mcols" style={{ marginTop: 12 }}>
                        <div className="mcol-left">
                          <div className="mcard">
                            <div className="mcard-title">Contact</div>
                            {field('First', firstName)}
                            {field('Last', lastName)}
                            {field('Title', title)}
                            {field('Department', department)}
                            {field('Seniority', seniority)}
                          </div>
                          <div className="mcard">
                            <div className="mcard-title">Signals</div>
                            <div className="signals">
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--b-t)' }} />
                                <div className="signal-text">Selected: {pepSelectedSignal}</div>
                              </div>
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--a-t)' }} />
                                <div className="signal-text">Intent score: {pepSignalScore}</div>
                              </div>
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--b-t)' }} />
                                <div className="signal-text">Indirect intent points: {indirectIntent.totalPoints}/20</div>
                              </div>
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--g-t)' }} />
                                <div className="signal-text">Primary channel: {pepContact.primary_channel}</div>
                              </div>
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--b-t)' }} />
                                <div className="signal-text">
                                  Contact FitCheck: {fitScores.contactScore} ({fitScores.contactBand})
                                </div>
                              </div>
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--a-t)' }} />
                                <div className="signal-text">
                                  Company FitCheck: {fitScores.companyScore} ({fitScores.companyBand})
                                </div>
                              </div>
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--g-t)' }} />
                                <div className="signal-text">
                                  Combined FitCheck (60/40): {fitScores.combinedScore} ({fitScores.combinedBand})
                                </div>
                              </div>
                              {pepFitCheck && (
                                <>
                                  <div className="signal-item">
                                    <span
                                      className="signal-dot"
                                      style={{ background: pepFitCheck.approved ? 'var(--g-t)' : (pepFitPassive ? 'var(--a-t)' : 'var(--r-t)') }}
                                    />
                                    <div className="signal-text">
                                      Step FitCheck: {pepFitCheck.approved ? 'Evaluated — Active' : (pepFitPassive ? 'Evaluated — Passive' : 'Evaluated — Blocked')} ({Number(pepFitCheck.fit_score ?? 0)})
                                    </div>
                                  </div>
                                  <div className="signal-item">
                                    <span className="signal-dot" style={{ background: 'var(--b-t)' }} />
                                    <div className="signal-text">
                                      Action: {String(pepFitCheck.recommended_action || 'view_profile').toUpperCase()}
                                      {pepFitCheck.target_url ? ` · Target: ${String(pepFitCheck.target_url)}` : ''}
                                    </div>
                                  </div>
                                  {pepFitCheck.reason ? (
                                    <div className="signal-item">
                                      <span className="signal-dot" style={{ background: 'var(--a-t)' }} />
                                      <div className="signal-text">Reason: {String(pepFitCheck.reason)}</div>
                                    </div>
                                  ) : null}
                                </>
                              )}
                            </div>
                          </div>
                          <div className="mcard">
                            <div className="mcard-title">Contact Signals</div>
                            <div className="tag-row">
                              <span className={`b ${hasLinkedIn ? 'bb' : 'bn'}`}>LinkedIn</span>
                              <span className={`b ${hasInstagram ? 'bb' : 'bn'}`}>Instagram</span>
                              <span className={`b ${hasFacebook ? 'bb' : 'bn'}`}>Facebook</span>
                              <span className={`b ${hasYoutube ? 'bb' : 'bn'}`}>YouTube</span>
                              <span className={`b ${hasTiktok ? 'bb' : 'bn'}`}>TikTok</span>
                              <span className={`b ${hasX ? 'bb' : 'bn'}`}>X</span>
                              <span className={`b ${hasThreads ? 'bb' : 'bn'}`}>Threads</span>
                            </div>
                          </div>
                        </div>
                        <div className="mcol-right">
                          <div className="mcard">
                            <div className="mcard-title">Company</div>
                            {field('Company', company)}
                            {field('Industry', industry)}
                            {field('Employees', employeeCount)}
                            {field('Revenue', revenueRange)}
                            {field('HQ', hqLocation)}
                            {field('Founded', foundedYear)}
                            {field('Website', companyWebsite)}
                            {field('Tech Usage', technologyCount)}
                          </div>
                          <div className="mcard">
                            <div className="mcard-title">Generated Message</div>
                            <div className="mtext">{pepMessage || 'Generate a message to see output.'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {contactModalTab === 'intel' && (
                    <div className="mpad">
                      <div className="mcard">
                        <div className="mcard-title">P.E.P.ai Intelligence</div>
                        <div className="mtext">Signal-based messaging engine</div>
                        {canUsePepaiResearch && instantlyCampaigns.length > 0 ? (
                          <div style={{ marginTop: 10 }}>
                            <label className="mcard-key" style={{ display: 'block', marginBottom: 6 }}>Email Campaign</label>
                            <select
                              value={selectedInstantlyCampaignId}
                              onChange={(e) => setSelectedInstantlyCampaignId(e.target.value)}
                              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                            >
                              {instantlyCampaigns.map((campaign) => (
                                <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                              ))}
                            </select>
                          </div>
                        ) : null}
                        <div style={{ marginTop: 10 }}>
                          <button className="btn btn-p" onClick={() => handleGeneratePep(c)} disabled={pepLoadingNow}>
                            {pepLoadingNow ? 'Generating…' : 'Generate'}
                          </button>
                        </div>
                        {pepErrorNow && <div className="mtext" style={{ color: 'var(--r-t)', marginTop: 8 }}>{pepErrorNow}</div>}
                      </div>
                      <div className="mcard">
                        <div className="mcard-title">Signals</div>
                        <div className="signals">
                          <div className="signal-item">
                            <span className="signal-dot" style={{ background: 'var(--b-t)' }} />
                            <div className="signal-text">Selected: {pepSelectedSignal}</div>
                          </div>
                          <div className="signal-item">
                            <span className="signal-dot" style={{ background: 'var(--a-t)' }} />
                            <div className="signal-text">Intent score: {pepSignalScore}</div>
                          </div>
                          <div className="signal-item">
                            <span className="signal-dot" style={{ background: 'var(--b-t)' }} />
                            <div className="signal-text">Indirect intent points: {indirectIntent.totalPoints}/20</div>
                          </div>
                          <div className="signal-item">
                            <span className="signal-dot" style={{ background: 'var(--g-t)' }} />
                            <div className="signal-text">Primary channel: {pepContact.primary_channel}</div>
                          </div>
                          {pepFitCheck ? (
                            <>
                              {(() => {
                                const recommended = String(pepFitCheck.recommended_action || '').toUpperCase();
                                const isPassive = ['VIEW_PROFILE', 'LIKE'].includes(recommended);
                                const evalLabel = pepFitCheck.approved
                                  ? 'Evaluated — Active'
                                  : (isPassive ? 'Evaluated — Passive' : 'Evaluated — Blocked');
                                return (
                                  <div className="signal-item">
                                    <span
                                      className="signal-dot"
                                      style={{ background: pepFitCheck.approved ? 'var(--g-t)' : (isPassive ? 'var(--a-t)' : 'var(--r-t)') }}
                                    />
                                    <div className="signal-text">
                                      Step FitCheck: {evalLabel} ({Number(pepFitCheck.fit_score ?? 0)})
                                    </div>
                                  </div>
                                );
                              })()}
                              <div className="signal-item">
                                <span
                                  className="signal-dot"
                                  style={{ background: 'var(--b-t)' }}
                                />
                                <div className="signal-text">
                                  Status: Ran
                                </div>
                              </div>
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--b-t)' }} />
                                <div className="signal-text">
                                  Action: {String(pepFitCheck.recommended_action || 'view_profile').toUpperCase()}
                                  {pepFitCheck.target_url ? ` · Target: ${String(pepFitCheck.target_url)}` : ''}
                                </div>
                              </div>
                              {pepFitCheck.reason ? (
                                <div className="signal-item">
                                  <span className="signal-dot" style={{ background: 'var(--a-t)' }} />
                                  <div className="signal-text">Reason: {String(pepFitCheck.reason)}</div>
                                </div>
                              ) : null}
                              {Array.isArray((pepFitCheck as any)?.themes) && (pepFitCheck as any).themes.length > 0 ? (
                                <div className="signal-item">
                                  <span className="signal-dot" style={{ background: 'var(--b-t)' }} />
                                  <div className="signal-text">
                                    Themes: {(pepFitCheck as any).themes
                                      .slice(0, 5)
                                      .map((t: any) => `${String(t.keyword)} (${Number(t.count || 0)})`)
                                      .join(', ')}
                                  </div>
                                </div>
                              ) : null}
                              {Array.isArray((pepFitCheck as any)?.recurring_topics) && (pepFitCheck as any).recurring_topics.length > 0 ? (
                                <div className="signal-item">
                                  <span className="signal-dot" style={{ background: 'var(--g-t)' }} />
                                  <div className="signal-text">
                                    Topics: {(pepFitCheck as any).recurring_topics.slice(0, 5).join(', ')}
                                  </div>
                                </div>
                              ) : null}
                              {pepFollowupHint ? (
                                <div className="signal-item">
                                  <span className="signal-dot" style={{ background: 'var(--g-t)' }} />
                                  <div className="signal-text">{pepFollowupHint}</div>
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <div className="signal-item">
                              <span className="signal-dot" style={{ background: 'var(--line)' }} />
                              <div className="signal-text">Step FitCheck: not generated yet</div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="pep-grid">
                        <div className="pep-left">
                          <button className="pep-regen" onClick={() => handleGeneratePep(c)} disabled={pepLoadingNow}>
                            ✨ Regenerate Touchpoint Path
                          </button>
                          <div className="pep-timeline">
                            <div className="pep-timeline-title">
                              <span>Touchpoint Sequence</span>
                              <span className="pep-progress">{pepCompletedCount}/{pepTotalCount || 0} completed</span>
                            </div>
                            {pepRun ? (
                              <div className="mtext" style={{ marginBottom: 8 }}>
                                Run: {String(pepRun.status || 'UNKNOWN').toUpperCase()}
                              </div>
                            ) : null}
                            {(pepTouchPath || []).map((step: any, idx: number) => (
                              <div key={`${pepKey}-tp-${idx}`} className="pep-step">
                                <div className="pep-dot" />
                                <div className="pep-step-body">
                                  <div className="pep-step-day">Day {step?.day ?? idx + 1}</div>
                                  <div className="pep-step-action">
                                    <span className="pep-chip">{String(step?.action || 'Action').replace(/_/g, ' ')}</span>
                                    {step?.channel ? <span className="pep-chip ghost">{String(step.channel).replace(/_/g, ' ')}</span> : null}
                                    {(() => {
                                      const liveStep = pepStepsByIndex.get(idx + 1);
                                      const status = String(liveStep?.status || '').toUpperCase();
                                      if (!status) return null;
                                      return <span className="pep-chip ghost">{status}</span>;
                                    })()}
                                  </div>
                                  <div className="pep-step-title">{step?.angle || 'Action'}</div>
                                  {step?.signal_hook_source ? (
                                    <div className="pep-step-copy">Signal: {String(step.signal_hook_source)}</div>
                                  ) : null}
                                  {typeof step?.wait_days === 'number' ? (
                                    <div className="pep-step-meta">Wait: {step.wait_days} day{step.wait_days === 1 ? '' : 's'}</div>
                                  ) : null}
                                  {(() => {
                                    const liveStep = pepStepsByIndex.get(idx + 1);
                                    const providerMessage = String(
                                      liveStep?.providerResponses?.info?.message ||
                                      liveStep?.providerResponses?.error ||
                                      liveStep?.error ||
                                      ''
                                    ).trim();
                                    return providerMessage ? (
                                      <div className="pep-step-meta">{providerMessage}</div>
                                    ) : null;
                                  })()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="pep-right">
                          <div className="pep-card">
                            <div className="pep-card-title">Program Status</div>
                            {pepProgramEntries.length ? (
                              <div style={{ display: 'grid', gap: 12 }}>
                                {pepProgramEntries.map((entry) => {
                                  const isPrimaryProgram =
                                    String(entry.runId || '').trim() !== '' &&
                                    String(entry.runId || '').trim() === String(pepRun?.runId || '').trim();
                                  const entryCompletedCount = isPrimaryProgram ? pepCompletedCount : null;
                                  const entryPendingCount = isPrimaryProgram ? pepPendingCount : null;
                                  const entryTotalCount = isPrimaryProgram ? pepTotalCount : null;
                                  return (
                                    <div
                                      key={`${entry.programType}-${entry.runId || entry.status}`}
                                      style={{ border: '1px solid rgba(75,85,99,0.6)', borderRadius: 10, padding: 10, display: 'grid', gap: 6 }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                                        <div className="mcard-key">{entry.programType.replace(/_/g, ' ')}</div>
                                        <div className="mcard-val">{pepBadgeMeta(entry.status).status}</div>
                                      </div>
                                      <div className="mcard-row">
                                        <div className="mcard-key">Run ID</div>
                                        <div className="mcard-val">{entry.runId || '—'}</div>
                                      </div>
                                      <div className="mcard-row">
                                        <div className="mcard-key">Entry Source</div>
                                        <div className="mcard-val">{entry.entrySource || '—'}</div>
                                      </div>
                                      <div className="mcard-row">
                                        <div className="mcard-key">Channel</div>
                                        <div className="mcard-val">{entry.channelFamily || '—'}</div>
                                      </div>
                                      <div className="mcard-row">
                                        <div className="mcard-key">Next Due</div>
                                        <div className="mcard-val">{entry.nextDueAt || '—'}</div>
                                      </div>
                                      <div className="mcard-row">
                                        <div className="mcard-key">Last Step</div>
                                        <div className="mcard-val">{entry.lastStep || '—'}</div>
                                      </div>
                                      <div className="mcard-row">
                                        <div className="mcard-key">Last Step Status</div>
                                        <div className="mcard-val">{entry.lastStepStatus || '—'}</div>
                                      </div>
                                      {entryTotalCount !== null ? (
                                        <>
                                          <div className="mcard-row">
                                            <div className="mcard-key">Completed</div>
                                            <div className="mcard-val">{entryCompletedCount}/{entryTotalCount || 0}</div>
                                          </div>
                                          <div className="mcard-row">
                                            <div className="mcard-key">Pending</div>
                                            <div className="mcard-val">{entryPendingCount}</div>
                                          </div>
                                        </>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="mcard-row">
                                <div className="mcard-key">Status</div>
                                <div className="mcard-val">NO ACTIVE PROGRAMS</div>
                              </div>
                            )}
                          </div>
                          <div className="pep-card">
                            <div className="pep-card-title">Decision</div>
                            <div className="mcard-row">
                              <div className="mcard-key">Current</div>
                              <div className="mcard-val">{pepDecisionLabel}</div>
                            </div>
                            <div className="mcard-row">
                              <div className="mcard-key">Reason</div>
                              <div className="mcard-val">{pepDecisionReason}</div>
                            </div>
                          </div>
                          <div className="pep-card">
                            <div className="pep-card-title">Policy & Audit</div>
                            <div className="mcard-row">
                              <div className="mcard-key">Version</div>
                              <div className="mcard-val">{pepPolicyVersion}</div>
                            </div>
                            <div className="mcard-row">
                              <div className="mcard-key">Mode</div>
                              <div className="mcard-val">{String(pepApprovalMode).toUpperCase()}</div>
                            </div>
                            <div className="mcard-row">
                              <div className="mcard-key">Merge</div>
                              <div className="mcard-val">system → tenant → user → company → runtime</div>
                            </div>
                            <div className="mcard-row">
                              <div className="mcard-key">Active</div>
                              <div className="mcard-val">{pepSourceSummary || 'system only'}</div>
                            </div>
                            <div style={{ marginTop: 8 }}>
                              <button
                                className="btn"
                                onClick={() => setPepPolicyExpanded((prev) => ({ ...prev, [pepKey]: !prev[pepKey] }))}
                              >
                                {pepPolicyOpen ? 'Hide Source Detail' : 'View Source Detail'}
                              </button>
                            </div>
                            {pepPolicyOpen && (
                              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                                <div>
                                  <div className="mcard-key">Merged</div>
                                  <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(pepMergedProfile || {}, null, 2)}</pre>
                                </div>
                                <div>
                                  <div className="mcard-key">System</div>
                                  <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify((pepProfileSources as any)?.system || {}, null, 2)}</pre>
                                </div>
                                <div>
                                  <div className="mcard-key">Tenant</div>
                                  <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify((pepProfileSources as any)?.tenant || {}, null, 2)}</pre>
                                </div>
                                <div>
                                  <div className="mcard-key">User</div>
                                  <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify((pepProfileSources as any)?.user || {}, null, 2)}</pre>
                                </div>
                                <div>
                                  <div className="mcard-key">Company</div>
                                  <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify((pepProfileSources as any)?.company || {}, null, 2)}</pre>
                                </div>
                                <div>
                                  <div className="mcard-key">Runtime</div>
                                  <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify((pepProfileSources as any)?.runtime || {}, null, 2)}</pre>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="pep-card">
                            <div className="pep-card-title">Channel Activity</div>
                            <div className="pep-channel-list">
                              {channelActivity.map((item) => (
                                <div key={item.key} className={`pep-channel ${item.active ? 'on' : 'off'}`}>
                                  <div className="pep-channel-icon">{item.icon}</div>
                                  <div className="pep-channel-meta">
                                    <div className="pep-channel-name">{item.label}</div>
                                    <div className="pep-channel-sub">Activity</div>
                                  </div>
                                  <div className="pep-channel-count">{item.count}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="pep-card">
                            <div className="pep-card-title">Recent Posts & Activity</div>
                            <div className="pep-activity-list">
                              {linkedinPosts.length === 0 ? (
                                <div className="pep-empty">No LinkedIn posts found.</div>
                              ) : (
                                linkedinPosts.map((post, idx) => {
                                  const candidateScores = Array.isArray((pepFitCheck as any)?.candidate_scores)
                                    ? (pepFitCheck as any).candidate_scores
                                    : [];
                                  const postUrl = String(post.url || '').trim();
                                  const matchedScore =
                                    candidateScores.find((score: any) => score?.id != null && String(score.id) === String(post.id)) ||
                                    candidateScores.find((score: any) => {
                                      const scoreUrl = String(score?.url || '').trim();
                                      return Boolean(scoreUrl && postUrl && scoreUrl === postUrl);
                                    });
                                  const fitLabel = !pepFitCheck
                                    ? 'FitCheck: not run'
                                    : matchedScore
                                      ? `FitCheck: ${Number(matchedScore.fit_score ?? 0)}${matchedScore.approved ? ' (approved)' : ' (not selected)'}`
                                      : 'FitCheck: not scored for this post';
                                  const indirectScore = indirectIntent.postScores.find((s) => String(s.id) === String(post.id));
                                  return (
                                    <div key={`${pepKey}-post-${post.id}-${idx}`} className="pep-activity">
                                      <div className="pep-activity-head">
                                        <span>LinkedIn Post</span>
                                        <span className="pep-activity-time">{post.time || '—'}</span>
                                      </div>
                                      <div className="pep-activity-body">{post.title}</div>
                                      <div className="pep-activity-time" style={{ marginTop: 4 }}>
                                        {fitLabel}
                                      </div>
                                      <div className="pep-activity-time" style={{ marginTop: 2 }}>
                                        Indirect intent: {Number(indirectScore?.applied ?? 0)}/5
                                      </div>
                                      {matchedScore?.reason ? (
                                        <div className="pep-activity-time" style={{ marginTop: 2 }}>
                                          {String(matchedScore.reason)}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                          <div className="pep-card">
                            <div className="pep-card-title">Generated Message</div>
                            <div className="mtext">{pepMessage || 'Generate a message to see output.'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {contactModalTab === 'sig' && (
                    <div className="mpad">
                      <div className="mcols">
                        <div className="mcol-left">
                          <div className="mcard">
                            <div className="mcard-title">Contact Signals</div>
                            <div className="signals">
                              {contactSignalsRows.map((row, idx) => (
                                <div key={`${pepKey}-contact-signal-${idx}`} className="signal-item">
                                  <span className="signal-dot" style={{ background: 'var(--b-t)' }} />
                                  <div className="signal-text">{row}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="mcard">
                            <div className="mcard-title">Indirect Intent by Post</div>
                            <div className="pep-activity-list">
                              {linkedinPosts.length === 0 ? (
                                <div className="pep-empty">No posts available for indirect intent scoring.</div>
                              ) : (
                                linkedinPosts.map((post, idx) => {
                                  const score = indirectIntent.postScores.find((s) => String(s.id) === String(post.id));
                                  return (
                                    <div key={`${pepKey}-sig-post-${post.id}-${idx}`} className="pep-activity">
                                      <div className="pep-activity-head">
                                        <span>{post.time || '—'}</span>
                                        <span className="pep-activity-time">
                                          Indirect intent: {Number(score?.applied ?? 0)}/5
                                        </span>
                                      </div>
                                      <div className="pep-activity-body">{post.title}</div>
                                      {Array.isArray(score?.reasons) && score.reasons.length > 0 ? (
                                        <div className="pep-activity-time" style={{ marginTop: 4 }}>
                                          {score.reasons.join(' · ')}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            <div className="mtext" style={{ marginTop: 8 }}>
                              Indirect intent cap: {indirectIntent.totalPoints}/20 (max 5 per post, max 20 total).
                            </div>
                          </div>
                        </div>
                        <div className="mcol-right">
                          <div className="mcard">
                            <div className="mcard-title">Company Signals</div>
                            <div className="signals">
                              {companySignalsRows.map((row, idx) => (
                                <div key={`${pepKey}-company-signal-${idx}`} className="signal-item">
                                  <span className="signal-dot" style={{ background: 'var(--g-t)' }} />
                                  <div className="signal-text">{row}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="mcard">
                            <div className="mcard-title">Combined Fit Snapshot</div>
                            <div className="signals">
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--b-t)' }} />
                                <div className="signal-text">Contact FitCheck: {fitScores.contactScore} ({fitScores.contactBand})</div>
                              </div>
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--a-t)' }} />
                                <div className="signal-text">Company FitCheck: {fitScores.companyScore} ({fitScores.companyBand})</div>
                              </div>
                              <div className="signal-item">
                                <span className="signal-dot" style={{ background: 'var(--g-t)' }} />
                                <div className="signal-text">Combined FitCheck (60/40): {fitScores.combinedScore} ({fitScores.combinedBand})</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {contactModalTab === 'act' && (
                    <div className="mpad">
                      <div className="mcard">
                        <div className="mcard-title">Activity Feed</div>
                        <div className="feed">
                          <div className="feed-item">
                            <div className="feed-dot-col">
                              <div className="feed-dot" style={{ background: 'var(--g-t)' }} />
                              <div className="feed-line" />
                            </div>
                            <div>
                              <div className="feed-action">Contact enriched</div>
                              <div className="feed-meta">{enrichedAt || 'Recently'}</div>
                              <div className="feed-note">Contact data refreshed with P.E.P.ai signals.</div>
                            </div>
                          </div>
                          <div className="feed-item">
                            <div className="feed-dot-col">
                              <div className="feed-dot" style={{ background: 'var(--b-t)' }} />
                            </div>
                            <div>
                              <div className="feed-action">Primary channel: {pepContact.primary_channel}</div>
                              <div className="feed-meta">Last 14 days: {pepContact.email_sends_last_14_days} touches</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {contactModalTab === 'seq' && (
                    <div className="mpad">
                      <div className="mcard">
                        <div className="mcard-title">Multi-Touch Playbook</div>
                        <div className="seq-steps">
                          {playbookBlocks.map((block) => {
                            const steps = toSteps((pepPlaybook as any)?.[block.key]);
                            if (steps.length === 0) return null;
                            return (
                              <div key={block.key} className="mcard" style={{ marginBottom: 8 }}>
                                <div className="mcard-title">{block.label}</div>
                                <div className="seq-steps">
                                  {steps.map((step, idx) => (
                                    <div key={`${block.key}-${idx}`} className="seq-step">
                                      <div className="step-num">{idx + 1}</div>
                                      <div>{step}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                          {!pepPlaybook && <div className="mtext">Generate P.E.P.ai to see the playbook.</div>}
                        </div>
                      </div>
                    </div>
                  )}

                  {contactModalTab === 'notes' && (
                    <div className="mpad">
                      <div className="mcard" style={{ marginBottom: 10 }}>
                        <div className="mcard-title">Contact Data Controls</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          {!contactNotesEditing ? (
                            <button className="btn btn-p" onClick={() => startContactNotesEdit(c)}>
                              Edit Contact Fields
                            </button>
                          ) : (
                            <>
                              <button className="btn btn-p" onClick={() => saveContactNotes(c)} disabled={contactNotesSaving}>
                                {contactNotesSaving ? 'Saving...' : 'Save'}
                              </button>
                              <button className="btn" onClick={cancelContactNotesEdit} disabled={contactNotesSaving}>
                                Cancel
                              </button>
                            </>
                          )}
                          {contactNotesError ? <span className="mtext" style={{ color: 'var(--r-t)' }}>{contactNotesError}</span> : null}
                        </div>
                      </div>
                      <div className="mcols">
                        <div className="mcol-left">
                          <div className="mcard">
                            <div className="mcard-title">Segmentation</div>
                            {notesField('Department Tags', 'departmentTags')}
                            {notesField('Seniority Tier', 'seniorityTier')}
                            {notesField('Company Size', 'companySize')}
                            {notesField('Industry Codes', 'industryCodes')}
                          </div>
                          <div className="mcard">
                            <div className="mcard-title">Company Intelligence</div>
                            {field('Contact FitCheck', `${fitScores.contactScore} (${fitScores.contactBand})`)}
                            {field('Company FitCheck', `${fitScores.companyScore} (${fitScores.companyBand})`)}
                            {field('Combined FitCheck (60/40)', `${fitScores.combinedScore} (${fitScores.combinedBand})`)}
                            {field('Connectability', fitScores.contactBreakdown.connectability)}
                            {field('Use Case Potential', fitScores.contactBreakdown.use_case_potential)}
                            {field('Context Data Depth', fitScores.contactBreakdown.context_data_depth)}
                            {field('ICP / Industry Match', fitScores.companyBreakdown.icp_industry_match)}
                            {field('Size / Revenue Fit', fitScores.companyBreakdown.size_revenue_fit)}
                            {field('Buying Signals', fitScores.companyBreakdown.buying_signals)}
                            {field('Engagement Footprint', fitScores.companyBreakdown.engagement_footprint)}
                            {field('Industry', industry)}
                            {field('Employees', employeeCount)}
                            {field('Revenue', revenueRange)}
                            {field('HQ', hqLocation)}
                            {field('Website', companyWebsite)}
                          </div>
                          <div className="mcard">
                            <div className="mcard-title">Data Quality</div>
                            {field('Email Validation', emailValidationStatus)}
                            {field('Phone Confidence', pickContactField(c, ['contactPhone1TotalAI', 'contactMobilePhone1TotalAI']))}
                            {field('Email Confidence', pickContactField(c, ['email1TotalAI']))}
                            {field('Company Phone Confidence', pickContactField(c, ['companyPhone1TotalAI']))}
                          </div>
                        </div>
                        <div className="mcol-right">
                          <div className="mcard">
                            <div className="mcard-title">Contact Info</div>
                            {notesField('Business Email', 'businessEmail')}
                            {notesField('Personal Email', 'personalEmail')}
                            {notesField('Business Phone', 'businessPhone')}
                            {notesField('Mobile Phone', 'mobilePhone')}
                            {notesField('LinkedIn', 'linkedin')}
                            {notesField('Location', 'location')}
                          </div>
                          <div className="mcard">
                            <div className="mcard-title">Social Scrapes</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <img src="https://cdn-icons-png.flaticon.com/512/174/174857.png" className={`w-6 h-6 ${liUrl ? '' : 'grayscale opacity-40'}`} alt="LinkedIn" />
                                {(liIds.profile || liIds.posts) ? (
                                  <button
                                    onClick={() => handleViewContactSocialReport(c, 'linkedin')}
                                    disabled={!!contactSocialReportLoading[liKey]}
                                    className="btn"
                                  >
                                    {contactSocialReportLoading[liKey] ? 'Loading...' : 'View Report'}
                                  </button>
                                ) : (
                                  <button onClick={() => handleScrapeContactSocial(c, 'linkedin')} className="btn">
                                    Scrape LinkedIn (1cr)
                                  </button>
                                )}
                                <button onClick={() => handleFindContactSocial(c, 'linkedin')} className="btn">
                                  Find (1cr)
                                </button>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div className="stat-chip">CB</div>
                                {companyKey && hasCompanyCrunchbaseData(c, companyKey) ? (
                                  <button
                                    onClick={async () => {
                                      if (crunchbaseReports[companyKey]) {
                                        setContactCrunchbaseReport({ companyName: companyKey, data: crunchbaseReports[companyKey] });
                                        return;
                                      }
                                      try {
                                        const res = await fetch(`${RESEARCH_API_URL}/company/crunchbase/full`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ companyName: companyKey }),
                                        });
                                        const data = await res.json();
                                        if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to load Crunchbase report');
                                        setCrunchbaseReports((prev) => ({ ...prev, [companyKey]: data.data }));
                                        setContactCrunchbaseReport({ companyName: companyKey, data: data.data });
                                      } catch (error) {
                                        console.error('Load Crunchbase report failed:', error);
                                        alert('Failed to load Crunchbase report');
                                      }
                                    }}
                                    className="btn"
                                  >
                                    View Report
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleScrapeContactCrunchbase(companyKey)}
                                    disabled={!companyKey || !!crunchbaseLoading[companyKey]}
                                    className="btn"
                                  >
                                    {crunchbaseLoading[companyKey] ? 'Scraping...' : 'Scrape Crunchbase (1cr)'}
                                  </button>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <img src="https://cdn-icons-png.flaticon.com/512/174/174857.png" className={`w-6 h-6 ${pickContactField(c, ['CompanyLIProfileUrl', 'companyLinkedinUrl', 'linkedin_company_url']) ? '' : 'grayscale opacity-40'}`} alt="Company LinkedIn" />
                                {companyKey && hasCompanyLinkedinData(c, companyKey) ? (
                                  <button
                                    onClick={() => handleViewCompanyLinkedinReport(companyKey)}
                                    className="btn"
                                  >
                                    View Company LI
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleScrapeCompanyLinkedin(c)}
                                    disabled={!companyKey || !!companyLinkedinLoading[companyKey]}
                                    className="btn"
                                  >
                                    {companyLinkedinLoading[companyKey] ? 'Scraping...' : 'Scrape Company LI (1cr)'}
                                  </button>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div className="stat-chip">WEB</div>
                                {companyKey && hasCompanyWebsiteData(c, companyKey) ? (
                                  <button
                                    onClick={() => handleViewCompanyWebsiteReport(companyKey)}
                                    className="btn"
                                  >
                                    View Website
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleScrapeCompanyWebsite(c)}
                                    disabled={!companyKey || !!websiteLoading[companyKey]}
                                    className="btn"
                                  >
                                    {websiteLoading[companyKey] ? 'Scraping...' : 'Scrape Website (1cr)'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="mcard">
                            <div className="mcard-title">Raw Contact Data</div>
                            <pre className="mtext" style={{ whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(c, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mfoot">
                  <div className="mfoot-meta">Created: {enrichedAt || '—'}</div>
                  <div className="mfoot-actions">
                    <button className="btn" onClick={() => setContactProfileDrawer(null)}>Close</button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Edit Contact Social URL Modal */}
      {socialEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => { setSocialEdit(null); setSocialEditValue(''); }} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-md w-full mx-4 p-6 relative z-10">
            <h3 className="text-xl font-bold text-white mb-2">Set {socialEdit.platform} URL</h3>
            <p className="text-sm text-gray-400 mb-4">{getContactName(socialEdit.contact)}</p>
            <input
              type="text"
              value={socialEditValue}
              onChange={(e) => setSocialEditValue(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white mb-4 focus:outline-none focus:border-red-500"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setSocialEdit(null); setSocialEditValue(''); }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveContactSocial}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold"
              >
                Save URL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Social Drawer */}
      {contactSocialDrawer && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setContactSocialDrawer(null)} />
          <div className="fixed top-0 right-0 w-[420px] max-w-[95vw] h-full bg-gray-900 border-l border-gray-700 z-50 overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">{getContactName(contactSocialDrawer.contact)}</h3>
                  <p className="text-sm text-gray-400">{contactSocialDrawer.platform} Profile</p>
                </div>
                <button onClick={() => setContactSocialDrawer(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
              </div>
              <div className="bg-gray-800 rounded-lg p-4">
                {(() => {
                  const url = getContactSocialUrl(contactSocialDrawer.contact, contactSocialDrawer.platform);
                  if (url) {
                    return (
                      <a href={url} target="_blank" rel="noreferrer" className="text-blue-300 hover:text-blue-200 underline">
                        {url}
                      </a>
                    );
                  }
                  return (
                    <div className="text-sm text-gray-400">
                      Add a URL or run Find (1cr) to locate the profile.
                    </div>
                  );
                })()}
              </div>
              <div className="mt-4 flex items-center gap-2">
                {(() => {
                  const url = getContactSocialUrl(contactSocialDrawer.contact, contactSocialDrawer.platform);
                  const ids = getContactSocialDatasetIds(contactSocialDrawer.contact, contactSocialDrawer.platform);
                  const key = `${contactSocialDrawer.contact.searchResultId}-${contactSocialDrawer.platform}`;
                  return (
                    <>
                      {(ids.profile || ids.posts) ? (
                        <button
                          onClick={() => handleViewContactSocialReport(contactSocialDrawer.contact, contactSocialDrawer.platform)}
                          disabled={!!contactSocialReportLoading[key]}
                          className="px-3 py-2 rounded text-[#156BFF] text-sm bg-white hover:bg-gray-100 disabled:opacity-50"
                        >
                          {contactSocialReportLoading[key] ? 'Loading...' : 'View Report'}
                        </button>
                      ) : null}
                      {url ? (
                        <button
                          onClick={() => window.open(url, '_blank')}
                          className="px-3 py-2 rounded text-white text-sm bg-gray-700 hover:bg-gray-600"
                        >
                          Open
                        </button>
                      ) : null}
                      <button
                        onClick={() => handleScrapeContactSocial(contactSocialDrawer.contact, contactSocialDrawer.platform)}
                        className="px-3 py-2 rounded text-white text-sm bg-gray-700 hover:bg-gray-600"
                      >
                        Scrape (1cr)
                      </button>
                      <button
                        onClick={() => handleFindContactSocial(contactSocialDrawer.contact, contactSocialDrawer.platform)}
                        className="px-3 py-2 rounded text-white text-sm bg-gray-700 hover:bg-gray-600"
                      >
                        Find (1cr)
                      </button>
                      <button
                        onClick={() => {
                          setSocialEdit(contactSocialDrawer);
                          setSocialEditValue(url || '');
                        }}
                        className="px-3 py-2 rounded text-white text-sm bg-gray-800 hover:bg-gray-700"
                      >
                        Edit
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Social Report Modal */}
      {contactSocialReport && (
        <div className="contact-report-modal fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setContactSocialReport(null)} />
          <div className="contact-report-panel bg-card-dark border border-gray-700 rounded-xl max-w-3xl w-full mx-4 p-6 relative z-10 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">LinkedIn Report</h3>
                <p className="text-sm text-gray-400">{getContactName(contactSocialReport.contact)}</p>
              </div>
              <button onClick={() => setContactSocialReport(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>
            {(() => {
              const payload = Array.isArray(contactSocialReport.items) ? contactSocialReport.items[0] ?? {} : {};
              const profile = Array.isArray((payload as any).profile) ? (payload as any).profile[0] : null;
              const posts = Array.isArray((payload as any).posts) ? (payload as any).posts : [];
              const formatDate = (input?: string) => {
                if (!input) return '';
                const d = new Date(input);
                return Number.isNaN(d.getTime()) ? input : d.toLocaleDateString();
              };
              return (
                <div className="space-y-6">
                  {profile ? (
                    <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4">
                      <div className="flex items-start gap-4">
                        {profile.profilePicHighQuality || profile.profilePic ? (
                          <img
                            src={profile.profilePicHighQuality || profile.profilePic}
                            alt={profile.fullName || 'Profile'}
                            className="w-20 h-20 rounded-full object-cover border border-gray-700"
                          />
                        ) : null}
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="text-lg font-semibold text-white">
                                {profile.fullName || `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim() || 'Unknown'}
                              </div>
                              <div className="text-sm text-gray-400">{profile.headline || profile.jobTitle || ''}</div>
                              <div className="text-xs text-gray-500 mt-1">{profile.addressWithCountry || profile.jobLocation || ''}</div>
                            </div>
                            {profile.linkedinPublicUrl || profile.linkedinUrl ? (
                              <a
                                href={profile.linkedinPublicUrl || profile.linkedinUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-blue-300 hover:text-blue-200 underline"
                              >
                                View on LinkedIn
                              </a>
                            ) : null}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-300">
                            {profile.connections ? <span>Connections: {profile.connections}</span> : null}
                            {profile.followers ? <span>Followers: {profile.followers}</span> : null}
                            {profile.currentJobDuration ? <span>Current Role: {profile.currentJobDuration}</span> : null}
                          </div>
                        </div>
                      </div>
                      {profile.about ? (
                        <div className="mt-4 text-sm text-gray-300 leading-relaxed">
                          {profile.about}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">No profile data found.</div>
                  )}

                  {Array.isArray(profile?.experiences) && profile.experiences.length ? (
                    <div>
                      <div className="text-sm font-semibold text-white mb-2">Experience</div>
                      <div className="space-y-2">
                        {profile.experiences.slice(0, 6).map((exp: any, idx: number) => (
                          <div key={`${exp.companyName || 'exp'}-${idx}`} className="bg-gray-900/60 border border-gray-800 rounded-lg p-3">
                            <div className="text-sm text-white font-medium">{exp.title || 'Role'}</div>
                            <div className="text-xs text-gray-400">
                              {exp.companyName || 'Company'} · {exp.employmentType || 'Role'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {exp.jobStartedOn || ''}{exp.jobEndedOn ? ` → ${exp.jobEndedOn}` : exp.jobStillWorking ? ' → Present' : ''}
                              {exp.jobLocation ? ` · ${exp.jobLocation}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {Array.isArray(profile?.educations) && profile.educations.length ? (
                    <div>
                      <div className="text-sm font-semibold text-white mb-2">Education</div>
                      <div className="space-y-2">
                        {profile.educations.slice(0, 4).map((edu: any, idx: number) => (
                          <div key={`${edu.title || 'edu'}-${idx}`} className="bg-gray-900/60 border border-gray-800 rounded-lg p-3">
                            <div className="text-sm text-white font-medium">{edu.title || 'School'}</div>
                            <div className="text-xs text-gray-400">{edu.subtitle || ''}</div>
                            {edu.period?.startedOn?.year || edu.period?.endedOn?.year ? (
                              <div className="text-xs text-gray-500 mt-1">
                                {edu.period?.startedOn?.year || ''}{edu.period?.endedOn?.year ? ` → ${edu.period.endedOn.year}` : ''}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {Array.isArray(profile?.skills) && profile.skills.length ? (
                    <div>
                      <div className="text-sm font-semibold text-white mb-2">Skills</div>
                      <div className="flex flex-wrap gap-2">
                        {profile.skills.slice(0, 24).map((skill: any, idx: number) => (
                          <span key={`${skill.title || 'skill'}-${idx}`} className="text-xs px-2 py-1 rounded bg-gray-800 text-gray-300">
                            {skill.title || skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {posts.length ? (
                    <div>
                      <div className="text-sm font-semibold text-white mb-2">Recent Posts</div>
                      <div className="space-y-3">
                        {posts.slice(0, 6).map((post: any, idx: number) => (
                          <div key={`${post.id || 'post'}-${idx}`} className="bg-gray-900/60 border border-gray-800 rounded-lg p-3">
                            <div className="text-xs text-gray-500">
                              {formatDate(post.postedAt?.date)}{post.postedAt?.postedAgoText ? ` · ${post.postedAt.postedAgoText}` : ''}
                            </div>
                            <div className="text-sm text-gray-200 mt-1 whitespace-pre-wrap">{post.content || ''}</div>
                            <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-400">
                              {post.engagement?.likes !== undefined ? <span>Likes: {post.engagement.likes}</span> : null}
                              {post.engagement?.comments !== undefined ? <span>Comments: {post.engagement.comments}</span> : null}
                              {post.engagement?.shares !== undefined ? <span>Shares: {post.engagement.shares}</span> : null}
                              {post.linkedinUrl ? (
                                <a href={post.linkedinUrl} target="_blank" rel="noreferrer" className="text-blue-300 hover:text-blue-200 underline">
                                  View post
                                </a>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Crunchbase Report Modal */}
      {contactCrunchbaseReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setContactCrunchbaseReport(null)} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-4xl w-full mx-4 p-6 relative z-10 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">Crunchbase Report</h3>
                <p className="text-sm text-gray-400">{contactCrunchbaseReport.companyName}</p>
              </div>
              <button onClick={() => setContactCrunchbaseReport(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>
            <pre className="text-xs text-gray-200 whitespace-pre-wrap bg-gray-900/60 border border-gray-800 rounded-lg p-4">
              {JSON.stringify(contactCrunchbaseReport.data, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {contactCompanyReport && (
        <DataDrawer
          isOpen={!!contactCompanyReport}
          onClose={() => setContactCompanyReport(null)}
          companyName={contactCompanyReport.companyName}
          source={contactCompanyReport.source}
          data={contactCompanyReport.data}
          scrapedAt={contactCompanyReport.scrapedAt}
        />
      )}

      {/* Page Header */}
      <div className="contacts-page-header px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">My Lists</h2>
            <p className="text-sm text-gray-400 mt-1">Your researched contacts from DynamoDB</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-400">Default List:</span>
            <span className="text-sm text-red-400 font-semibold">{defaultList || 'None'}</span>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-b border-gray-800 bg-gray-950/40">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Hot Lists</h3>
            <p className="text-xs text-gray-400">Saved entities from Podcasts, Listeners, and Social Scraping for deeper research.</p>
          </div>
          <span className="text-xs text-orange-300">{hotListItems.length} items</span>
        </div>
        {hotListItems.length === 0 ? (
          <div className="text-xs text-gray-500">No hot list entities yet.</div>
        ) : (
          <div className="space-y-2 max-h-44 overflow-auto pr-1">
            {hotListItems.slice(0, 50).map((item) => (
              <div key={item.id} className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-900/50 px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{item.title}</div>
                  <div className="text-xs text-gray-400 truncate">
                    {item.source} {item.platform ? `· ${item.platform}` : ''} {item.subtitle ? `· ${item.subtitle}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-200"
                    >
                      Open
                    </a>
                  ) : null}
                  <button
                    onClick={() => handleRemoveHotListItem(item.id)}
                    className="text-xs px-2 py-1 rounded bg-red-800 hover:bg-red-700 text-white"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {loadingMore && !loading && (
        <div className="px-6 py-3 border-b border-gray-800 bg-gray-950">
          <div className="flex items-center gap-2 text-xs text-gray-300">
            <div className="h-2 w-2 animate-pulse rounded-full bg-yellow-400" />
            Loading additional contacts…
          </div>
        </div>
      )}

      {contactsLoadError && (
        <div className="px-6 py-3 border-b border-red-800 bg-red-950/40 text-sm text-red-200">
          {contactsLoadError}
        </div>
      )}\n\n      {/* Filters Bar */}
      <div className="contacts-filters bg-card-dark border-b border-gray-800 px-6 py-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search name, company, title..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { commitSearch(); } }}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500"
            />
            <button
              onClick={commitSearch}
              className="ml-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-semibold"
            >Search</button>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">List:</label>
            <select
              value={currentListFilter}
              onChange={e => {
                setCurrentListFilter(e.target.value);
                setSelectedContacts(new Set());
                setSelectionScope('page');
                setCurrentPage(0);
              }}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[150px]"
            >
              <option value="">All Contacts</option>
              {userLists.map(list => (
                <option key={list} value={list}>{list}{list === defaultList ? ' ⭐' : ''}</option>
              ))}
            </select>
            <button onClick={() => setManageListsModal(true)} className="text-gray-400 hover:text-white" title="Manage Lists">
              <Settings className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">Sort:</label>
            <select
              value={sortKeyContacts}
              onChange={e => {
                setSortKeyContacts(e.target.value as typeof sortKeyContacts);
                setSelectedContacts(new Set());
                setSelectionScope('page');
                setCurrentPage(0);
              }}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[200px]"
            >
              <option value="date_desc">Date searched (newest)</option>
              <option value="date_asc">Date searched (oldest)</option>
              <option value="name_asc">Contact name (A-Z)</option>
              <option value="name_desc">Contact name (Z-A)</option>
              <option value="company_asc">Company name (A-Z)</option>
              <option value="company_desc">Company name (Z-A)</option>
              <option value="pepai_asc">PEPai status</option>
              <option value="pepai_desc">PEPai status (reverse)</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">Date:</label>
            <input
              type="date"
              value={researchedDateFrom}
              onChange={e => {
                setResearchedDateFrom(e.target.value);
                setSelectedContacts(new Set());
                setSelectionScope('page');
                setCurrentPage(0);
              }}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            />
            <span className="text-sm text-gray-500">to</span>
            <input
              type="date"
              value={researchedDateTo}
              onChange={e => {
                setResearchedDateTo(e.target.value);
                setSelectedContacts(new Set());
                setSelectionScope('page');
                setCurrentPage(0);
              }}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            />
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">PEPai:</label>
            <select
              value={pepStatusFilter}
              onChange={e => {
                setPepStatusFilter(e.target.value);
                setSelectedContacts(new Set());
                setSelectionScope('page');
                setCurrentPage(0);
              }}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[160px]"
            >
              <option value="">All statuses</option>
              <option value="RUNNING">Active</option>
              <option value="QUEUED">Queued</option>
              <option value="COMPLETE">Complete</option>
              <option value="FAILED">Failed</option>
              <option value="IDLE">Idle</option>
            </select>
          </div>

          <div className="flex items-center gap-2 rounded border border-gray-700 bg-gray-900/60 px-3 py-2">
            <span className="text-xs text-gray-400">Has:</span>
            <label className="inline-flex items-center gap-1 text-xs text-gray-200">
              <input
                type="checkbox"
                checked={hasBusinessEmailFilter}
                onChange={(e) => {
                  setHasBusinessEmailFilter(e.target.checked);
                  setSelectedContacts(new Set());
                  setSelectionScope('page');
                  setCurrentPage(0);
                }}
              />
              Biz Email
            </label>
            <label className="inline-flex items-center gap-1 text-xs text-gray-200">
              <input
                type="checkbox"
                checked={hasPersonalEmailFilter}
                onChange={(e) => {
                  setHasPersonalEmailFilter(e.target.checked);
                  setSelectedContacts(new Set());
                  setSelectionScope('page');
                  setCurrentPage(0);
                }}
              />
              Personal Email
            </label>
            <label className="inline-flex items-center gap-1 text-xs text-gray-200">
              <input
                type="checkbox"
                checked={hasLinkedinFilter}
                onChange={(e) => {
                  setHasLinkedinFilter(e.target.checked);
                  setSelectedContacts(new Set());
                  setSelectionScope('page');
                  setCurrentPage(0);
                }}
              />
              LinkedIn
            </label>
            <label className="inline-flex items-center gap-1 text-xs text-gray-200">
              <input
                type="checkbox"
                checked={hasBusinessPhoneFilter}
                onChange={(e) => {
                  setHasBusinessPhoneFilter(e.target.checked);
                  setSelectedContacts(new Set());
                  setSelectionScope('page');
                  setCurrentPage(0);
                }}
              />
              Biz Phone
            </label>
            <label className="inline-flex items-center gap-1 text-xs text-gray-200">
              <input
                type="checkbox"
                checked={hasPersonalPhoneFilter}
                onChange={(e) => {
                  setHasPersonalPhoneFilter(e.target.checked);
                  setSelectedContacts(new Set());
                  setSelectionScope('page');
                  setCurrentPage(0);
                }}
              />
              Personal Phone
            </label>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">Title:</label>
            <input
              type="text"
              value={titleFilterInput}
              onChange={(e) => {
                setTitleFilterInput(e.target.value);
                setSelectedContacts(new Set());
                setSelectionScope('page');
                setCurrentPage(0);
              }}
              placeholder="comma-separated titles"
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[220px]"
            />
            <label className="inline-flex items-center gap-1 text-xs text-gray-300">
              <input
                type="checkbox"
                checked={titleExactMatch}
                onChange={(e) => {
                  setTitleExactMatch(e.target.checked);
                  setSelectedContacts(new Set());
                  setSelectionScope('page');
                  setCurrentPage(0);
                }}
              />
              Exact
            </label>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">Location:</label>
            <input
              type="text"
              value={locationFilterInput}
              onChange={(e) => {
                setLocationFilterInput(e.target.value);
                setSelectedContacts(new Set());
                setSelectionScope('page');
                setCurrentPage(0);
              }}
              placeholder="city, state, country"
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[220px]"
            />
            <select
              value={locationScopeFilter}
              onChange={(e) => {
                setLocationScopeFilter(e.target.value as 'both_or' | 'both_and' | 'contact' | 'company');
                setSelectedContacts(new Set());
                setSelectionScope('page');
                setCurrentPage(0);
              }}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            >
              <option value="both_or">Contact or Company</option>
              <option value="both_and">Contact and Company</option>
              <option value="contact">Contact only</option>
              <option value="company">Company only</option>
            </select>
          </div>

          {hasAdvancedContactsFilters && (
            <button
              onClick={clearAllVisibleFilters}
              className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg"
            >
              Clear Extra Filters
            </button>
          )}

          <label className="inline-flex items-center gap-2 rounded border border-gray-700 bg-gray-900/60 px-3 py-2 text-sm text-gray-200">
            <input
              type="checkbox"
              checked={sendToCrm}
              onChange={(e) => setSendToCrm(e.target.checked)}
              className="h-4 w-4 rounded-sm border-2 border-white bg-white accent-red-600"
            />
            <span className="font-medium">Send to my CRM</span>
          </label>

          {canUsePepaiResearch && (
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-400">Instantly:</label>
              <select
                value={selectedInstantlyCampaignId}
                onChange={(e) => setSelectedInstantlyCampaignId(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[260px]"
              >
                {instantlyCampaigns.length === 0 && <option value="">No Instantly campaigns found</option>}
                {instantlyCampaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                ))}
              </select>
            </div>
          )}

          {canChooseGhlTarget && (
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-400">Send To GHL:</label>
              <select
                value={ghlSendAsLocationId}
                disabled={!adminToken}
                onChange={async (e) => {
                  const nextValue = e.target.value;
                  setGhlSendAsLocationId(nextValue);
                  if (!adminToken || !userEmail) return;
                  setGhlSendAsSaving(true);
                  try {
                    await setGhlSendAsLocation({
                      token: adminToken,
                      email: userEmail,
                      ghlSendAsLocationId: nextValue || null,
                    });
                  } catch (error) {
                    console.error('Failed to save GHL send-as target', error);
                  } finally {
                    setGhlSendAsSaving(false);
                  }
                }}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[280px]"
              >
                {!adminToken && <option value="">Admin token missing (re-login)</option>}
                <option value="">Send to my GHL account</option>
                {ghlTargetOptions.map((option) => (
                  <option key={option.locationId} value={option.locationId}>
                    Send to {option.label}
                  </option>
                ))}
              </select>
              {ghlSendAsSaving && <span className="text-xs text-gray-400">Saving...</span>}
            </div>
          )}

          <button
            onClick={() => { loadContacts(); loadUserLists(); }}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-400">
              Total:{' '}
              <span className="text-white font-bold">
                {hasClientSideOnlyFilters ? `${filteredContacts.length} / ${contactsForUi.length}` : totalContacts}
              </span>
            </span>
            {loadingMore && !loading && (
              <span className="text-xs text-gray-400">Loading more…</span>
            )}
            {selectedCount > 0 && (
              <span className="text-sm text-red-400 font-semibold">
                {selectedCount} selected{selectionScope === 'matching' ? ' (all matching)' : ''}
              </span>
            )}
            {allMatchingSelected && (
              <button
                onClick={() => {
                  setSelectedContacts(new Set());
                  setSelectionScope('page');
                }}
                className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-200"
              >
                Clear selection
              </button>
            )}
            {canOfferSelectAllMatching && (
              <span className="text-xs text-gray-400">
                All {visiblePageIds.length} on this page selected.
              </span>
            )}
            {canOfferSelectAllMatching && (
              <button
                onClick={handleSelectAllFilteredContacts}
                className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-200"
              >
                Select all matching results
              </button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-gray-400 text-sm">25 per page</span>
          </div>

          <div className="flex items-center space-x-1">
            <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} className="px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded text-white text-sm">←</button>
            <span className="text-sm text-gray-400 px-2">Page {currentPage + 1} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1} className="px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded text-white text-sm">→</button>
          </div>

          {selectedContacts.size > 0 && (
            <div className="flex items-center gap-2">
              {canUsePepaiResearch ? (
                <>
                  <button
                    onClick={handleStartPepaiBulk}
                    disabled={bulkPepLoading || !selectedInstantlyCampaignId}
                    className="bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white px-3 py-2 rounded-lg text-sm"
                  >
                    {bulkPepLoading ? 'Starting PEPai…' : 'Start PEPai'}
                  </button>
                  <button
                    onClick={handleSendSelectedToInstantly}
                    disabled={instantlySyncLoading || !selectedInstantlyCampaignId}
                    className="bg-indigo-700 hover:bg-indigo-600 disabled:opacity-60 text-white px-3 py-2 rounded-lg text-sm"
                  >
                    {instantlySyncLoading ? 'Sending…' : 'Send Selected to Instantly'}
                  </button>
                </>
              ) : null}
              <button
                onClick={handleRerunSelected}
                className="bg-red-700 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm"
              >
                Rerun Selected
              </button>
              <button
                onClick={handleSendSelectedToCrm}
                disabled={crmSyncLoading}
                className="bg-blue-700 hover:bg-blue-600 disabled:opacity-60 text-white px-3 py-2 rounded-lg text-sm"
              >
                {crmSyncLoading ? 'Sending…' : 'Send Selected to CRM'}
              </button>
              <select
                onChange={e => { if (e.target.value === '__new__') { setCreateListModal(true); } else if (e.target.value) { handleAddToList(e.target.value); } e.target.value = ''; }}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
              >
                <option value="">-- Add to List --</option>
                {defaultList && <option value={defaultList}>{defaultList} (Default)</option>}
                {userLists.filter(l => l !== defaultList).map(list => <option key={list} value={list}>{list}</option>)}
                <option value="__new__">+ Create New List</option>
              </select>
              {currentListFilter && (
                <button onClick={handleRemoveFromList} className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm">Remove from List</button>
              )}
            </div>
          )}

          <button onClick={() => setCreateListModal(true)} className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New List
          </button>

          <button
            onClick={() => setShowExportFieldsModal(true)}
            disabled={exportProgress.active}
            className="bg-gray-800 hover:bg-gray-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {exportProgress.active ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
        {showExportFieldsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/80" onClick={() => setShowExportFieldsModal(false)} />
            <div className="bg-card-dark border border-gray-700 rounded-xl max-w-2xl w-full mx-4 p-6 relative z-10">
              <h3 className="text-xl font-bold text-white mb-2">Select Export Fields</h3>
              <p className="text-sm text-gray-400 mb-4">
                Choose up to {MAX_EXPORT_FIELDS} fields. Export will include only these columns.
              </p>
              <div className="flex gap-2 mb-3">
                <select
                  value={exportFieldPickerValue}
                  onChange={(e) => setExportFieldPickerValue(e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                >
                  <option value="">-- Select a field --</option>
                  {availableExportFields
                    .filter((field) => !exportFieldSelection.includes(field))
                    .map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                </select>
                <button
                  onClick={() => {
                    const field = exportFieldPickerValue.trim();
                    if (!field) return;
                    if (exportFieldSelection.includes(field)) return;
                    if (exportFieldSelection.length >= MAX_EXPORT_FIELDS) {
                      alert(`You can select up to ${MAX_EXPORT_FIELDS} fields.`);
                      return;
                    }
                    setExportFieldSelection((prev) => [...prev, field]);
                    setExportFieldPickerValue('');
                  }}
                  className="px-3 py-2 bg-blue-700 hover:bg-blue-600 text-white rounded-lg text-sm"
                >
                  Add
                </button>
              </div>
              <div className="text-xs text-gray-400 mb-2">
                Selected: {exportFieldSelection.length} / {MAX_EXPORT_FIELDS}
              </div>
              <div className="max-h-52 overflow-y-auto border border-gray-700 rounded-lg p-2 bg-gray-900/40 mb-4">
                <div className="flex flex-wrap gap-2">
                  {exportFieldSelection.map((field) => (
                    <button
                      key={field}
                      onClick={() => setExportFieldSelection((prev) => prev.filter((value) => value !== field))}
                      className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-xs text-white"
                      title="Remove field"
                    >
                      {field} ×
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setExportFieldSelection([])}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
                >
                  Clear
                </button>
                <button
                  onClick={() => setShowExportFieldsModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (exportFieldSelection.length === 0) {
                      alert('Select at least one field to export.');
                      return;
                    }
                    setShowExportFieldsModal(false);
                    void handleExportCSV(exportFieldSelection);
                  }}
                  className="px-4 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-white text-sm font-semibold"
                >
                  Export Selected Fields
                </button>
              </div>
            </div>
          </div>
        )}
        {activeFilterPills.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-400">Active Filters:</span>
            {activeFilterPills.map((pill) => (
              <button
                key={pill.key}
                onClick={pill.onRemove}
                className="inline-flex items-center gap-1 rounded-full border border-blue-700/40 bg-blue-900/30 px-2 py-0.5 text-[11px] text-blue-200 hover:bg-blue-900/50"
                title="Remove filter"
              >
                {pill.label}
                <span className="text-blue-300">×</span>
              </button>
            ))}
            <button
              onClick={clearAllVisibleFilters}
              className="text-[11px] text-gray-300 underline hover:text-white"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/75 z-40 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-500 mx-auto mb-4" />
            <p className="text-white text-lg">Loading contacts...</p>
          </div>
        </div>
      )}
      {loadingMore && !loading && (
        <div className="fixed bottom-6 right-6 bg-gray-900/90 border border-gray-700 text-gray-200 px-4 py-2 rounded-lg text-sm">
          Loading more contacts…
        </div>
      )}
      {exportProgress.active && (
        <div className="fixed bottom-20 right-6 bg-gray-900/95 border border-gray-700 text-gray-200 px-4 py-3 rounded-lg text-sm min-w-[260px]">
          <div className="font-semibold text-white mb-1">{exportProgress.stage}</div>
          <div className="text-xs text-gray-400 mb-2">
            {exportProgress.processed} / {exportProgress.total}
          </div>
          <div className="h-2 w-full rounded bg-gray-800 overflow-hidden">
            <div
              className="h-2 bg-blue-500"
              style={{
                width: `${Math.min(
                  100,
                  Math.max(0, Math.round((exportProgress.processed / Math.max(exportProgress.total, 1)) * 100))
                )}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="contacts-table-wrap px-6 py-4">
        <div className="contacts-table-shell bg-card-dark border border-gray-700 rounded-xl overflow-hidden">
          <div className="max-h-[calc(100vh-300px)] overflow-x-auto">
            <div style={{ minWidth: contactsGridWidth }}>
              <div
                className="contacts-grid-head sticky top-0 z-10 grid border-b border-gray-700 bg-card-dark text-xs font-semibold uppercase text-gray-400"
                style={{ gridTemplateColumns: contactsGridTemplate, minWidth: contactsGridWidth }}
              >
                <div className="px-2 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 accent-red-600"
                  />
                </div>
                <div className="px-3 py-3 text-left">Contact Name</div>
                <div className="px-3 py-3 text-left">Contact Title</div>
                <div className="px-3 py-3 text-left">Company Name</div>
                <div className="px-3 py-3 text-left">Contact Location</div>
                <div className="px-3 py-3 text-left">Business Email</div>
                <div className="px-3 py-3 text-left">Personal Email</div>
                <div className="px-3 py-3 text-left">Business Phone</div>
                <div className="px-3 py-3 text-left">Mobile Phone</div>
                <div className="px-3 py-3 text-center">LinkedIn URL</div>
                <div className="px-3 py-3 text-center">Facebook</div>
                <div className="px-3 py-3 text-center">Instagram</div>
                <div className="px-3 py-3 text-center">YouTube</div>
                <div className="px-3 py-3 text-center">TikTok</div>
                <div className="px-3 py-3 text-center">X Page</div>
                <div className="px-3 py-3 text-center">Threads</div>
                <div className="px-3 py-3 text-left">Date Researched</div>
                <div className="px-3 py-3 text-left">List Name</div>
                <div className="px-3 py-3 text-center border-l border-gray-700">Rerun</div>
              </div>
              {paginatedContacts.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">No contacts found.</div>
              ) : (
                <div style={{ minWidth: contactsGridWidth }}>
                  <List
                    rowCount={paginatedContacts.length}
                    rowHeight={contactRowHeight}
                    rowComponent={ContactRow}
                    rowProps={{ items: paginatedContacts }}
                    style={{ height: contactsListHeight, width: contactsGridWidth }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 px-6 py-4 mt-8">
        <div className="flex justify-between items-center text-sm text-gray-500">
          <div>© 2026 Profitibull. All rights reserved.</div>
          <div className="flex space-x-4">
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
